/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { Tenant, UserProfile, Opportunity, Application, Placement, PlacementNote, PlacementEvaluation, TrainingAgreement, StudentTask, AttendanceReport, SystemLog, UserRole, GlobalBranding, Interview, PortalMessage, School, District, Recommendation, OrganizationTeamMember, NotificationPreferences } from '../types';
import { DEFAULT_TENANTS, MOCK_USERS, MOCK_OPPORTUNITIES, MOCK_APPLICATIONS, MOCK_PLACEMENTS, MOCK_TASKS, MOCK_ATTENDANCE, MOCK_LOGS } from '../data/mockData';
import { db, auth, isUsingMockKeys, OperationType, handleFirestoreError } from '../firebase';
import { signInWithPopup, signInWithRedirect, GoogleAuthProvider, signOut, onAuthStateChanged, User as FirebaseUser, createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { collection, doc, setDoc, addDoc, getDoc, getDocs, updateDoc, deleteDoc, query, where, onSnapshot, writeBatch, limit } from 'firebase/firestore';
import { SC_COUNTIES } from '../components/scPaths';
import { getRegionDarkBg, getRegionThemeAccent, formatRegionProperNoun, formatRegionWithNoun } from '../utils/regionUtils';
import { deriveDistrictForSchoolName, deriveDistrictIdForSchoolName, resolveDistrictForSchool, deriveRegionIdForSchool } from '../utils/schoolUtils';
import { formatTime12HourEST, calculateAge, formatDateOfBirth, parseLocalDate } from '../utils/timeUtils';
import { inAppAlert, inAppToast } from '../components/Common/InAppDialog';

function cleanFirestoreData<T>(obj: T): T {
  if (obj === undefined) return undefined as any;
  if (obj === null) return null as any;
  if (Array.isArray(obj)) {
    return obj
      .filter(item => item !== undefined)
      .map(item => cleanFirestoreData(item)) as any;
  }
  if (typeof obj === 'object') {
    if (obj.constructor !== Object) {
      return obj; // Return Dates, Timestamps, FieldValues as-is
    }
    const cleaned: any = {};
    for (const key of Object.keys(obj as any)) {
      const val = (obj as any)[key];
      if (val !== undefined) {
        cleaned[key] = cleanFirestoreData(val);
      }
    }
    return cleaned;
  }
  return obj;
}

const INITIAL_TENANTS: Tenant[] = [
  ...DEFAULT_TENANTS,
  ...SC_COUNTIES.map(county => ({
    id: county.id,
    name: county.name,
    subdomain: county.id,
    logo: 'Globe',
    primaryColor: '#0094c8',
    secondaryColor: '#4f46e5',
    welcomeMessage: `Welcome to ${county.name} County WBL Portal.`,
    isActive: ['spartanburg', 'union', 'charleston', 'greenville', 'richland', 'horry', 'lexington', 'beaufort'].includes(county.id),
    isVisible: true,
    highSchoolActive: true,
    postSecondaryActive: true,
    foundationsEnabled: true,
    customDomain: county.id === 'spartanburg' ? 'thespartanburgedge.com' : undefined,
  }))
];

const STATIC_MOCK_SCHOOLS = [
  { id: 'bayside-high-school-(training)', name: 'Bayside High School (training)', regionId: 'training' },
  { id: 'valley-view-high-school-(training)', name: 'Valley View High School (training)', regionId: 'training' },
  { id: 'charleston-charter-school-for-math-and-science-(charleston)', name: 'Charleston Charter School for Math and Science (charleston)', regionId: 'charleston' },
  { id: 'wando-high-school-(charleston)', name: 'Wando High School (charleston)', regionId: 'charleston' },
  { id: 'academic-magnet-high-school-(charleston)', name: 'Academic Magnet High School (charleston)', regionId: 'charleston' },
  { id: 'west-ashley-high-school-(charleston)', name: 'West Ashley High School (charleston)', regionId: 'charleston' },
  { id: 'greenville-high-school-(greenville)', name: 'Greenville High School (greenville)', regionId: 'greenville' },
  { id: 'wade-hampton-high-school-(greenville)', name: 'Wade Hampton High School (greenville)', regionId: 'greenville' },
  { id: 'j.l.-mann-high-school-(greenville)', name: 'J.L. Mann High School (greenville)', regionId: 'greenville' },
  { id: 'eastside-high-school-(greenville)', name: 'Eastside High School (greenville)', regionId: 'greenville' },
  { id: 'riverside-high-school-(greenville)', name: 'Riverside High School (greenville)', regionId: 'greenville' },
  { id: 'campobello-gramling-school-(spartanburg)', name: 'Campobello Gramling School (spartanburg)', regionId: 'spartanburg' },
  { id: 'chapman-high-school-(spartanburg)', name: 'Chapman High School (spartanburg)', regionId: 'spartanburg' },
  { id: 'landrum-high-school-(spartanburg)', name: 'Landrum High School (spartanburg)', regionId: 'spartanburg' },
  { id: 'boiling-springs-high-school-(spartanburg)', name: 'Boiling Springs High School (spartanburg)', regionId: 'spartanburg' },
  { id: 'chesnee-high-school-(spartanburg)', name: 'Chesnee High School (spartanburg)', regionId: 'spartanburg' },
  { id: 'broome-high-school-(spartanburg)', name: 'Broome High School (spartanburg)', regionId: 'spartanburg' },
  { id: 'woodruff-high-school-(spartanburg)', name: 'Woodruff High School (spartanburg)', regionId: 'spartanburg' },
  { id: 'james-f.-byrnes-high-school-(spartanburg)', name: 'James F. Byrnes High School (spartanburg)', regionId: 'spartanburg' },
  { id: 'dorman-high-school-(spartanburg)', name: 'Dorman High School (spartanburg)', regionId: 'spartanburg' },
  { id: 'spartanburg-high-school-(spartanburg)', name: 'Spartanburg High School (spartanburg)', regionId: 'spartanburg' },
  { id: 'daniel-morgan-technology-center-(spartanburg)', name: 'Daniel Morgan Technology Center (spartanburg)', regionId: 'spartanburg' },
  { id: 'swofford-career-center-(spartanburg)', name: 'Swofford Career Center (spartanburg)', regionId: 'spartanburg' },
  { id: 'r.d.-anderson-applied-technology-center-(spartanburg)', name: 'R.D. Anderson Applied Technology Center (spartanburg)', regionId: 'spartanburg' },
  { id: 'r.-d.-anderson-applied-tech-(spartanburg)', name: 'R. D. Anderson Applied Tech (spartanburg)', regionId: 'spartanburg' },
  { id: 'whitlock-flexible-learning-center-(spartanburg)', name: 'Whitlock Flexible Learning Center (spartanburg)', regionId: 'spartanburg' },
  { id: 'columbia-high-school-(richland)', name: 'Columbia High School (richland)', regionId: 'richland' },
  { id: 'spring-valley-high-school-(richland)', name: 'Spring Valley High School (richland)', regionId: 'richland' },
  { id: 'ridge-view-high-school-(richland)', name: 'Ridge View High School (richland)', regionId: 'richland' },
  { id: 'dreher-high-school-(richland)', name: 'Dreher High School (richland)', regionId: 'richland' },
  { id: 'myrtle-beach-high-school-(horry)', name: 'Myrtle Beach High School (horry)', regionId: 'horry' },
  { id: 'carolina-forest-high-school-(horry)', name: 'Carolina Forest High School (horry)', regionId: 'horry' },
  { id: 'socastee-high-school-(horry)', name: 'Socastee High School (horry)', regionId: 'horry' },
  { id: 'conway-high-school-(horry)', name: 'Conway High School (horry)', regionId: 'horry' }
];

const STATIC_MOCK_DISTRICTS = [
  { id: 'training-school-district-(training)', name: 'Training School District (training)', regionId: 'training' },
  { id: 'charleston-county-school-district-(charleston)', name: 'Charleston County School District (charleston)', regionId: 'charleston' },
  { id: 'greenville-county-school-district-(greenville)', name: 'Greenville County School District (greenville)', regionId: 'greenville' },
  { id: 'spartanburg-school-district-1-(spartanburg)', name: 'Spartanburg School District 1 (spartanburg)', regionId: 'spartanburg' },
  { id: 'spartanburg-school-district-2-(spartanburg)', name: 'Spartanburg School District 2 (spartanburg)', regionId: 'spartanburg' },
  { id: 'spartanburg-school-district-3-(spartanburg)', name: 'Spartanburg School District 3 (spartanburg)', regionId: 'spartanburg' },
  { id: 'spartanburg-school-district-4-(spartanburg)', name: 'Spartanburg School District 4 (spartanburg)', regionId: 'spartanburg' },
  { id: 'spartanburg-school-district-5-(spartanburg)', name: 'Spartanburg School District 5 (spartanburg)', regionId: 'spartanburg' },
  { id: 'spartanburg-school-district-6-(spartanburg)', name: 'Spartanburg School District 6 (spartanburg)', regionId: 'spartanburg' },
  { id: 'spartanburg-school-district-7-(spartanburg)', name: 'Spartanburg School District 7 (spartanburg)', regionId: 'spartanburg' },
  { id: 'richland-school-district-one-(richland)', name: 'Richland School District One (richland)', regionId: 'richland' },
  { id: 'richland-school-district-two-(richland)', name: 'Richland School District Two (richland)', regionId: 'richland' },
  { id: 'horry-county-schools-(horry)', name: 'Horry County Schools (horry)', regionId: 'horry' }
];

const DEFAULT_GLOBAL_BRANDING: GlobalBranding = {
  id: 'global',
  palette: {
    darkMain: '#0f172a',
    lightMain: '#0094c8',
    darkSecondary: '#1e293b',
    lightSecondary: '#94a3b8',
    background: '#f8fafc',
  },
  logos: {
    navigation: '',
    hero: '',
    secondary: '',
    sponsor1: '',
    sponsor2: '',
  },
  heroVideo: '',
  clusterImages: {},
  subclusterImages: {},
  customDomain: 'wblportal.com',
};

interface WBLContextProps {
  activeTenant: Tenant;
  activeUser: UserProfile | null;
  tenants: Tenant[];
  globalBranding: GlobalBranding;
  users: UserProfile[];
  opportunities: Opportunity[];
  allOpportunities: Opportunity[];
  applications: Application[];
  placements: Placement[];
  tasks: StudentTask[];
  attendance: AttendanceReport[];
  logs: SystemLog[];
  interviews: Interview[];
  messages: PortalMessage[];
  recommendations: Recommendation[];
  userLinks: any[];
  schools: School[];
  allSchools: School[];
  districts: District[];
  allDistricts: District[];
  isLoading: boolean;
  isFirebaseActive: boolean;
  isDbSeeded: boolean;
  currentView: string;
  setCurrentView: (view: string) => void;
  
  // Auth Commands
  signInWithGoogle: () => Promise<void>;
  signUpWithEmail: (email: string, password: string, role: UserRole) => Promise<void>;
  signUpWithRoleAndData: (email: string, password: string, role: UserRole, data: any) => Promise<void>;
  verifyStudentTicket: (dob: string, code: string) => Promise<string | null>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
  runSchoolIdMigration: () => Promise<void>;
  
  // Data Commands
  changeTenant: (subdomainOrId: string) => void;
  updateTenantBranding: (tenantId: string, updates: Partial<Tenant>) => void;
  updateAllTenantsBranding: (updates: Partial<Tenant>) => Promise<void>;
  updateGlobalBranding: (updates: Partial<GlobalBranding>) => Promise<void>;
  createTenant: (tenant: Tenant) => void;
  
  addOpportunity: (opportunity: Omit<Opportunity, 'id' | 'createdAt'>) => Promise<void>;
  updateOpportunity: (id: string, opportunity: Partial<Opportunity>) => Promise<void>;
  applyToOpportunity: (opportunityId: string, coverLetter: string, resumeSummary: string) => Promise<void>;
  reviewApplication: (applicationId: string, status: 'accepted' | 'rejected', agreement?: TrainingAgreement, declineLetter?: string) => Promise<void>;
  createPlacement: (oppId: string, studentId: string, mentorEmail: string, mentorName: string, agreement?: TrainingAgreement) => Promise<void>;
  scheduleInterview: (interview: Omit<Interview, 'id'>) => Promise<void>;
  cancelInterview: (interviewId: string, applicationId: string) => Promise<void>;
  rescheduleInterview: (interviewId: string, updates: Partial<Interview>) => Promise<void>;
  updateInterviewNotesAndScore: (interviewId: string, notes: string, score?: number) => Promise<void>;
  recommendStudentToOpportunity: (studentId: string, opportunityId: string) => Promise<void>;
  
  addTask: (placementId: string, studentId: string, title: string, description: string, dueDate: string) => Promise<void>;
  submitTask: (taskId: string, notes: string, link: string) => Promise<void>;
  gradeTask: (taskId: string, feedback: string, rating: number) => Promise<void>;
  
  submitAttendance: (placementId: string, weekEnding: string, hoursWorked: number, summary: string) => Promise<void>;
  approveAttendance: (reportId: string, status: 'approved' | 'rejected', feedback?: string) => Promise<void>;
  
  updatePlacementEvaluation: (placementId: string, evalNum: 1 | 2, evaluation: PlacementEvaluation) => Promise<void>;
  updatePlacementAgreement: (placementId: string, agreement: TrainingAgreement) => Promise<void>;
  hireStudent: (placementId: string) => Promise<void>;
  addPlacementNote: (placementId: string, text: string, visibleToStudents: boolean, visibleToAdvisors: boolean) => Promise<void>;
  deletePlacementNote: (placementId: string, noteId: string) => Promise<void>;
  
  // Messaging Operations
  markMessageAsRead: (id: string) => Promise<void>;
  markMessageAsUnread: (id: string) => Promise<void>;
  deletePortalMessage: (id: string) => Promise<void>;
  sendManualPortalMessage: (toEmail: string, subject: string, title: string, bodyHtml: string) => Promise<void>;
  sendVerificationReminderToAdvisor: () => Promise<void>;

  // Admin Operations
  updateUserProfile: (uid: string, updates: Partial<UserProfile>) => Promise<void>;
  addUserProfile: (profile: UserProfile) => Promise<void>;
  createAuthenticatedUser: (email: string, password: string, profile: UserProfile) => Promise<string>;
  deleteUserProfile: (uid: string) => Promise<void>;
  linkStudent: (studentUid: string, studentName: string, targetParentUid?: string) => Promise<void>;
  switchActiveChild?: (studentUid: string) => Promise<void>;
  grantFerpaConsent: (granted: boolean, signatureData?: { type: 'draw' | 'type', value: string, name: string, date: string }) => Promise<void>;
  adminOverrideFerpaConsent: (studentId: string, signatureData: { type: 'draw' | 'type', value: string, name: string, date: string }) => Promise<void>;
  notifySuperadminOnFerpaConsent: (details: {
    studentName: string;
    studentEmail?: string;
    studentUid?: string;
    schoolName?: string;
    schoolId?: string;
    additionalSchoolIds?: string[];
    additionalSchools?: string[];
    tenantName?: string;
    granterName: string;
    granterEmail: string;
    granterRole?: string;
    consentDate?: string;
    method?: string;
  }) => Promise<void>;
  resendStudentFerpaConsent: (studentId: string, customEmail?: string) => Promise<{ success: boolean; recipientEmail: string }>;
  process18thBirthdayTransition: (studentId?: string, overrideParentOversightChoice?: boolean) => Promise<void>;
  updateMyPassword: (newPassword: string, currentPassword?: string) => Promise<void>;
  adminResetUserPassword: (targetUid: string, newPassword?: string, forceChangeOnNextLogin?: boolean) => Promise<void>;
  systemConfig: { 
    multiTenantEnabled: boolean; 
    selectedRegionId: string;
    currentTerm: string;
    availableTerms: string[];
  };
  updateSystemConfig: (updates: Partial<{ 
    multiTenantEnabled: boolean; 
    selectedRegionId: string;
    currentTerm: string;
    availableTerms: string[];
  }>) => void;
  selectedTerm: string;
  setSelectedTerm: (term: string) => void;
  getTenantBaseUrl: (tenant?: Tenant | null) => string;
  getOrgTeamInviteUrl: (orgUser: Partial<UserProfile>, member?: { name?: string; title?: string; email?: string; phone?: string; orgRole?: string }) => string;
  sendOrgTeamInviteEmail: (orgUser: Partial<UserProfile>, member: { name: string; title?: string; email: string; phone?: string; orgRole: string }) => Promise<void>;
  delegateOrgLead: (newLeadUid: string, targetOrgId?: string) => Promise<void>;
  removeOrgTeamMember: (
    targetMember: { id: string; uid?: string; email?: string; name?: string },
    options?: { deleteAuthAndProfile?: boolean; targetOrgId?: string }
  ) => Promise<void>;
  submitRegionInquiry: (data: {
    regionId: string;
    regionName: string;
    name: string;
    email: string;
    phone?: string;
    role: string;
    organization: string;
    notes?: string;
  }) => Promise<void>;
  shouldUserReceiveNotification: (
    user: UserProfile | null | undefined,
    channel: 'email' | 'in_app',
    category?: keyof NotificationPreferences
  ) => boolean;

  // Superadmin Remote Proxy (Mimic View)
  proxyOriginalSuperadmin: UserProfile | null;
  isProxyActive: boolean;
  startProxySession: (targetUser: UserProfile) => void;
  exitProxySession: () => void;
}

const WBLContext = createContext<WBLContextProps | undefined>(undefined);

export const GLOBAL_TENANT: Tenant = {
  id: 'global',
  name: 'Global',
  subdomain: 'global',
  logo: 'Cpu',
  primaryColor: '#0094c8',
  secondaryColor: '#0f172a',
  welcomeMessage: 'Welcome to the WBL Administration Portal.',
  isActive: true,
  highSchoolActive: true,
  postSecondaryActive: true,
};

const VALID_VIEWS = ['profile', 'opportunities', 'console', 'dashboard', 'onboarding', 'setup', 'applications', 'placements', 'placement-detail', 'advisees', 'advisors', 'messages', 'settings', 'parent-onboarding', 'org-onboarding', 'reports'];

function parseUrl() {
  if (typeof window === 'undefined') {
    return { tenantParam: null, viewParam: null };
  }
  const segments = window.location.pathname.split('/').filter(Boolean);
  
  // Explicitly handle and get rid of the /onboarding page (not parent-onboarding)
  if (segments.includes('onboarding')) {
    window.history.replaceState(null, '', '/');
    return { tenantParam: null, viewParam: 'dashboard' };
  }

  let tenantParam: string | null = null;
  let viewParam: string | null = null;

  if (segments.length === 1) {
    const seg = segments[0].toLowerCase();
    if (VALID_VIEWS.includes(seg)) {
      viewParam = seg;
    } else {
      tenantParam = segments[0];
    }
  } else if (segments.length >= 2) {
    const seg1 = segments[0].toLowerCase();
    const seg2 = segments[1].toLowerCase();
    
    if (VALID_VIEWS.includes(seg1)) {
      viewParam = seg1;
    } else {
      tenantParam = segments[0];
      if (VALID_VIEWS.includes(seg2)) {
        viewParam = seg2;
      }
    }
  }

  return { tenantParam, viewParam };
}

function pushUrlState(tenantId: string | null, view: string) {
  if (typeof window === 'undefined') return;
  
  let targetPath = '/';
  const hasTenant = tenantId && tenantId !== 'global' && tenantId !== 'apextech';

  if (hasTenant) {
    targetPath += tenantId;
    if (view && view !== 'dashboard') {
      targetPath += `/${view}`;
    }
  } else {
    if (view && view !== 'dashboard') {
      targetPath += view;
    }
  }

  const currentSearch = window.location.search || '';
  const fullTarget = `${targetPath}${currentSearch}`;

  if (window.location.pathname !== targetPath) {
    window.history.pushState({ tenantId, view }, '', fullTarget);
  }
}

const generateRandomCode = (length: number = 8) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export const getNormalizedDate = (dateStr: string) => {
  if (!dateStr) return '';
  if (dateStr.includes('/')) {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
    }
  }
  const match = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (match) {
    return `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}`;
  }
  return dateStr;
};

export function getUserRegions(
  user: UserProfile | null, 
  tenantList: Tenant[], 
  schoolList: School[] = []
): Tenant[] {
  if (!user) return [];
  if (user.role === 'superadmin') {
    return (tenantList || []).filter(t => t.id !== 'global');
  }

  const regionIds = new Set<string>();
  if (user.tenantId && user.tenantId !== 'global') {
    regionIds.add(user.tenantId.toLowerCase().trim());
  }
  if (user.region && user.region.toLowerCase().trim() !== 'global') {
    const match = (tenantList || []).find(t => 
      t.name.toLowerCase().trim() === user.region?.toLowerCase().trim() || 
      t.id.toLowerCase().trim() === user.region?.toLowerCase().trim()
    );
    if (match) regionIds.add(match.id);
  }
  if (user.extendedRegions && Array.isArray(user.extendedRegions)) {
    user.extendedRegions.forEach(r => {
      if (r && r !== 'global') regionIds.add(r.toLowerCase().trim());
    });
  }
  
  // For advisor / student / parent school linkages
  const schoolRefIds = Array.from(new Set([
    user.schoolId,
    user.pendingSchoolId,
    ...(user.schoolsServedIds || []),
    user.school,
    user.pendingSchool,
    ...(user.schoolsServed || [])
  ].filter(Boolean) as string[]));

  for (const ref of schoolRefIds) {
    const normRef = ref.toLowerCase().trim();
    const matchedSchool = (schoolList || []).find(s => 
      s.id.toLowerCase().trim() === normRef || 
      s.name.toLowerCase().trim() === normRef
    );
    if (matchedSchool?.regionId && matchedSchool.regionId !== 'global') {
      regionIds.add(matchedSchool.regionId.toLowerCase().trim());
    }
  }

  return (tenantList || []).filter(t => t.id !== 'global' && regionIds.has(t.id.toLowerCase().trim()));
}

export function resolveUserTenant(
  user: UserProfile | null, 
  tenantList: Tenant[], 
  schoolList: School[] = []
): Tenant | null {
  if (!user) return null;
  if (user.role === 'superadmin') {
    return (tenantList || []).find(t => t.id === 'global') || GLOBAL_TENANT;
  }

  // 1. Direct tenantId match if valid and not global
  if (user.tenantId && user.tenantId !== 'global') {
    const normId = user.tenantId.toLowerCase().trim();
    const found = (tenantList || []).find(t => t.id.toLowerCase() === normId || (t.subdomain && t.subdomain.toLowerCase() === normId));
    if (found) return found;
  }

  // 2. Direct region name match
  if (user.region && user.region.toLowerCase().trim() !== 'global') {
    const normRegion = user.region.toLowerCase().trim();
    const found = (tenantList || []).find(t => 
      t.name.toLowerCase().trim() === normRegion ||
      t.id.toLowerCase().trim() === normRegion
    );
    if (found) return found;
  }

  // 3. Resolve from advisor/student schools
  const schoolRefIds = Array.from(new Set([
    user.schoolId,
    user.pendingSchoolId,
    ...(user.schoolsServedIds || []),
    user.school,
    user.pendingSchool,
    ...(user.schoolsServed || [])
  ].filter(Boolean) as string[]));

  for (const ref of schoolRefIds) {
    const normRef = ref.toLowerCase().trim();
    const matchedSchool = (schoolList || []).find(s => 
      s.id.toLowerCase().trim() === normRef || 
      s.name.toLowerCase().trim() === normRef
    );
    if (matchedSchool?.regionId && matchedSchool.regionId !== 'global') {
      const found = (tenantList || []).find(t => t.id.toLowerCase() === matchedSchool.regionId.toLowerCase());
      if (found) return found;
    }
  }

  // 4. Extended regions if any
  if (user.extendedRegions && user.extendedRegions.length > 0) {
    const firstExt = user.extendedRegions[0];
    const found = (tenantList || []).find(t => t.id.toLowerCase() === firstExt.toLowerCase());
    if (found) return found;
  }

  return null;
}

export const WBLProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  const [tenants, setTenants] = useState<Tenant[]>(INITIAL_TENANTS);
  const [globalBranding, setGlobalBranding] = useState<GlobalBranding>(DEFAULT_GLOBAL_BRANDING);
  const [activeTenant, setActiveTenant] = useState<Tenant>(GLOBAL_TENANT);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const processedBirthdayTransitionSet = useRef<Set<string>>(new Set());
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [tasks, setTasks] = useState<StudentTask[]>([]);
  const [attendance, setAttendance] = useState<AttendanceReport[]>([]);
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [messages, setMessages] = useState<PortalMessage[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [userLinks, setUserLinks] = useState<any[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [allSchools, setAllSchools] = useState<School[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [allDistricts, setAllDistricts] = useState<District[]>([]);
  const [activeUser, setActiveUserRaw] = useState<UserProfile | null>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = sessionStorage.getItem('wbl_active_user') || localStorage.getItem('wbl_active_user');
        if (stored) return JSON.parse(stored);
      } catch (e) {
        console.warn("Failed to parse stored active user:", e);
      }
    }
    return null;
  });

  const setActiveUser = (user: UserProfile | null | ((prev: UserProfile | null) => UserProfile | null)) => {
    setActiveUserRaw(prev => {
      const nextUser = typeof user === 'function' ? user(prev) : user;
      if (typeof window !== 'undefined') {
        if (nextUser) {
          sessionStorage.setItem('wbl_active_user', JSON.stringify(nextUser));
          localStorage.setItem('wbl_active_user', JSON.stringify(nextUser));
        } else {
          sessionStorage.removeItem('wbl_active_user');
          localStorage.removeItem('wbl_active_user');
        }
      }
      return nextUser;
    });
  };
  const lastUserUidRef = useRef<string | null>(null);

  // Superadmin Remote Proxy (Mimic View) State
  const [proxyOriginalSuperadmin, setProxyOriginalSuperadmin] = useState<UserProfile | null>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = sessionStorage.getItem('wbl_superadmin_proxy_backup');
        if (stored) return JSON.parse(stored);
      } catch (e) {
        console.warn("Failed to parse stored proxy superadmin backup:", e);
      }
    }
    return null;
  });

  const isProxyActive = Boolean(proxyOriginalSuperadmin);

  const checkProxyActionGuard = (actionName: string = 'This action'): boolean => {
    if (isProxyActive) {
      inAppToast({
        title: 'View-Only Mode Safeguard',
        message: `${actionName} is protected and cannot be changed while in superadmin proxy mode.`,
        type: 'warning',
        duration: 4500
      });
      return true;
    }
    return false;
  };

  const startProxySession = (targetUser: UserProfile) => {
    const effectiveSuperadmin = proxyOriginalSuperadmin || (activeUser?.role === 'superadmin' ? activeUser : null);
    if (!effectiveSuperadmin) {
      inAppToast({
        title: 'Proxy Restricted',
        message: 'Only superadmins are authorized to initiate remote proxy sessions.',
        type: 'error'
      });
      return;
    }

    setProxyOriginalSuperadmin(effectiveSuperadmin);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('wbl_superadmin_proxy_backup', JSON.stringify(effectiveSuperadmin));
    }

    setActiveUser(targetUser);
    setCurrentView('dashboard');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    inAppToast({
      title: 'Mimic View Active',
      message: `Now viewing as ${targetUser.name || targetUser.email} (${targetUser.role}). Modifications are guarded in view-only mode.`,
      type: 'info',
      duration: 5000
    });
  };

  const exitProxySession = () => {
    if (!proxyOriginalSuperadmin) return;

    const restoredAdmin = proxyOriginalSuperadmin;
    setProxyOriginalSuperadmin(null);
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('wbl_superadmin_proxy_backup');
    }

    setActiveUser(restoredAdmin);
    setCurrentView('superadmin-users');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    inAppToast({
      title: 'Exited Proxy Mode',
      message: 'Returned to Superadmin Users Console.',
      type: 'success',
      duration: 3500
    });
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFirebaseActive, setIsFirebaseActive] = useState<boolean>(!isUsingMockKeys);
  const [isSystemConfigLoaded, setIsSystemConfigLoaded] = useState<boolean>(isUsingMockKeys); // if using mock keys, system config is considered already loaded.
  const [viewingStudentId, setViewingStudentId] = useState<string | null>(null);
  const [selectedTerm, setSelectedTermState] = useState<string>(() => {
    return localStorage.getItem('wbl_selected_term') || 'Fall 2026';
  });

  const setSelectedTerm = (term: string) => {
    setSelectedTermState(term);
    try {
      localStorage.setItem('wbl_selected_term', term);
    } catch (e) {}
  };

  const [systemConfig, setSystemConfig] = useState<{
    multiTenantEnabled?: boolean;
    selectedRegionId?: string;
    currentTerm: string;
    availableTerms: string[];
    [key: string]: any;
  }>({ 
    multiTenantEnabled: true, 
    selectedRegionId: 'global',
    currentTerm: 'Fall 2026',
    availableTerms: ['Fall 2026', 'Spring 2027', 'Summer 2027', 'Fall 2027'],
    prohibitedDomains: ['@spart1.net']
  });
  const [lastActivitySync, setLastActivitySync] = useState<number>(Date.now());

  // Portal Usage Tracking (SC HB 3431 Compliance)
  useEffect(() => {
    if (!activeUser || isUsingMockKeys) return;

    const interval = setInterval(() => {
      // Only track if tab is focused
      if (document.visibilityState === 'visible') {
        const userRef = doc(db, 'users', activeUser.uid);
        updateDoc(userRef, {
          totalMinutesOnPortal: (activeUser.totalMinutesOnPortal || 0) + 1
        }).catch(err => console.error("Usage sync error:", err));
      }
    }, 60000); // Every 60 seconds

    return () => clearInterval(interval);
  }, [activeUser?.uid, isUsingMockKeys]);

  // Load system config from Firestore
  useEffect(() => {
    if (isFirebaseActive) {
      const unsub = onSnapshot(doc(db, 'config', 'system'), (snapshot) => {
        if (snapshot.exists()) {
          console.log("WBL Gateway: System config synchronized.", snapshot.data());
          const data = snapshot.data() as any;
          const activeCurrentTerm = data.currentTerm || 'Fall 2026';
          const validTerms = (data.availableTerms && data.availableTerms.length > 0)
            ? data.availableTerms
            : ['Fall 2026', 'Spring 2027', 'Summer 2027', 'Fall 2027'];

          setSystemConfig(prev => ({
            ...prev,
            ...data,
            currentTerm: activeCurrentTerm,
            availableTerms: validTerms
          }));
          
          setSelectedTermState(prev => {
            const saved = localStorage.getItem('wbl_selected_term');
            if (saved && validTerms.includes(saved)) {
              return saved;
            }
            if (validTerms.includes(prev)) {
              return prev;
            }
            return activeCurrentTerm;
          });
        } else {
          console.log("WBL Gateway: System config document missing. using defaults.");
        }
        setIsSystemConfigLoaded(true);
      }, (error) => {
        console.error("WBL Gateway Permission Error: config/system", error);
        handleFirestoreError(error, OperationType.GET, 'config/system');
        setIsSystemConfigLoaded(true);
      });
      return () => unsub();
    }
  }, [isFirebaseActive]);

  const grantFerpaConsent = async (granted: boolean, signatureData?: { type: 'draw' | 'type', value: string, name: string, date: string }) => {
    if (!activeUser) return;
    const consentDate = signatureData?.date || new Date().toISOString();
    const updates = {
      ferpaConsent: {
        granted,
        date: consentDate,
        version: '1.0',
        signatureType: signatureData?.type,
        signatureValue: signatureData?.value,
      },
      ferpaConsentName: granted ? (signatureData?.name || activeUser.name) : '',
      ferpaConsentDate: granted ? consentDate : ''
    };
    
    try {
      if (!isUsingMockKeys && isFirebaseActive) {
        // 1. Update Parent Record
        await updateDoc(doc(db, 'users', activeUser.uid), updates);
        
        // 2. Propagate to Child Records
        if (activeUser.role === 'parent') {
          const studentIds = new Set<string>();
          
          if (activeUser.childId) {
            studentIds.add(activeUser.childId);
          }
          if (Array.isArray(activeUser.childrenIds)) {
            activeUser.childrenIds.forEach(id => { if (id) studentIds.add(id); });
          }
          
          const linksQuery = query(collection(db, 'userLinks'), where('parentId', '==', activeUser.uid));
          const linksSnap = await getDocs(linksQuery);
          linksSnap.forEach(ls => {
            const sid = ls.data().studentId;
            if (sid) studentIds.add(sid);
          });

          // Check users collection for parentId or parentEmail matching
          const parentUid = activeUser.uid;
          const parentEmail = activeUser.email;
          users.forEach(u => {
            if (u.role === 'student' && (u.parentId === parentUid || (parentEmail && u.parentEmail?.toLowerCase() === parentEmail.toLowerCase()))) {
              studentIds.add(u.uid);
            }
          });

          const childQuery1 = query(collection(db, 'users'), where('parentId', '==', parentUid));
          const childSnap1 = await getDocs(childQuery1);
          childSnap1.forEach(cs => studentIds.add(cs.id));

          if (parentEmail) {
            const childQuery2 = query(collection(db, 'users'), where('role', '==', 'student'), where('parentEmail', '==', parentEmail));
            const childSnap2 = await getDocs(childQuery2);
            childSnap2.forEach(cs => studentIds.add(cs.id));
          }

          if (studentIds.size > 0) {
            console.log(`WBL FERPA Engine: Propagating consent (${granted}) to ${studentIds.size} student profiles...`);
            for (const sid of studentIds) {
              await updateDoc(doc(db, 'users', sid), {
                ferpaConsent: updates.ferpaConsent,
                ferpaConsentName: updates.ferpaConsentName,
                ferpaConsentDate: updates.ferpaConsentDate,
                parentId: activeUser.uid
              });
              console.log(`WBL FERPA Engine: Successfully updated student ${sid}`);
            }
          }
        }
      }

      // Update local users state
      setUsers(prev => prev.map(u => {
        const isParent = u.uid === activeUser.uid;
        const studentIdsArr = Array.isArray(activeUser.childrenIds) ? activeUser.childrenIds : (activeUser.childId ? [activeUser.childId] : []);
        const isLinked = userLinks.some(l => l.parentId === activeUser.uid && l.studentId === u.uid);
        const isChild = studentIdsArr.includes(u.uid) || u.parentEmail === activeUser.email || u.parentId === activeUser.uid || isLinked;
        if (isParent || isChild) {
          return { ...u, ...updates };
        }
        return u;
      }));

      // Update local state for immediate feedback
      setActiveUser({ ...activeUser, ...updates });
      if (granted) {
        if (activeUser.role === 'parent') {
          // Identify all children of this parent
          const linkedStudents = users.filter(u => 
            u.role === 'student' && (
              u.parentId === activeUser.uid ||
              (activeUser.childId && u.uid === activeUser.childId) ||
              (Array.isArray(activeUser.childrenIds) && activeUser.childrenIds.includes(u.uid)) ||
              (activeUser.email && u.parentEmail?.toLowerCase() === activeUser.email.toLowerCase()) ||
              (activeUser.email && u.ferpaConsentRecipientEmail?.toLowerCase() === activeUser.email.toLowerCase())
            )
          );

          if (linkedStudents.length > 0) {
            for (const child of linkedStudents) {
              notifySuperadminOnFerpaConsent({
                studentName: child.name,
                studentEmail: child.email,
                studentUid: child.uid,
                schoolName: child.school,
                schoolId: child.schoolId,
                additionalSchoolIds: child.additionalSchoolIds,
                additionalSchools: child.additionalSchools,
                tenantName: child.tenantId,
                granterName: activeUser.name || activeUser.email,
                granterEmail: activeUser.email,
                granterRole: 'Parent/Guardian',
                consentDate: updates.ferpaConsentDate,
                method: 'Parent Digital Authorization'
              }).catch(err => console.warn('Silent advisor/admin notification warning:', err));
            }
          } else {
            notifySuperadminOnFerpaConsent({
              studentName: activeUser.name || 'Student Profile',
              studentEmail: activeUser.email,
              studentUid: activeUser.uid,
              schoolName: activeUser.school,
              schoolId: activeUser.schoolId,
              additionalSchoolIds: activeUser.additionalSchoolIds,
              additionalSchools: activeUser.additionalSchools,
              tenantName: activeTenant?.name || activeUser.tenantId,
              granterName: activeUser.name || activeUser.email,
              granterEmail: activeUser.email,
              granterRole: 'Parent/Guardian',
              consentDate: updates.ferpaConsentDate,
              method: 'Parent Digital Authorization'
            }).catch(err => console.warn('Silent advisor/admin notification warning:', err));
          }
        } else {
          // Eligible student self-consenting (18+)
          notifySuperadminOnFerpaConsent({
            studentName: activeUser.name || 'Student Profile',
            studentEmail: activeUser.email,
            studentUid: activeUser.uid,
            schoolName: activeUser.school,
            schoolId: activeUser.schoolId,
            additionalSchoolIds: activeUser.additionalSchoolIds,
            additionalSchools: activeUser.additionalSchools,
            tenantName: activeTenant?.name || activeUser.tenantId,
            granterName: activeUser.name || activeUser.email,
            granterEmail: activeUser.email,
            granterRole: 'Eligible Student (18+)',
            consentDate: updates.ferpaConsentDate,
            method: 'Eligible Student Self-Consent (18+)'
          }).catch(err => console.warn('Silent advisor/admin notification warning:', err));
        }
      } else {
        writeLog(activeUser.tenantId, activeUser.email, 'FERPA_CONSENT', `User updated FERPA consent status: ${granted}`);
      }
    } catch (err) {
      console.error("WBL FERPA Engine Error:", err);
      throw err;
    }
  };

  const notifySuperadminOnFerpaConsent = async (details: {
    studentName: string;
    studentEmail?: string;
    studentUid?: string;
    schoolName?: string;
    schoolId?: string;
    additionalSchoolIds?: string[];
    additionalSchools?: string[];
    tenantName?: string;
    granterName: string;
    granterEmail: string;
    granterRole?: string;
    consentDate?: string;
    method?: string;
  }) => {
    try {
      // Record structured audit trail entry
      try {
        writeLog(
          details.tenantName || 'global',
          details.granterEmail,
          'FERPA_CONSENT_GRANTED',
          `FERPA consent release granted for student ${details.studentName} (${details.studentEmail || details.studentUid || 'Profile'}) by ${details.granterName} (${details.granterEmail}) [${details.granterRole || 'Signer'}] via ${details.method || 'Digital Authorization'}`
        );
      } catch (logErr) {
        console.warn('WBL FERPA Notify log write note:', logErr);
      }

      // Authoritative backend dispatch to Advisors and Administrators via SendGrid and Firestore
      try {
        await fetch('/api/notify-ferpa-signed', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            studentUid: details.studentUid,
            studentName: details.studentName,
            studentEmail: details.studentEmail,
            schoolName: details.schoolName,
            schoolId: details.schoolId,
            additionalSchoolIds: details.additionalSchoolIds,
            additionalSchools: details.additionalSchools,
            tenantId: details.tenantName || 'spartanburg',
            signerName: details.granterName,
            signerEmail: details.granterEmail,
            signerRole: details.granterRole || 'Parent/Guardian',
            consentDate: details.consentDate,
            method: details.method || 'Digital Authorization'
          })
        });
        console.log('WBL FERPA Notify: Successfully dispatched via backend API.');
      } catch (apiErr) {
        console.warn('WBL FERPA Notify: Backend API dispatch note:', apiErr);
      }
    } catch (err) {
      console.error("WBL FERPA Notify Error:", err);
    }
  };

  const adminOverrideFerpaConsent = async (studentId: string, signatureData: { type: 'draw' | 'type', value: string, name: string, date: string }) => {
    if (!activeUser || !isFirebaseActive) return;
    
    // Admin only
    if (activeUser.role !== 'regional_admin' && activeUser.role !== 'superadmin') {
      throw new Error("Unauthorized");
    }

    const consentDate = signatureData.date || new Date().toISOString();
    const updates = {
      ferpaConsent: {
        granted: true,
        date: consentDate,
        version: '1.0-override', // Marking as override
        signatureType: signatureData.type,
        signatureValue: signatureData.value,
        overrideBy: activeUser.name,
        overrideByUid: activeUser.uid,
      },
      ferpaConsentName: signatureData.name,
      ferpaConsentDate: consentDate
    };
    
    try {
      await updateDoc(doc(db, 'users', studentId), updates);
      setUsers(prev => prev.map(u => u.uid === studentId ? { ...u, ...updates } : u));
      const targetStudent = users.find(u => u.uid === studentId);
      writeLog(activeUser.tenantId, activeUser.email, 'FERPA_CONSENT_GRANTED', `Admin ${activeUser.name} (${activeUser.email}) overrode and granted FERPA consent for student ${targetStudent?.name || studentId}`);
      notifySuperadminOnFerpaConsent({
        studentName: targetStudent?.name || signatureData.name || 'Student Profile',
        studentEmail: targetStudent?.email,
        studentUid: studentId,
        schoolName: targetStudent?.school,
        schoolId: targetStudent?.schoolId,
        additionalSchoolIds: targetStudent?.additionalSchoolIds,
        additionalSchools: targetStudent?.additionalSchools,
        tenantName: targetStudent?.tenantId || activeUser.tenantId,
        granterName: `${activeUser.name} (Admin Override)`,
        granterEmail: activeUser.email,
        granterRole: activeUser.role,
        consentDate: consentDate,
        method: 'Admin Verification Override'
      }).catch(err => console.warn('Silent superadmin notification warning on override:', err));
    } catch (err) {
      console.error("WBL FERPA Override Engine Error:", err);
      throw err;
    }
  };

  const resendStudentFerpaConsent = async (studentId: string, customEmail?: string): Promise<{ success: boolean; recipientEmail: string }> => {
    if (!activeUser) {
      throw new Error("Unauthorized to dispatch consent reminders.");
    }

    const isSelfStudent = activeUser.role === 'student' && (activeUser.uid === studentId || activeUser.id === studentId);
    const isAdminOrStaff = activeUser.role === 'regional_admin' || activeUser.role === 'superadmin' || activeUser.role === 'district_admin' || activeUser.role === 'school_admin' || activeUser.role === 'advisor';

    if (!isSelfStudent && !isAdminOrStaff) {
      throw new Error("Unauthorized to dispatch consent reminders.");
    }

    const student = users.find(u => u.uid === studentId || u.id === studentId) || (activeUser.uid === studentId || activeUser.id === studentId ? activeUser : null);
    if (!student) throw new Error("Student record not found.");

    const targetEmail = (customEmail || student.parentEmail || activeUser.parentEmail || '').trim().toLowerCase();
    if (!targetEmail) {
      throw new Error("Please specify a valid parent email address for the consent reminder.");
    }

    const studentTenant = tenants.find(t => t.id === student.tenantId) || activeTenant;
    const studentCode = student.studentCode || '';
    const dob = student.dateOfBirth || '';
    const studentName = student.name || 'Student';
    const baseUrl = getTenantBaseUrl(studentTenant);
    const signupUrl = `${baseUrl}/parent-onboarding?code=${studentCode}&dob=${dob}&name=${encodeURIComponent(studentName)}&region=${studentTenant.id}`;
    const activeRegionNoun = formatRegionWithNoun(studentTenant.name || studentTenant.id || 'Work-Based Learning Portal');
    const sentTimestamp = new Date().toISOString();

    await sendNotificationEmail(
      targetEmail,
      `Parent/Guardian Consent Required: ${studentName}`,
      'Work-Based Learning Portal Consent Request',
      `
        <p>Dear Parent or Guardian,</p>
        <p>This is an official consent request for your student, <strong>${studentName}</strong>, enrolled in the Work-Based Learning Portal for the <strong>${activeRegionNoun}</strong>.</p>
        <p>Under Federal Family Educational Rights and Privacy Act (FERPA) requirements, signed parental/guardian consent is required before student placement data, applications, and advisor verifications can proceed.</p>
        
        <div style="text-align: center; margin: 32px 0;">
          <a href="${signupUrl}" style="background-color: #0094c8; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: bold; display: inline-block;">Complete Parent Registration & Sign Consent</a>
        </div>

        <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin: 24px 0; border: 1px dashed #cbd5e1;">
          <p style="margin-top: 0; font-size: 12px; color: #64748b; font-weight: 600;">Student Identification Details:</p>
          <div style="margin-bottom: 8px;">
            <span style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: bold; color: #94a3b8;">Student Name:</span>
            <span style="font-size: 13px; font-weight: bold; color: #0f172a; margin-left: 6px;">${studentName}</span>
          </div>
          <div style="margin-bottom: 8px;">
            <span style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: bold; color: #94a3b8;">School:</span>
            <span style="font-size: 13px; font-weight: 600; color: #0f172a; margin-left: 6px;">${student.school || 'Unassigned High School'}</span>
          </div>
          ${studentCode ? `
          <div>
            <span style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: bold; color: #94a3b8;">Student Access Code:</span>
            <span style="font-size: 13px; font-family: monospace; font-weight: bold; color: #0094c8; margin-left: 6px;">${studentCode}</span>
          </div>` : ''}
        </div>

        <p style="font-size: 12px; color: #64748b;">If you already have a parent account on the portal, signing in will automatically link ${studentName} to your profile.</p>
      `,
      studentTenant.id
    );

    const updates: Partial<UserProfile> = {
      ferpaConsentSentAt: sentTimestamp,
      ferpaConsentRecipientEmail: targetEmail
    };
    if (customEmail && customEmail.toLowerCase() !== (student.parentEmail || '').toLowerCase()) {
      updates.parentEmail = targetEmail;
    }
    if (isSelfStudent) {
      updates.studentAttestation = {
        certified: true,
        timestamp: sentTimestamp,
        statement: "I certify under penalty of disciplinary action, loss of program credit, and potential removal from the Work-Based Learning program that the email address provided belongs exclusively to my legal parent or court-appointed legal guardian.",
        emailProvided: targetEmail
      };
    }

    await updateUserProfile(studentId, updates);
    writeLog(
      studentTenant.id, 
      activeUser.email, 
      'FERPA_STUDENT_ATTESTATION', 
      `Student legal attestation certified under penalty of disciplinary action for ${studentName} (${student.email || studentId}). Parent recipient email: ${targetEmail}.`
    );

    return { success: true, recipientEmail: targetEmail };
  };

  const updateMyPassword = async (newPassword: string, currentPassword?: string) => {
    if (!activeUser && !auth.currentUser) throw new Error('Not authenticated');
    
    // 1. If Firebase Auth currentUser is present, attempt client-side reauthentication and updatePassword
    if (auth.currentUser && !isUsingMockKeys) {
      try {
        const { updatePassword, EmailAuthProvider, reauthenticateWithCredential } = await import('firebase/auth');
        if (currentPassword && auth.currentUser.email) {
          try {
            const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
            await reauthenticateWithCredential(auth.currentUser, credential);
          } catch (reauthErr: any) {
            console.warn('Firebase client reauthentication notice:', reauthErr?.message);
          }
        }
        await updatePassword(auth.currentUser, newPassword);
      } catch (err: any) {
        console.warn('Firebase client auth updatePassword notice:', err?.message);
      }
    }

    // 2. Call backend reset-password endpoint with Firebase Admin SDK privileges to guarantee password update in Firebase Authentication
    const targetUid = activeUser?.uid || auth.currentUser?.uid;
    const targetEmail = activeUser?.email || auth.currentUser?.email;
    if (newPassword && (targetUid || targetEmail)) {
      try {
        await fetch('/api/admin/reset-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            targetUid,
            email: targetEmail,
            newPassword
          })
        });
      } catch (authErr: any) {
        console.warn('Backend Auth password update endpoint note:', authErr?.message);
      }
    }

    // 3. Update active user profile in Firestore database and local context
    if (activeUser) {
      const resetTimestamp = new Date().toISOString();
      await updateUserProfile(activeUser.uid, { 
        requiresPasswordChange: false,
        firstLogin: false,
        customPassword: newPassword,
        tempPassword: newPassword,
        lastPasswordReset: resetTimestamp
      });
      setActiveUser({ 
        ...activeUser, 
        requiresPasswordChange: false, 
        firstLogin: false,
        customPassword: newPassword,
        tempPassword: newPassword,
        lastPasswordReset: resetTimestamp
      });
      writeLog(activeUser.tenantId, activeUser.email, 'PASSWORD_UPDATED', `User ${activeUser.name} updated security access credentials.`);
    }
  };

  const adminResetUserPassword = async (
    targetUid: string, 
    newPassword?: string, 
    forceChangeOnNextLogin: boolean = true
  ) => {
    let targetUser = users.find(u => u.uid === targetUid || (u as any).id === targetUid) || (isUsingMockKeys ? MOCK_USERS.find(u => u.uid === targetUid) : undefined);

    if (!targetUser && !isUsingMockKeys) {
      try {
        const userDoc = await getDoc(doc(db, 'users', targetUid));
        if (userDoc.exists()) {
          targetUser = { uid: userDoc.id, ...userDoc.data() } as UserProfile;
        }
      } catch (err) {
        console.warn('Could not fetch user directly from Firestore:', err);
      }
    }

    const resetTimestamp = new Date().toISOString();
    const adminIdentifier = activeUser?.email || 'Console Administrator';

    // 1. Call backend server endpoint to update Firebase Authentication credentials directly if available
    if (newPassword && targetUser?.email) {
      try {
        await fetch('/api/admin/reset-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            targetUid,
            email: targetUser.email,
            newPassword
          })
        });
      } catch (authErr: any) {
        console.warn('Backend Auth reset endpoint note:', authErr.message);
      }
    }

    // 2. Update target user profile in Firestore / local state
    const profileUpdates: Partial<UserProfile> = {
      requiresPasswordChange: forceChangeOnNextLogin,
      lastPasswordReset: resetTimestamp,
      resetByAdmin: adminIdentifier
    };
    if (newPassword) {
      profileUpdates.customPassword = newPassword;
      profileUpdates.tempPassword = newPassword;
    }

    await updateUserProfile(targetUid, profileUpdates);

    if (newPassword && targetUser?.email) {
      // Send notification email if template or email service available
      await sendManualPortalMessage(
        targetUser.email,
        'Work-Based Learning Portal Password Reset Notice',
        'Your Password Has Been Updated',
        `<p>Dear ${targetUser.name || 'User'},</p><p>An administrator has updated your password for the Work-Based Learning Portal. Your new temporary password is: <strong>${newPassword}</strong></p><p>Please sign in with this temporary password and update your credentials in your account settings.</p>`
      );
    }

    // Notify administrators if systemSecurityAlerts is turned on
    try {
      const securityAdmins = users.filter(u => 
        (u.role === 'superadmin' || (u.role === 'regional_admin' && (u.tenantId === targetUser?.tenantId || u.region === targetUser?.region))) &&
        u.email && 
        u.email.toLowerCase() !== activeUser?.email?.toLowerCase()
      );
      for (const adm of securityAdmins) {
        if (adm.email && shouldUserReceiveNotification(adm, 'email', 'systemSecurityAlerts')) {
          sendNotificationEmail(
            adm.email,
            `[Security Audit] Administrative Password Reset: ${targetUser?.name || targetUid}`,
            `Security Audit: Administrative Password Reset`,
            `
              <p>Hello Administrator,</p>
              <p>An administrative credential reset has been executed on the Work-Based Learning Portal:</p>
              <div style="background-color: #fff1f2; border-left: 4px solid #f43f5e; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;">
                <strong>Target Account:</strong> ${targetUser?.name || 'User'} (${targetUser?.email || targetUid})<br/>
                <strong>Target Role:</strong> ${targetUser?.role || 'User'}<br/>
                <strong>Authorized By:</strong> ${adminIdentifier}<br/>
                <strong>Timestamp:</strong> ${new Date(resetTimestamp).toLocaleString('en-US', { timeZone: 'America/New_York' })} EST
              </div>
              <p>This event has been recorded in the platform security audit log.</p>
            `,
            targetUser?.tenantId || 'global',
            'systemSecurityAlerts'
          ).catch(() => {});
        }
      }
    } catch (secErr) {
      console.warn('WBL Notify: Silent error on dispatching security audit alerts:', secErr);
    }
  };

  const updateSystemConfig = async (updates: Partial<{ 
    multiTenantEnabled: boolean; 
    selectedRegionId: string;
    currentTerm: string;
    availableTerms: string[];
  }>) => {
    const newConfig = { ...systemConfig, ...updates };
    setSystemConfig(newConfig);
    if (updates.currentTerm) {
      setSelectedTerm(updates.currentTerm);
    }
    
    if (isFirebaseActive) {
      try {
        await setDoc(doc(db, 'config', 'system'), cleanFirestoreData(newConfig));
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'config/system');
      }
    }
  };

  useEffect(() => {
    if (activeUser?.role === 'parent' && activeUser.childId) {
      setViewingStudentId(activeUser.childId);
    } else if (activeUser?.role === 'student') {
      setViewingStudentId(activeUser.uid);
      
      // Auto-generate student code for existing students if missing
      if (!activeUser.studentCode && !isUsingMockKeys) {
        const newCode = generateRandomCode(6);
        updateDoc(doc(db, 'users', activeUser.uid), {
          studentCode: newCode
        }).catch(err => console.warn("Background code sync error:", err));
      }
    } else {
      setViewingStudentId(null);
    }
  }, [activeUser, isUsingMockKeys]);
  const [isDbSeeded, setIsDbSeeded] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<string>('dashboard');

  // Sync activeTenant with systemConfig if single-tenant mode is enabled
  useEffect(() => {
    if (!systemConfig.multiTenantEnabled && systemConfig.selectedRegionId !== 'global') {
        const found = tenants.find(t => t.id === systemConfig.selectedRegionId);
        if (found) {
            setActiveTenant(prev => prev.id === found.id ? prev : found);
            if (window.location.pathname === '/') {
                const tenantUrlId = found.subdomain || found.id;
                pushUrlState(tenantUrlId, currentView);
            }
        }
    } else if (systemConfig.multiTenantEnabled && !parseUrl().tenantParam) {
        if (activeUser) {
          const userRegs = getUserRegions(activeUser, tenants, allSchools);
          if (userRegs.length <= 1 && activeUser.role !== 'superadmin') {
            const resolved = resolveUserTenant(activeUser, tenants, allSchools);
            if (resolved) {
              setActiveTenant(prev => prev.id === resolved.id ? prev : resolved);
              return;
            }
          }
        }
        if (!activeUser || activeUser.role === 'superadmin') {
          setActiveTenant(prev => prev.id === GLOBAL_TENANT.id ? prev : GLOBAL_TENANT);
        }
    }
  }, [systemConfig.multiTenantEnabled, systemConfig.selectedRegionId, tenants, allSchools, activeUser]);

  // Load initial route from URL and listen to history navigation
  useEffect(() => {
    const handlePopState = () => {
      const { tenantParam, viewParam } = parseUrl();
      
      if (tenantParam) {
        changeTenant(tenantParam);
      } else {
        if (!systemConfig.multiTenantEnabled && systemConfig.selectedRegionId !== 'global') {
          const found = tenants.find(t => t.id === systemConfig.selectedRegionId);
          if (found) {
            setActiveTenant(prev => prev.id === found.id ? prev : found);
          } else {
            setActiveTenant(prev => prev.id === GLOBAL_TENANT.id ? prev : GLOBAL_TENANT);
          }
        } else {
          if (activeUser) {
            const userRegs = getUserRegions(activeUser, tenants, allSchools);
            if (userRegs.length <= 1 && activeUser.role !== 'superadmin') {
              const resolved = resolveUserTenant(activeUser, tenants, allSchools);
              if (resolved) {
                setActiveTenant(prev => prev.id === resolved.id ? prev : resolved);
              }
            } else {
              if (activeTenant.id !== 'global' && !userRegs.some(t => t.id === activeTenant.id)) {
                setActiveTenant(GLOBAL_TENANT);
              }
            }
          } else {
            setActiveTenant(prev => prev.id === GLOBAL_TENANT.id ? prev : GLOBAL_TENANT);
          }
        }
      }
      
      if (viewParam) {
        setCurrentView(viewParam);
      } else {
        setCurrentView('dashboard');
      }
    };

    // Run initial URL parsing
    const { tenantParam, viewParam } = parseUrl();
    if (tenantParam) {
      changeTenant(tenantParam);
    } else {
      if (!systemConfig.multiTenantEnabled && systemConfig.selectedRegionId !== 'global') {
        const found = tenants.find(t => t.id === systemConfig.selectedRegionId);
        if (found) {
          setActiveTenant(prev => prev.id === found.id ? prev : found);
        } else {
          setActiveTenant(prev => prev.id === GLOBAL_TENANT.id ? prev : GLOBAL_TENANT);
        }
      } else {
        if (activeUser) {
          const userRegs = getUserRegions(activeUser, tenants, allSchools);
          if (userRegs.length <= 1 && activeUser.role !== 'superadmin') {
            const resolved = resolveUserTenant(activeUser, tenants, allSchools);
            if (resolved) {
              setActiveTenant(prev => prev.id === resolved.id ? prev : resolved);
            }
          } else {
            setActiveTenant(prev => prev.id === GLOBAL_TENANT.id ? prev : GLOBAL_TENANT);
          }
        } else {
          setActiveTenant(prev => prev.id === GLOBAL_TENANT.id ? prev : GLOBAL_TENANT);
        }
      }
    }
    if (viewParam) {
      setCurrentView(viewParam);
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [systemConfig.multiTenantEnabled, systemConfig.selectedRegionId, tenants, allSchools, activeUser]);

  // Sync state changes back to URL
  useEffect(() => {
    const tenantId = activeTenant && activeTenant.id !== 'apextech' && activeTenant.id !== 'global'
      ? activeTenant.subdomain || activeTenant.id
      : null;
    
    pushUrlState(tenantId, currentView);
  }, [activeTenant.id, activeTenant.subdomain, currentView]);

  // Live Firebase Observer
  useEffect(() => {
    if (isUsingMockKeys) {
      setIsLoading(false);
      return;
    }

    console.log("WBL Gateway: Establishing authentication context listener...");
    setIsLoading(true);

    let profileUnsubscribe: (() => void) | null = null;

    // Safety timeout to prevent the app from getting stuck in loading state indefinitely when offline or network is slow
    const loadingTimeout = setTimeout(() => {
      console.warn("WBL Portal Warning: Firebase authentication observer resolution is taking longer than expected. Continuing with cached or offline context.");
      setIsLoading(false);
    }, 2500);

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      try {
        // Clean up previous profile listener if any
        if (profileUnsubscribe) {
          profileUnsubscribe();
          profileUnsubscribe = null;
        }

        if (firebaseUser) {
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          
          // Verify ID Token to catch corrupt sessions early
          try {
            const idTokenResult = await firebaseUser.getIdTokenResult();
            if (!idTokenResult || !idTokenResult.token) {
              throw new Error("Token is malformed or invalid");
            }
          } catch (tokenErr) {
            clearTimeout(loadingTimeout);
            console.error("WBL Portal: Authentication token is malformed or corrupt. Clearing storage & signing out.", tokenErr);
            localStorage.clear();
            sessionStorage.clear();
            await signOut(auth);
            setActiveUser(null);
            setCurrentView('dashboard');
            setIsLoading(false);
            return;
          }

          try {
            profileUnsubscribe = onSnapshot(userDocRef, async (docSnap) => {
              clearTimeout(loadingTimeout);
              try {
                if (docSnap.exists()) {
                  let profile = { uid: docSnap.id, ...docSnap.data() } as UserProfile;
                  
                  // Force Joshua to be treated as superadmin
                  if (profile.email?.toLowerCase() === 'joshua@losonova.com' && (profile.role !== 'superadmin' || profile.tenantId !== 'global')) {
                    profile.role = 'superadmin';
                    profile.tenantId = 'global';
                    profile.region = 'global';
                    profile.profileComplete = true;
                    updateDoc(userDocRef, { role: 'superadmin', tenantId: 'global', region: 'global', profileComplete: true }).catch(err => console.error("Self-healing Joshua to superadmin failed:", err));
                  }
                  
                  // Proactively sync photoURL if authentication has one but profile lacks a valid URL
                  const hasValidAvatar = profile.avatar && (profile.avatar.startsWith('http') || profile.avatar.startsWith('/') || profile.avatar.startsWith('data:') || profile.avatar.length <= 4);
                  
                  if (firebaseUser.photoURL && !hasValidAvatar) {
                    profile.avatar = firebaseUser.photoURL;
                    updateDoc(userDocRef, { avatar: firebaseUser.photoURL }).catch(err => console.error("Avatar sync write failure:", err));
                  } else if (!profile.avatar) {
                    // If absolutely no avatar, generate initials
                    const defInitials = (profile.name || firebaseUser.displayName || 'U')
                      .split(' ')
                      .filter(Boolean)
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);
                    profile.avatar = defInitials;
                    updateDoc(userDocRef, { avatar: defInitials }).catch(err => console.error("Avatar initials write failure:", err));
                  }
                  
                  // Ensure UID is present in document
                  if (!profile.uid) {
                    profile.uid = firebaseUser.uid;
                    updateDoc(userDocRef, { uid: firebaseUser.uid }).catch(err => console.error("UID sync write failure:", err));
                  }

                  if (profile.firstLogin) {
                    setCurrentView('profile');
                    updateDoc(userDocRef, { firstLogin: false }).catch(err => console.error("First login flag reset error:", err));
                    profile.firstLogin = false;
                  }

                  if (profile.role === 'superadmin' && !profile.profileComplete) {
                    profile = { ...profile, profileComplete: true };
                    updateDoc(userDocRef, { profileComplete: true }).catch(err => console.error("Auto profile completion write failure:", err));
                  }

                  // Auto-default existing organization users to Lead if not assigned to an umbrella parent
                  if (profile.role === 'organization' && profile.isOrgLead === undefined && !profile.parentOrgId) {
                    profile.isOrgLead = true;
                    if (!profile.orgRole) profile.orgRole = 'owner';
                    if (!profile.orgTeamId) profile.orgTeamId = profile.uid;
                  }

                  // --- Self-healing automatic data migration and mapping for demo profiles ---
                  if (profile.role === 'student') {
                    if (profile.email?.toLowerCase() === 'student.sam@example.com' && profile.uid !== 'student-sam') {
                      migrateStudentData('student-sam', profile.uid);
                    } else if (profile.email?.toLowerCase() === 'student.alex@example.com' && profile.uid !== 'student-alex') {
                      migrateStudentData('student-alex', profile.uid);
                    }
                  } else if (profile.role === 'parent') {
                    if (profile.email?.toLowerCase() === 'parent.pam@example.com' && profile.uid !== 'parent-pam') {
                      migrateParentData('parent-pam', profile.uid);
                    } else if (profile.email?.toLowerCase() === 'parent.pat@example.com' && profile.uid !== 'parent-pat') {
                      migrateParentData('parent-pat', profile.uid);
                    }
                  }

                  // --- Self-healing Regional Guard: Students must NEVER remain in 'global' or 'all' ---
                  if (profile.role === 'student') {
                    const candidateSchool = profile.school || profile.schoolId || (profile.additionalSchools && profile.additionalSchools[0]);
                    const isGlobalTenant = !profile.tenantId || profile.tenantId === 'global' || profile.tenantId === 'all';
                    if (isGlobalTenant && candidateSchool) {
                      const resolved = resolveDistrictForSchool(candidateSchool, undefined, allSchools, allDistricts);
                      const derivedRegion = resolved.regionId || deriveRegionIdForSchool({ name: candidateSchool });
                      if (derivedRegion && derivedRegion !== 'global') {
                        profile.tenantId = derivedRegion;
                        profile.region = formatRegionWithNoun(derivedRegion);
                        const autoFixUpdates: any = {
                          tenantId: derivedRegion,
                          region: formatRegionWithNoun(derivedRegion)
                        };
                        if ((!profile.district || profile.district === 'Unassigned') && resolved.districtName) {
                          profile.district = resolved.districtName;
                          profile.districtId = resolved.districtId;
                          autoFixUpdates.district = resolved.districtName;
                          autoFixUpdates.districtId = resolved.districtId;
                        }
                        updateDoc(userDocRef, autoFixUpdates).catch(err => console.error("Auto self-healing student regional binding failed:", err));
                      }
                    }
                  }

                  if (profile.uid) {
                    const sessionKey = `wbl_signin_${profile.uid}`;
                    if (!sessionStorage.getItem(sessionKey)) {
                      sessionStorage.setItem(sessionKey, 'true');
                      writeLog(profile.tenantId || 'global', profile.email, 'USER_SIGN_IN', `${profile.name || 'User'} (${profile.role}) signed into the portal.`);
                      const nowIso = new Date().toISOString();
                      const loginUpdates: any = {
                        lastLogin: nowIso,
                      };
                      if (!profile.createdAt) {
                        loginUpdates.createdAt = firebaseUser.metadata.creationTime ? new Date(firebaseUser.metadata.creationTime).toISOString() : nowIso;
                      }
                      updateDoc(userDocRef, loginUpdates).catch(err => console.error("Last login write failure:", err));
                    }
                  }

                  setActiveUser(profile);
                  if (profile.tenantId && profile.tenantId !== 'global') {
                    const userTenant = tenants.find(t => t.id === profile.tenantId);
                    if (userTenant) setActiveTenant(userTenant);
                  }
                  if (!currentView || currentView === 'landing' || currentView === 'region') {
                    setCurrentView('dashboard');
                  }
                  setIsLoading(false);
                } else {
                  if (localStorage.getItem('signup_in_progress') === 'true') {
                    setIsLoading(false);
                    return;
                  }

                  // Check pre-registered profile by email address
                  try {
                    const emailQuery = query(collection(db, 'users'), where('email', '==', firebaseUser.email));
                    const querySnap = await getDocs(emailQuery);
                    if (!querySnap.empty) {
                      const preDoc = querySnap.docs[0];
                      const preProfile = preDoc.data() as UserProfile;
                      const defInitials = preProfile.name 
                        ? preProfile.name.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2)
                        : 'U';
                      const migratedProfile: UserProfile = {
                        ...preProfile,
                        uid: firebaseUser.uid,
                        avatar: firebaseUser.photoURL || preProfile.avatar || defInitials,
                        photoURL: firebaseUser.photoURL || undefined
                      };
                      await setDoc(userDocRef, cleanFirestoreData(migratedProfile));
                      if (preDoc.id !== firebaseUser.uid) {
                        await deleteDoc(doc(db, 'users', preDoc.id));
                        await migrateStudentData(preDoc.id, firebaseUser.uid);
                      }
                      setActiveUser(migratedProfile);
                      setCurrentView('dashboard');
                      writeLog(migratedProfile.tenantId, firebaseUser.email || '', 'LOGIN_RESOLVE', `Pre-registered profile consolidated with authenticated credential.`);
                      setIsLoading(false);
                      return;
                    }
                  } catch (queryError) {
                    console.warn("Could not query pre-registered profile:", queryError);
                  }
                  
                  const userEmail = firebaseUser.email || '';
                  const isDefaultAdmin = userEmail.toLowerCase() === 'joshua@losonova.com';
                  const pendingRole = localStorage.getItem('pendingRole') as UserRole || null;
                  localStorage.removeItem('pendingRole');
                    
                  const defInitials = (firebaseUser.displayName || 'Authorized Gateway User')
                    .split(' ')
                    .filter(Boolean)
                    .map(n => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2) || 'U';

                  let resolvedRole: UserRole = isDefaultAdmin ? 'superadmin' : (pendingRole || 'student');
                  let resolvedTenantId = isDefaultAdmin ? 'global' : activeTenant.id;
                  let resolvedRegion = isDefaultAdmin ? 'global' : (activeTenant.name || 'Northeast Region');
                  let resolvedProfileComplete = isDefaultAdmin ? true : false;
                  let resolvedOrgName = '';

                  const emailLower = userEmail.toLowerCase();
                  if (emailLower.includes('organization')) {
                    resolvedRole = 'organization';
                    resolvedTenantId = 'apextech';
                    resolvedRegion = 'Northeast Region';
                    resolvedOrgName = 'Apex Tech Solutions';
                    resolvedProfileComplete = true;
                  } else if (emailLower.includes('advisor')) {
                    resolvedRole = 'advisor';
                    resolvedTenantId = 'apextech';
                    resolvedRegion = 'Northeast Region';
                    resolvedProfileComplete = true;
                  } else if (emailLower.includes('student')) {
                    resolvedRole = 'student';
                    resolvedTenantId = 'apextech';
                    resolvedRegion = 'Northeast Region';
                    resolvedProfileComplete = true;
                  }

                  const newProfile: UserProfile = {
                    uid: firebaseUser.uid,
                    name: firebaseUser.displayName || (resolvedRole === 'organization' ? 'Dan Foster' : 'Authorized Gateway User'),
                    email: userEmail,
                    role: resolvedRole,
                    tenantId: resolvedTenantId,
                    region: resolvedRegion,
                    orgName: resolvedOrgName || undefined,
                    avatar: firebaseUser.photoURL || defInitials,
                    photoURL: firebaseUser.photoURL || undefined,
                    firstLogin: true,
                    profileComplete: resolvedProfileComplete
                  };
                  await setDoc(userDocRef, cleanFirestoreData(newProfile));
                  setActiveUser(newProfile);
                  setCurrentView('dashboard');
                  writeLog(newProfile.tenantId, userEmail, 'CREATE_USER_PROFILE', `New student gateway access created.`);
                  
                  // Send welcome email to fallback-created user explaining their role with a login link
                  triggerWelcomeEmail(userEmail, resolvedRole, newProfile.name, resolvedRegion, newProfile.school, undefined, resolvedTenantId, newProfile.district);
                  
                  setIsLoading(false);
                }
              } catch (snapErr) {
                console.error("WBL Portal: Error during snapshot user processing. Resetting state.", snapErr);
                localStorage.clear();
                sessionStorage.clear();
                signOut(auth).catch(() => {});
                setActiveUser(null);
                setCurrentView('dashboard');
                setIsLoading(false);
              }
            }, (snapError) => {
              clearTimeout(loadingTimeout);
              console.error("WBL Portal: User Profile snapshot permission denied or failed. Resetting state.", snapError);
              localStorage.clear();
              sessionStorage.clear();
              signOut(auth).catch(() => {});
              setActiveUser(null);
              setCurrentView('dashboard');
              setIsLoading(false);
            });
          } catch (error) {
            clearTimeout(loadingTimeout);
            console.error("WBL Portal: Failed to initialize live snapshot profile listener.", error);
            localStorage.clear();
            sessionStorage.clear();
            signOut(auth).catch(() => {});
            setActiveUser(null);
            setCurrentView('dashboard');
            setIsLoading(false);
          }
        } else {
          clearTimeout(loadingTimeout);
          const hasStoredSession = typeof window !== 'undefined' && (sessionStorage.getItem('wbl_active_user') || localStorage.getItem('wbl_active_user'));
          if (!hasStoredSession) {
            setActiveUser(null);
          }
          setIsLoading(false);
        }
      } catch (outerErr) {
        clearTimeout(loadingTimeout);
        console.error("WBL Portal: Critical Auth State Changed Error. Forcing recovery.", outerErr);
        localStorage.clear();
        sessionStorage.clear();
        signOut(auth).catch(() => {});
        setActiveUser(null);
        setCurrentView('dashboard');
        setIsLoading(false);
      }
    });

    // Real-time Tenants observer (mapping from regions)
    const unsubTenants = onSnapshot(collection(db, 'regions'), (snap) => {
      const dataMap = new Map<string, Tenant>();
      
      // Pre-fill with our static SC configuration
      INITIAL_TENANTS.forEach(tenant => {
        dataMap.set(tenant.id, tenant);
      });

      // Layer any database states (e.g. customized color schemes or subdomains) on top
      snap.forEach(docSnap => {
        if (docSnap.id === 'global') {
          const globalData = docSnap.data() as GlobalBranding;
          setGlobalBranding(prev => ({
            ...prev,
            ...globalData,
            palette: globalData.palette ? { ...prev.palette, ...globalData.palette } : prev.palette,
            logos: globalData.logos ? { ...prev.logos, ...globalData.logos } : prev.logos,
          }));
        } else {
          const region = docSnap.data() as Tenant;
          const rId = docSnap.id || region.id;
          if (rId && rId !== 'apextech' && rId !== 'valleyhealth' && rId !== 'springfield') {
            const existsRef = dataMap.get(rId) || {};
            dataMap.set(rId, { ...existsRef, ...region, id: rId } as Tenant);
          }
        }
      });

      const mergedData = Array.from(dataMap.values()).filter(t => t.id !== 'apextech' && t.id !== 'valleyhealth' && t.id !== 'springfield');
      setTenants(mergedData);
      setIsDbSeeded(snap.size > 0);
      
      // Auto-detect tenant based on hostname, customDomain, or subdomain (only if not already set to match)
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname.toLowerCase();
        const subdomain = hostname.split('.')[0];
        
        // 1. Check if hostname matches any configured tenant customDomain
        const customDomainMatch = mergedData.find(t => {
          if (!t.customDomain) return false;
          const cleanCd = t.customDomain.replace(/^https?:\/\//i, '').replace(/\/.*$/, '').toLowerCase();
          return cleanCd === hostname || hostname === `www.${cleanCd}` || cleanCd === `www.${hostname}`;
        });

        if (customDomainMatch) {
          setActiveTenant(prev => prev.id === customDomainMatch.id ? prev : customDomainMatch);
        } else if (hostname.includes('thespartanburgedge')) {
          const spartanburgTenant = mergedData.find(t => t.id === 'spartanburg' || t.subdomain === 'spartanburg');
          if (spartanburgTenant) {
            setActiveTenant(prev => prev.id === spartanburgTenant.id ? prev : spartanburgTenant);
          }
        } else if (hostname.includes('wblportal.com')) {
          // If accessing via a county subdomain e.g. greenville.wblportal.com
          const detectedTenant = mergedData.find(t => t.subdomain === subdomain && subdomain !== 'wblportal' && subdomain !== 'www');
          if (detectedTenant) {
            setActiveTenant(prev => prev.id === detectedTenant.id ? prev : detectedTenant);
          }
        }
      }
    }, (error) => {
      console.warn("Regions (tenants) snapshots permission restriction:", error.message);
    });


    let unsubSchools = () => {};
    const unsubDistricts = onSnapshot(collection(db, 'districts'), (distSnap) => {
      const distMap = new Map<string, string>();
      const distList: District[] = [];
      distSnap.forEach(d => {
        const data = d.data();
        distMap.set(d.id, data.regionId || data.tenantId);
        distList.push({ id: d.id, ...data } as any as District);
      });
      setAllDistricts(distList);
      
      // Unsubscribe from previous schools listener if it exists
      unsubSchools();
      
      unsubSchools = onSnapshot(collection(db, 'schools'), (snap) => {
        const sList: School[] = [];
        snap.forEach(docSnap => {
          const data = docSnap.data();
          const regionId = data.regionId || data.tenantId || (data.districtId ? distMap.get(data.districtId) : undefined);
          sList.push({ id: docSnap.id, ...data, regionId } as any as School);
        });
        setAllSchools(sList);
      }, (error) => {
        console.warn("Schools snapshot failure:", error.message);
      });
    }, (error) => {
      console.warn("Districts snapshot failure:", error.message);
    });


    return () => {
      clearTimeout(loadingTimeout);
      unsubscribeAuth();
      if (profileUnsubscribe) profileUnsubscribe();
      unsubTenants();
      unsubSchools();
      unsubDistricts();
    };
  }, [isFirebaseActive]);

  // Synchronize Active Tenant Context on Tenant lists loading (only on login / user change)
  useEffect(() => {
    if (!activeUser) {
      lastUserUidRef.current = null;
      return;
    }

    const userRegs = getUserRegions(activeUser, tenants, allSchools);
    const hasMulti = activeUser.role === 'superadmin' || userRegs.length > 1;

    if (!hasMulti) {
      // Single-region user: MUST ALWAYS be in their specific region tenant, NEVER in global
      const resolved = resolveUserTenant(activeUser, tenants, allSchools);
      if (resolved && activeTenant.id !== resolved.id) {
        setActiveTenant(resolved);
      }
      // If user profile has 'global' or empty tenantId, sanitize profile locally
      if (resolved && (activeUser.tenantId === 'global' || !activeUser.tenantId || activeUser.region === 'Global')) {
        setActiveUser(prev => prev ? {
          ...prev,
          tenantId: resolved.id,
          region: resolved.name
        } : null);
      }
    } else {
      // Multi-region user (superadmin or users with > 1 region):
      if (activeUser.uid !== lastUserUidRef.current) {
        lastUserUidRef.current = activeUser.uid;
        
        // Default to global for multi-region users
        const globalTenant = tenants.find(t => t.id === 'global') || GLOBAL_TENANT;
        setActiveTenant(globalTenant);
      }
    }
  }, [tenants, allSchools, activeUser, activeTenant.id]);

  // Secure Role-Based Collection Snapshots
  useEffect(() => {
    if (isUsingMockKeys) {
      if (activeUser) {
        setUsers(MOCK_USERS);
        setOpportunities(MOCK_OPPORTUNITIES);
        setApplications(MOCK_APPLICATIONS);
        setPlacements(MOCK_PLACEMENTS);
        setTasks(MOCK_TASKS);
        setAttendance(MOCK_ATTENDANCE);
        setLogs(MOCK_LOGS);
        setSchools(STATIC_MOCK_SCHOOLS);
        setAllSchools(STATIC_MOCK_SCHOOLS);
        setDistricts(STATIC_MOCK_DISTRICTS);
        setAllDistricts(STATIC_MOCK_DISTRICTS);
        setInterviews([]);
        setMessages([
          {
            id: 'msg-mock-1',
            tenantId: activeUser.tenantId || 'global',
            toEmail: activeUser.email,
            subject: 'Welcome to your Work-Based Learning Portal!',
            title: 'Welcome to the Work-Based Learning Portal',
            bodyHtml: `<p>Dear ${activeUser.name},</p><p>We are thrilled to welcome you to the Work-Based Learning Portal. This platform connects you with career opportunities, work placements, training agreements, and weekly attendance logs in your Region.</p><p>Please verify that your profile information is complete and explore available opportunities.</p><p>Best regards,<br/>The Regional Work-Based Learning Administration Team</p>`,
            sentAt: new Date(Date.now() - 2 * 3600000).toISOString(),
            read: false,
            fromName: 'WBL Portal'
          },
          {
            id: 'msg-mock-2',
            tenantId: activeUser.tenantId || 'global',
            toEmail: activeUser.email,
            subject: 'Important Guidelines for Workplace Attendance Logging',
            title: 'Workplace Attendance Tracking',
            bodyHtml: `<p>Dear ${activeUser.name},</p><p>This is an administrative reminder that all placement attendance reports must be submitted weekly. Your assigned coordinators and mentors will review these reports to verify your course credit allocations.</p><p>Please log your hours promptly by the end of each work week.</p>`,
            sentAt: new Date(Date.now() - 24 * 3600000).toISOString(),
            read: true,
            fromName: 'Portal Admin'
          }
        ]);
      } else {
        setUsers([]);
        setOpportunities(MOCK_OPPORTUNITIES);
        setApplications([]);
        setPlacements([]);
        setTasks([]);
        setAttendance([]);
        setLogs([]);
        setSchools([]);
        setAllSchools([]);
        setDistricts([]);
        setAllDistricts([]);
        setInterviews([]);
        setMessages([]);
        setRecommendations([]);
      }
      return;
    }

    if (!activeUser) {
      // Clear user-specific states on logout, but keep opportunities active for landing page / interactive map
      setApplications([]);
      setPlacements([]);
      setTasks([]);
      setAttendance([]);
      setLogs([]);
      setInterviews([]);
      setMessages([]);
      setRecommendations([]);

      if (!isUsingMockKeys) {
        const oppsQuery = collection(db, 'opportunities');
        const unsubOpps = onSnapshot(oppsQuery, (snap) => {
          const list: Opportunity[] = [];
          const mockIds = new Set(MOCK_OPPORTUNITIES.map(o => o.id));
          snap.forEach(dDoc => {
            const id = dDoc.id;
            if (mockIds.has(id)) {
              // Delete auto-seeded mock doc from Firestore so only real user opportunities remain
              deleteDoc(doc(db, 'opportunities', id)).catch(e => console.warn("Mock opp cleanup notice:", e));
            } else {
              list.push({ id, ...dDoc.data() } as Opportunity);
            }
          });
          setOpportunities(list);
        }, (err) => {
          console.warn("Unauthenticated opportunities snapshot error:", err.message);
          setOpportunities([]);
        });
        return () => unsubOpps();
      } else {
        setOpportunities(MOCK_OPPORTUNITIES);
      }
      return;
    }

    const uid = activeUser.uid;
    const role = activeUser.role;
    const tenantId = ['superadmin', 'regional_admin', 'organization'].includes(role) ? activeTenant.id : activeUser.tenantId;
    const childId = activeUser.childId;

    console.log(`WBL Gateway: Building secure snapshot pipelines for Role: ${role} | Tenant anchor: ${tenantId}`);

    const unsubscribes: (() => void)[] = [];

    // 1. Live Users directory pipeline
    let usersQuery;
    if (role === 'superadmin') {
      usersQuery = collection(db, 'users');
    } else if (role === 'regional_admin' || role === 'advisor' || role === 'organization' || role === 'mentor' || role === 'student') {
      usersQuery = query(collection(db, 'users'), where('tenantId', '==', tenantId));
    } else if (role === 'parent') {
      // Ensure parents can see themselves + student profile at minimum via their shared tenant context
      if (childId) {
        usersQuery = query(collection(db, 'users'), where('tenantId', '==', tenantId));
      } else {
        usersQuery = query(collection(db, 'users'), where('uid', '==', uid));
      }
    } else {
      usersQuery = query(collection(db, 'users'), where('uid', '==', uid));
    }

    const unsubUsers = onSnapshot(usersQuery, (snap) => {
      const list: UserProfile[] = [];
      snap.forEach(dDoc => {
        const u = { uid: dDoc.id, ...dDoc.data() } as UserProfile;
        const uid = u.uid || '';
        const email = (u.email || '').toLowerCase();
        
        const isMockUid = uid.includes('mock') || 
                          uid.startsWith('super-') || 
                          uid.startsWith('regional-') || 
                          uid.startsWith('org-') || 
                          uid.startsWith('student-') || 
                          uid.startsWith('parent-');
                          
        const isKnownFakeEmail = [
          'advisor@wblportal.edu',
          'advisor3@wblportal.edu',
          'student@wblportal.edu',
          'benjamindavidloso@gmail.com',
          'braylonblanton08@yahoo.com',
          'cosullivan@rdanderson.org',
          'sarah.reg@example.com',
          'bill.reg@example.com',
          'dan.corp@example.com',
          'doctor.green@example.com',
          'student.sam@example.com',
          'student.alex@example.com'
        ].includes(email);

        if (uid) {
          if (u.role === 'organization') {
            if (u.isOrgLead === undefined && !u.parentOrgId) {
              u.isOrgLead = true;
              if (!u.orgRole) u.orgRole = 'owner';
              if (!u.orgTeamId) u.orgTeamId = u.uid;
            }
          }
          list.push(u);
        }
      });
      setUsers(list);
    }, (error) => {
      console.warn("Users snapshot error:", error.message);
    });
    unsubscribes.push(unsubUsers);

    // 2. Live Opportunities pipeline (Fetch all opportunities posted by any organization)
    const oppsQuery = collection(db, 'opportunities');

    const unsubOpps = onSnapshot(oppsQuery, (snap) => {
      const list: Opportunity[] = [];
      const mockIds = new Set(MOCK_OPPORTUNITIES.map(o => o.id));
      snap.forEach(dDoc => {
        const id = dDoc.id;
        if (mockIds.has(id)) {
          // Delete auto-seeded mock doc from Firestore so only real user opportunities remain
          deleteDoc(doc(db, 'opportunities', id)).catch(e => console.warn("Mock opp cleanup notice:", e));
        } else {
          list.push({ id, ...dDoc.data() } as Opportunity);
        }
      });
      setOpportunities(list);
    }, (error) => {
      console.warn("Opportunities snapshot error:", error.message);
      setOpportunities([]);
    });
    unsubscribes.push(unsubOpps);

    // 3. Live Applications pipeline
    let appsQuery;
    if (role === 'superadmin') {
      appsQuery = collection(db, 'applications');
    } else if (role === 'student') {
      appsQuery = query(collection(db, 'applications'), where('studentId', '==', uid));
    } else if (role === 'parent') {
      if (childId) {
        appsQuery = query(collection(db, 'applications'), where('studentId', '==', childId));
      } else {
        // Parent without link should see nothing
        appsQuery = query(collection(db, 'applications'), where('studentId', '==', 'NONE'));
      }
    } else {
      if (tenantId === 'global') {
        appsQuery = collection(db, 'applications');
      } else {
        const tenantIds = [tenantId];
        if (activeUser?.extendedRegions) {
          tenantIds.push(...activeUser.extendedRegions);
        }
        // Use 'in' operator to fetch from all authorized regions
        appsQuery = query(collection(db, 'applications'), where('tenantId', 'in', tenantIds));
      }
    }

    const unsubApps = onSnapshot(appsQuery, (snap) => {
      const list: Application[] = [];
      snap.forEach(dDoc => list.push({ id: dDoc.id, ...dDoc.data() } as Application));
      setApplications(list);
    }, (error) => {
      console.warn("Applications snapshot error:", error.message);
    });
    unsubscribes.push(unsubApps);

    // Live Interviews pipeline
    let interviewsQuery;
    if (role === 'superadmin') {
      interviewsQuery = collection(db, 'interviews');
    } else if (role === 'student') {
      interviewsQuery = query(collection(db, 'interviews'), where('studentId', '==', uid));
    } else if (role === 'parent') {
      if (childId) {
        interviewsQuery = query(collection(db, 'interviews'), where('studentId', '==', childId));
      } else {
        interviewsQuery = query(collection(db, 'interviews'), where('studentId', '==', 'NONE'));
      }
    } else {
      if (tenantId === 'global') {
        interviewsQuery = collection(db, 'interviews');
      } else {
        const tenantIds = [tenantId];
        if (activeUser?.extendedRegions) {
          tenantIds.push(...activeUser.extendedRegions);
        }
        interviewsQuery = query(collection(db, 'interviews'), where('tenantId', 'in', tenantIds));
      }
    }

    const unsubInterviews = onSnapshot(interviewsQuery, (snap) => {
      const list: Interview[] = [];
      snap.forEach(dDoc => list.push({ id: dDoc.id, ...dDoc.data() } as Interview));
      setInterviews(list);
    }, (error) => {
      console.warn("Interviews snapshot error:", error.message);
    });
    unsubscribes.push(unsubInterviews);

    // 4. Live Placements pipeline
    let placeQuery;
    if (role === 'superadmin') {
      placeQuery = collection(db, 'placements');
    } else if (role === 'student') {
      placeQuery = query(collection(db, 'placements'), where('studentId', '==', uid));
    } else if (role === 'parent') {
      if (childId) {
        placeQuery = query(collection(db, 'placements'), where('studentId', '==', childId));
      } else {
        placeQuery = query(collection(db, 'placements'), where('studentId', '==', 'NONE'));
      }
    } else {
      if (tenantId === 'global') {
        placeQuery = collection(db, 'placements');
      } else {
        const tenantIds = [tenantId];
        if (activeUser?.extendedRegions) {
          tenantIds.push(...activeUser.extendedRegions);
        }
        placeQuery = query(collection(db, 'placements'), where('tenantId', 'in', tenantIds));
      }
    }

    const unsubPlace = onSnapshot(placeQuery, (snap) => {
      const list: Placement[] = [];
      snap.forEach(dDoc => list.push({ id: dDoc.id, ...dDoc.data() } as Placement));
      setPlacements(list);
    }, (error) => {
      console.warn("Placements snapshot error:", error.message);
    });
    unsubscribes.push(unsubPlace);

    // 5. Live Tasks pipeline
    let tasksQuery;
    if (role === 'superadmin') {
      tasksQuery = collection(db, 'tasks');
    } else if (role === 'student') {
      tasksQuery = query(collection(db, 'tasks'), where('studentId', '==', uid));
    } else if (role === 'parent') {
      if (childId) {
        tasksQuery = query(collection(db, 'tasks'), where('studentId', '==', childId));
      } else {
        tasksQuery = query(collection(db, 'tasks'), where('studentId', '==', 'NONE'));
      }
    } else {
      if (tenantId === 'global') {
        tasksQuery = collection(db, 'tasks');
      } else {
        const tenantIds = [tenantId];
        if (activeUser?.extendedRegions) {
          tenantIds.push(...activeUser.extendedRegions);
        }
        tasksQuery = query(collection(db, 'tasks'), where('tenantId', 'in', tenantIds));
      }
    }

    const unsubTasks = onSnapshot(tasksQuery, (snap) => {
      const list: StudentTask[] = [];
      snap.forEach(dDoc => list.push({ id: dDoc.id, ...dDoc.data() } as StudentTask));
      setTasks(list);
    }, (error) => {
      console.warn("Tasks snapshot error:", error.message);
    });
    unsubscribes.push(unsubTasks);

    // Live Recommendations pipeline
    let recsQuery;
    if (role === 'superadmin') {
      recsQuery = collection(db, 'recommendations');
    } else if (role === 'student') {
      recsQuery = query(collection(db, 'recommendations'), where('studentId', '==', uid));
    } else if (role === 'parent') {
      if (childId) {
        recsQuery = query(collection(db, 'recommendations'), where('studentId', '==', childId));
      } else {
        recsQuery = query(collection(db, 'recommendations'), where('studentId', '==', 'NONE'));
      }
    } else if (role === 'advisor') {
      recsQuery = query(collection(db, 'recommendations'), where('advisorId', '==', uid));
    } else {
      recsQuery = query(collection(db, 'recommendations'), where('tenantId', '==', tenantId));
    }

    const unsubRecs = onSnapshot(recsQuery, (snap) => {
      const list: Recommendation[] = [];
      snap.forEach(dDoc => list.push({ id: dDoc.id, ...dDoc.data() } as Recommendation));
      setRecommendations(list);
    }, (error) => {
      console.warn("Recommendations snapshot error:", error.message);
    });
    unsubscribes.push(unsubRecs);

    // 6. Live Attendance logs pipeline
    let attQuery;
    if (role === 'superadmin') {
      attQuery = collection(db, 'attendance');
    } else if (role === 'student') {
      attQuery = query(collection(db, 'attendance'), where('studentId', '==', uid));
    } else if (role === 'parent' && childId) {
      attQuery = query(collection(db, 'attendance'), where('studentId', '==', childId));
    } else {
      if (tenantId === 'global') {
        attQuery = collection(db, 'attendance');
      } else {
        const tenantIds = [tenantId];
        if (activeUser?.extendedRegions) {
          tenantIds.push(...activeUser.extendedRegions);
        }
        attQuery = query(collection(db, 'attendance'), where('tenantId', 'in', tenantIds));
      }
    }

    const unsubAtt = onSnapshot(attQuery, (snap) => {
      const list: AttendanceReport[] = [];
      snap.forEach(dDoc => list.push({ id: dDoc.id, ...dDoc.data() } as AttendanceReport));
      setAttendance(list);
    }, (error) => {
      console.warn("Attendance snapshot error:", error.message);
    });
    unsubscribes.push(unsubAtt);

    // 8. Live Messages observation pipeline
    let messagesQuery;
    if (role === 'superadmin') {
      messagesQuery = collection(db, 'messages');
    } else {
      messagesQuery = query(collection(db, 'messages'), where('toEmail', '==', activeUser.email.toLowerCase()));
    }

    const unsubMessages = onSnapshot(messagesQuery, (snap) => {
      const list: PortalMessage[] = [];
      snap.forEach(dDoc => {
        list.push({ id: dDoc.id, ...dDoc.data() } as PortalMessage);
      });
      list.sort((a, b) => b.sentAt.localeCompare(a.sentAt));
      setMessages(list);
    }, (error) => {
      console.warn("Messages snapshot error:", error.message);
    });
    unsubscribes.push(unsubMessages);

    // 9. Live User Links pipeline (For Parents/Students)
    let linksQuery;
    if (role === 'superadmin') {
      linksQuery = collection(db, 'userLinks');
    } else if (role === 'parent') {
      linksQuery = query(collection(db, 'userLinks'), where('parentId', '==', uid));
    } else if (role === 'student') {
      linksQuery = query(collection(db, 'userLinks'), where('studentId', '==', uid));
    } else {
      linksQuery = query(collection(db, 'userLinks'), where('tenantId', '==', tenantId));
    }

    const unsubLinks = onSnapshot(linksQuery, (snap) => {
      const list: any[] = [];
      snap.forEach(dDoc => list.push(dDoc.data()));
      setUserLinks(list);
    }, (error) => {
      console.warn("UserLinks snapshot error:", error.message);
    });
    unsubscribes.push(unsubLinks);

    // 7. Live System Logs (Superadmin Audit trails)
    if (role === 'superadmin') {
      const unsubLogs = onSnapshot(collection(db, 'logs'), (snap) => {
        const list: SystemLog[] = [];
        snap.forEach(dDoc => list.push({ id: dDoc.id, ...dDoc.data() } as SystemLog));
        setLogs(list.sort((a, b) => b.timestamp.localeCompare(a.timestamp)));
      }, (error) => {
        console.warn("System logs snapshot error:", error.message);
      });
      unsubscribes.push(unsubLogs);
    }

    return () => {
      console.log("WBL Gateway: Unsubscribing active snap pipelines.");
      unsubscribes.forEach(unsub => unsub());
    };
  }, [activeUser, isUsingMockKeys, activeTenant.id]);

  const hasCheckedRemindersRef = useRef(false);

  useEffect(() => {
    if (placements.length > 0 && !hasCheckedRemindersRef.current) {
      hasCheckedRemindersRef.current = true;
      checkAndSendEvaluationReminders(placements);
    }
  }, [placements]);

  useEffect(() => {
    if (!activeUser) {
      hasCheckedRemindersRef.current = false;
    }
  }, [activeUser]);

  // Automatic auto-seeding removed to prevent accidental overwrite of live user data.

  // Manual trace audit logger
  const writeLog = (tenantId: string, email: string, action: string, details: string) => {
    const newLog: SystemLog = {
      id: `log-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      tenantId,
      userEmail: email,
      action,
      details,
      timestamp: new Date().toISOString()
    };

    if (!isUsingMockKeys) {
      setDoc(doc(db, 'logs', newLog.id), newLog).catch(e => {
        console.warn("Audit trace write restriction:", e.message);
      });
    } else {
      setLogs(prev => [newLog, ...prev]);
    }
  };

  // Switch Subdomain active viewport workspace
  const changeTenant = (subdomainOrId: string) => {
    if (subdomainOrId === 'global') {
      if (activeUser && activeUser.role !== 'superadmin' && activeUser.role !== 'regional_admin' && activeUser.role !== 'organization') {
        const userRegs = getUserRegions(activeUser, tenants, allSchools);
        if (userRegs.length <= 1) {
          // Single-region user should not be switched to global
          const resolved = resolveUserTenant(activeUser, tenants, allSchools);
          if (resolved) {
            setActiveTenant(resolved);
            return;
          }
        }
      }
      setActiveTenant(GLOBAL_TENANT);
      writeLog('global', activeUser?.email || 'Guest Visitor', 'SWITCH_TENANT', `Re-routed viewport context to Global branding.`);
      return;
    }
    let found = tenants.find(t => t.id === subdomainOrId || t.subdomain === subdomainOrId);
    if (!found && subdomainOrId) {
      const specialNames: Record<string, string> = {
        'mccormick': 'McCormick'
      };
      const formattedName = specialNames[subdomainOrId.toLowerCase()] || subdomainOrId
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      found = {
        id: subdomainOrId.toLowerCase(),
        name: formattedName,
        subdomain: subdomainOrId.toLowerCase(),
        logo: 'Globe',
        primaryColor: '#0094c8',
        secondaryColor: '#4f46e5',
        welcomeMessage: `Welcome to ${formattedName} WBL Portal.`
      };
      setTenants(prev => {
         if (!prev.some(t => t.id === subdomainOrId.toLowerCase())) {
           return [...prev, found as Tenant];
         }
         return prev;
      });
    }

    if (found) {
      setActiveTenant(found);
      writeLog(found.id, activeUser?.email || 'Guest Visitor', 'SWITCH_TENANT', `Re-routed viewport context to ${found.name} branding.`);
    }
  };

  // Live Database Seed Function
  const runSchoolIdMigration = async () => {
    if (!isFirebaseActive || activeUser?.role !== 'superadmin') {
      console.warn("WBL Migration: Unauthorized or Firebase inactive.");
      return;
    }

    console.log("WBL Migration: Starting school ID retroactive sync...");
    const usersSnap = await getDocs(collection(db, 'users'));
    const schoolsSnap = await getDocs(collection(db, 'schools'));
    const districtsSnap = await getDocs(collection(db, 'districts'));

    const allSchools = schoolsSnap.docs.map(d => ({ id: d.id, ...d.data() } as School));
    const allDistricts = districtsSnap.docs.map(d => ({ id: d.id, ...d.data() } as District));

    const batch = writeBatch(db);
    let count = 0;

    usersSnap.forEach(userDoc => {
      const userData = userDoc.data() as UserProfile;
      const updates: any = {};

      // 1. Migrate single school
      if (userData.school && !userData.schoolId) {
        const found = allSchools.find(s => 
          s.name.toLowerCase().trim() === userData.school?.toLowerCase().trim() ||
          s.id.toLowerCase().trim() === userData.school?.toLowerCase().trim()
        );
        if (found) {
          updates.schoolId = found.id;
          updates.school = found.name; // Standardize name
          if (found.districtId) updates.districtId = found.districtId;
        }
      }

      // 2. Migrate district
      if (userData.district && !userData.districtId) {
        const found = allDistricts.find(d => 
          d.name.toLowerCase().trim() === userData.district?.toLowerCase().trim() ||
          d.id.toLowerCase().trim() === userData.district?.toLowerCase().trim()
        );
        if (found) {
          updates.districtId = found.id;
          updates.district = found.name;
        }
      }

      // 3. Migrate schoolsServed
      if (userData.schoolsServed && userData.schoolsServed.length > 0 && !userData.schoolsServedIds) {
        const ids: string[] = [];
        userData.schoolsServed.forEach(sName => {
          const found = allSchools.find(s => 
            s.name.toLowerCase().trim() === sName.toLowerCase().trim() ||
            s.id.toLowerCase().trim() === sName.toLowerCase().trim()
          );
          if (found) ids.push(found.id);
        });
        if (ids.length > 0) {
          updates.schoolsServedIds = ids;
        }
      }

      if (Object.keys(updates).length > 0) {
        batch.update(userDoc.ref, updates);
        count++;
      }
    });

    if (count > 0) {
      await batch.commit();
      console.log(`WBL Migration: Successfully updated ${count} profiles with unique school IDs.`);
    } else {
      console.log("WBL Migration: No profiles required updates.");
    }
  };

  // Google SSO authenticator popup trigger
  const signInWithGoogle = async () => {
    if (isUsingMockKeys) {
      // Offline mock fallback if credentials omitted
      const mockAdmin = MOCK_USERS[0];
      setActiveUser(mockAdmin);
      return;
    }

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error: any) {
      console.error("Federated SSO popup triggered failure:", error);
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/cancelled-popup-request' || error.code === 'auth/popup-closed-by-user') {
          console.log("Retrying with redirect...");
          await signInWithRedirect(auth, new GoogleAuthProvider());
      } else {
          inAppAlert({
            title: "SSO Authentication Failed",
            message: `SSO authentication failed: ${error.message} (${error.code || 'unknown-error'}). Please try again.`,
            type: "error"
          });
      }
    }
  };

  const signUpWithEmail = async (email: string, password: string, role: UserRole) => {
    localStorage.setItem('pendingRole', role);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        try {
          await signInWithEmailAndPassword(auth, email, password);
          return;
        } catch (signInError) {
          throw new Error('This email is already in use. Please sign in or use a different email.');
        }
      }
      throw error;
    }
  };

  const signUpWithRoleAndData = async (email: string, password: string, role: UserRole, data: any) => {
    const emailLower = (email || "").toLowerCase().trim();
    const prohibited = systemConfig.prohibitedDomains || ["@spart1.net"];
    for (const domain of prohibited) {
      const cleanDom = domain.toLowerCase().trim();
      if (emailLower.endsWith(cleanDom) || emailLower.includes(cleanDom)) {
        throw new Error(`Registration rejected: Email domain ${cleanDom} is prohibited by system policy.`);
      }
    }
    localStorage.setItem('signup_in_progress', 'true');
    if (role !== 'superadmin' && activeTenant.id === 'global') {
      localStorage.removeItem('signup_in_progress');
      throw new Error('Please select a specific region/county for your account. Registration cannot be completed for the Global region.');
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;
      const nameToUse = ((data.contactFirstName || '') + ' ' + (data.contactLastName || '')).trim() || 'User';
      const initials = nameToUse.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
      
      let regionCode = '';
      let studentCode = '';

      if (role === 'student') {
        regionCode = (activeTenant.id.slice(0, 3).toUpperCase() || 'WBL');
        studentCode = generateRandomCode(6);
      }

      if (role === 'parent' && data.childId) {
        data.childrenIds = [data.childId];
      }

      // Automatic Reverse Mapping: Derive District from School
      const targetSchool = data.school || data.pendingSchool || data.signupSchool;
      const targetSchoolId = data.schoolId || data.pendingSchoolId || data.signupSchoolId;

      let resolvedDistrictName = data.district;
      let resolvedDistrictId = data.districtId;
      let resolvedSchoolName = data.school;
      let resolvedSchoolId = data.schoolId;

      let resolvedTenantId = data.tenantId || (activeTenant.id !== 'global' ? activeTenant.id : undefined);
      let resolvedRegionName = data.region || (activeTenant.id !== 'global' ? activeTenant.name : undefined);

      if (targetSchool || targetSchoolId || ['student', 'advisor', 'parent'].includes(role)) {
        const resolved = resolveDistrictForSchool(
          targetSchoolId || targetSchool,
          resolvedTenantId,
          allSchools,
          allDistricts
        );
        if (resolved.districtName) resolvedDistrictName = resolved.districtName;
        if (resolved.districtId) resolvedDistrictId = resolved.districtId;
        if (resolved.schoolName && !resolvedSchoolName) resolvedSchoolName = resolved.schoolName;
        if (resolved.schoolId && !resolvedSchoolId) resolvedSchoolId = resolved.schoolId;
        if (resolved.regionId && (!resolvedTenantId || resolvedTenantId === 'global')) {
          resolvedTenantId = resolved.regionId;
          resolvedRegionName = formatRegionWithNoun(resolved.regionId);
        }
      }

      if (role === 'student' && (!resolvedTenantId || resolvedTenantId === 'global')) {
        const derived = deriveRegionIdForSchool({ name: resolvedSchoolName || targetSchool || '' });
        resolvedTenantId = (derived && derived !== 'global') ? derived : 'spartanburg';
        resolvedRegionName = formatRegionWithNoun(resolvedTenantId);
      }

      // Check if registering user is joining an existing organization team via invite or authorized email
      const normRegEmail = (email || '').toLowerCase().trim();
      const explicitOrgId = data.orgId || data.parentOrgId || data.orgTeamId;
      let matchedUmbrellaOrg = (role === 'organization')
        ? (explicitOrgId
            ? users.find(u => u.uid === explicitOrgId && u.role === 'organization')
            : (normRegEmail
                ? users.find(u =>
                    u.role === 'organization' &&
                    !u.parentOrgId &&
                    ((u.orgAuthorizedEmails || []).some(e => e.toLowerCase().trim() === normRegEmail) ||
                      (u.orgTeamMembers || []).some(m => (m.email || '').toLowerCase().trim() === normRegEmail && m.orgRole !== 'contact_only'))
                  )
                : undefined))
        : undefined;

      // Secondary fallback: match existing parent org by corporate name
      if (!matchedUmbrellaOrg && role === 'organization' && data.orgName) {
        matchedUmbrellaOrg = users.find(u =>
          u.role === 'organization' &&
          !u.parentOrgId &&
          u.orgName &&
          u.orgName.toLowerCase().trim() === (data.orgName || '').toLowerCase().trim()
        );
      }

      // Direct Firestore fallback if not found in current memory state
      if (!matchedUmbrellaOrg && explicitOrgId && !isUsingMockKeys) {
        try {
          const directSnap = await getDoc(doc(db, 'users', explicitOrgId));
          if (directSnap.exists()) {
            matchedUmbrellaOrg = { uid: directSnap.id, ...directSnap.data() } as UserProfile;
          }
        } catch (e) {
          console.warn("Direct lookup of umbrella org failed:", e);
        }
      }

      const matchedMemberEntry = matchedUmbrellaOrg
        ? (matchedUmbrellaOrg.orgTeamMembers || []).find(m => (m.email || '').toLowerCase().trim() === normRegEmail)
        : undefined;

      const newProfile: UserProfile = {
          uid,
          email,
          role,
          name: matchedMemberEntry?.name || nameToUse,
          tenantId: resolvedTenantId || data.tenantId || matchedUmbrellaOrg?.tenantId || (activeTenant.id !== 'global' ? activeTenant.id : 'spartanburg'),
          region: resolvedRegionName || data.region || matchedUmbrellaOrg?.region || (activeTenant.id !== 'global' ? activeTenant.name : 'Spartanburg County'),
          avatar: matchedUmbrellaOrg?.avatar || data.avatar || initials, // Always ensure avatar is present
          profileComplete: true,
          firstLogin: true,
          regionCode,
          studentCode,
          ...data,
          ...(role === 'organization' ? (
            matchedUmbrellaOrg ? {
              parentOrgId: matchedUmbrellaOrg.uid,
              orgTeamId: matchedUmbrellaOrg.uid,
              isOrgLead: false,
              orgRole: data.orgRole || matchedMemberEntry?.orgRole || 'admin',
              orgName: matchedUmbrellaOrg.orgName || data.orgName,
              industry: matchedUmbrellaOrg.industry || data.industry,
              address: matchedUmbrellaOrg.address || data.address,
              website: matchedUmbrellaOrg.website || data.website,
              companyProfileText: matchedUmbrellaOrg.companyProfileText || data.companyProfileText,
              onboardingText: matchedUmbrellaOrg.onboardingText || data.onboardingText,
              targetingMap: matchedUmbrellaOrg.targetingMap || data.targetingMap,
              extendedRegions: matchedUmbrellaOrg.extendedRegions || data.extendedRegions || [],
              orgTeamMembers: matchedUmbrellaOrg.orgTeamMembers || [],
              orgAuthorizedEmails: matchedUmbrellaOrg.orgAuthorizedEmails || [],
              professionalTitle: matchedMemberEntry?.title || data.professionalTitle,
              contactPhone: matchedMemberEntry?.phone || data.contactPhone
            } : {
              isOrgLead: true,
              orgRole: 'owner',
              orgTeamId: uid,
              orgTeamMembers: []
            }
          ) : {}),
          district: resolvedDistrictName || data.district,
          districtId: resolvedDistrictId || data.districtId,
          pendingSchool: role === 'advisor' ? (resolvedSchoolName || data.pendingSchool || data.school) : data.pendingSchool,
          pendingSchoolId: role === 'advisor' ? (resolvedSchoolId || data.pendingSchoolId || data.schoolId) : data.pendingSchoolId,
          school: resolvedSchoolName || data.school || (role === 'advisor' ? data.pendingSchool : undefined),
          schoolId: resolvedSchoolId || data.schoolId || (role === 'advisor' ? data.pendingSchoolId : undefined),
          signupSchool: data.signupSchool || resolvedSchoolName,
          signupSchoolId: data.signupSchoolId || resolvedSchoolId,
          ferpaConsent: data.ferpaConsent || ((role === 'student' || role === 'parent') ? {
            granted: false,
            date: '',
            version: '1.0'
          } : undefined)
      };
      const cleanedProfile = cleanFirestoreData(newProfile);
      await setDoc(doc(db, 'users', uid), cleanedProfile);
      setActiveUser(cleanedProfile);

      if (matchedUmbrellaOrg) {
        const prevMembers = matchedUmbrellaOrg.orgTeamMembers || [];
        const existingIdx = prevMembers.findIndex(m => m.uid === uid || (m.email && m.email.toLowerCase().trim() === normRegEmail));
        const newTeamEntry: OrganizationTeamMember = {
          id: existingIdx >= 0 ? prevMembers[existingIdx].id : `org-contact-${uid}`,
          uid,
          name: matchedMemberEntry?.name || nameToUse,
          title: matchedMemberEntry?.title || data.professionalTitle || 'Team Member',
          email,
          phone: matchedMemberEntry?.phone || data.contactPhone || '',
          orgRole: (data.orgRole || matchedMemberEntry?.orgRole || 'admin') as any,
          isLead: false,
          addedAt: matchedMemberEntry?.addedAt || new Date().toISOString()
        };
        let updatedMembers: OrganizationTeamMember[];
        if (existingIdx >= 0) {
          updatedMembers = prevMembers.map((m, i) => i === existingIdx ? { ...m, ...newTeamEntry } : m);
        } else {
          updatedMembers = [...prevMembers, newTeamEntry];
        }
        await setDoc(doc(db, 'users', matchedUmbrellaOrg.uid), { orgTeamMembers: cleanFirestoreData(updatedMembers) }, { merge: true }).catch(() => {});
      }

      // Handle formal reciprocal linking for parents (True Link)
      if (role === 'parent' && data.childId) {
        try {
          // 1. Update Student Profile with parent link AND FERPA consent if granted by parent
          const studentUpdates: any = {
            parentId: uid,
            parentEmail: email,
            parentPhone: data.parentPhone || ''
          };
          if (data.ferpaConsent && (data.ferpaConsent.granted || data.ferpaConsent === true)) {
            studentUpdates.ferpaConsent = data.ferpaConsent;
            studentUpdates.ferpaConsentName = data.ferpaConsentName || nameToUse;
            studentUpdates.ferpaConsentDate = data.ferpaConsentDate || new Date().toISOString();
            studentUpdates.ferpaConsentRecipientEmail = email;
          }
          await setDoc(doc(db, 'users', data.childId), studentUpdates, { merge: true });

          // 2. Also inform server to ensure backend persistence
          try {
            fetch('/api/link-student-to-parent', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                parentUid: uid,
                parentEmail: email,
                studentUid: data.childId,
                studentName: data.studentChildName || 'Student'
              })
            }).catch(() => {});
          } catch (e) {}

          // 3. Create a formal Relationship Record (for audit and true link verification)
          const linkId = `link-${uid}-${data.childId}`;
          await setDoc(doc(db, 'userLinks', linkId), {
            id: linkId,
            parentId: uid,
            studentId: data.childId,
            parentName: nameToUse,
            studentName: data.studentChildName || 'Student',
            status: 'verified',
            type: 'guardian',
            createdAt: new Date().toISOString(),
            tenantId: activeTenant.id
          });

          console.log(`WBL Link Engine: Established true link between Parent ${uid} and Student ${data.childId}`);
        } catch (linkError) {
          console.error('Reciprocal linking error:', linkError);
        }
      }

      writeLog(activeTenant.id, email, 'CREATE_USER_PROFILE', `New user registered with role ${role}`);

      // Send definitive welcome email to the newly registered user explaining their role with a login link
      triggerWelcomeEmail(email, role, newProfile.name || nameToUse, newProfile.region || activeTenant.name || 'Work-Based Learning Portal', newProfile.school || data.school, undefined, newProfile.tenantId || activeTenant.id, newProfile.district || data.district);

      // Handle student-specific notifications
      if (role === 'student' && data.parentEmail) {
        const signupUrl = `${getTenantBaseUrl(activeTenant)}/parent-onboarding?code=${studentCode}&dob=${data.dateOfBirth}&name=${encodeURIComponent(data.contactFirstName + ' ' + data.contactLastName)}&region=${activeTenant.id}`;
        const activeRegionNoun = formatRegionWithNoun(activeTenant.name || activeTenant.id);

        sendNotificationEmail(
          data.parentEmail,
          `Parent/Guardian Consent Required: ${data.contactFirstName} ${data.contactLastName}`,
          'Work-Based Learning Portal Consent Request',
          `
            <p>Dear Parent or Guardian,</p>
            <p>Your student, <strong>${data.contactFirstName} ${data.contactLastName}</strong>, has registered for the Work-Based Learning Portal in the <strong>${activeRegionNoun}</strong>.</p>
            <p>To provide Family Educational Rights and Privacy Act (FERPA) consent and authorize your student's participation in work-based learning opportunities, please complete your parent registration.</p>
            
            <div style="text-align: center; margin: 32px 0;">
              <a href="${signupUrl}" style="background-color: #0094c8; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: bold; display: inline-block;">Complete Parent Registration</a>
            </div>

            <div style="background-color: #f8fafc; padding: 24px; border-radius: 8px; margin: 24px 0; border: 1px dashed #cbd5e1;">
              <p style="margin-top: 0; font-size: 12px; color: #64748b; font-weight: 600;">Manual Registration Details:</p>
              <div style="margin-bottom: 16px;">
                <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 1px; font-weight: bold; color: #94a3b8; margin-bottom: 4px;">Region Code</div>
                <div style="font-size: 20px; font-weight: 800; color: #0f172a; font-family: monospace;">${regionCode}</div>
              </div>
              <div>
                <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 1px; font-weight: bold; color: #94a3b8; margin-bottom: 4px;">Student Access Code</div>
                <div style="font-size: 20px; font-weight: 800; color: #0f172a; font-family: monospace;">${studentCode}</div>
              </div>
            </div>
            
            <p style="font-size: 14px; line-height: 1.6;">You can use these credentials to create a <strong>Parent Account</strong>, follow your student's learning progress, and electronically sign required participation documents.</p>
            
            <p style="font-size: 14px; margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; color: #94a3b8;">
              If you did not expect this email, please contact your student's school administration.
            </p>
          `
        ).catch(err => console.error('Parent notification error:', err));
      }
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        try {
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const uid = userCredential.user.uid;
          const nameToUse = ((data.contactFirstName || '') + ' ' + (data.contactLastName || '')).trim() || 'User';
          const initials = nameToUse.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
          
          let regionCode = '';
          let studentCode = '';

          if (role === 'student') {
            regionCode = (activeTenant.id.slice(0, 3).toUpperCase() || 'WBL');
            studentCode = generateRandomCode(6);
          }

          const existingDoc = await getDoc(doc(db, 'users', uid));
          let finalProfile: UserProfile;
          if (existingDoc.exists()) {
            finalProfile = {
              ...existingDoc.data() as UserProfile,
              ...data,
              profileComplete: true,
            };
          } else {
            finalProfile = {
              uid,
              email,
              role,
              name: nameToUse,
              tenantId: activeTenant.id,
              region: activeTenant.name,
              avatar: data.avatar || initials,
              profileComplete: true,
              firstLogin: true,
              regionCode,
              studentCode,
              ...data,
              ferpaConsent: data.ferpaConsent || ((role === 'student' || role === 'parent') ? {
                granted: false,
                date: '',
                version: '1.0'
              } : undefined)
            };
          }
          const cleanedProfile = cleanFirestoreData(finalProfile);
          await setDoc(doc(db, 'users', uid), cleanedProfile);
          setActiveUser(cleanedProfile);

          // Handle formal reciprocal linking for parents (True Link)
          if (role === 'parent' && data.childId) {
            try {
              await updateDoc(doc(db, 'users', data.childId), {
                parentId: uid
              });

              const linkId = `link-${uid}-${data.childId}`;
              await setDoc(doc(db, 'userLinks', linkId), {
                id: linkId,
                parentId: uid,
                studentId: data.childId,
                parentName: nameToUse,
                studentName: data.studentChildName || 'Student',
                status: 'verified',
                type: 'guardian',
                createdAt: new Date().toISOString(),
                tenantId: activeTenant.id
              });

              console.log(`WBL Link Engine: Established true link between Parent ${uid} and Student ${data.childId}`);
            } catch (linkError) {
              console.error('Reciprocal linking error:', linkError);
            }
          }
          return;
        } catch (signInError) {
          throw new Error('This email is already in use. Please sign in or use a different email.');
        }
      }
      throw error;
    } finally {
      localStorage.removeItem('signup_in_progress');
    }
  };

  const verifyStudentTicket = async (dob: string, code: string): Promise<string | null> => {
    if (!isFirebaseActive) return 'student-sam';
    try {
      // Normalize input code: upper case and remove any non-alphanumeric (like dashes or spaces)
      const cleanInputCode = code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
      
      if (!cleanInputCode) return null;

      // Filter by essential criteria first to keep the result set small
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'student'),
        where('dateOfBirth', '==', dob),
        limit(5)
      );
      
      const querySnapshot = await getDocs(q);
      
      // Look for a match in the returned students
      const matchedDoc = querySnapshot.docs.find(doc => {
        const storedCode = (doc.data().studentCode || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (!storedCode) return false;
        
        // Exact match (ignoring dashes/case)
        if (storedCode === cleanInputCode) return true;
        
        // Handle cases where user includes the region prefix (e.g. SPA-A4NNGB -> A4NNGB)
        if (cleanInputCode.endsWith(storedCode)) return true;
        
        return false;
      });

      return matchedDoc ? matchedDoc.id : null;
    } catch (error) {
      console.error('Ticket verification error:', error);
      return null;
    }
  };

  const linkStudent = async (studentUid: string, studentName: string, targetParentUid?: string) => {
    const parentUid = targetParentUid || activeUser?.uid;
    if (!parentUid) return;
    try {
      // Fetch student data to sync tenant context
      const studentRef = doc(db, 'users', studentUid);
      const studentSnap = await getDoc(studentRef);
      const studentData = studentSnap.data() as UserProfile | undefined;
      const studentTenantId = studentData?.tenantId;

      // 1. Update Parent Profile - adopt student context for visibility and update childrenIds
      const parentRef = doc(db, 'users', parentUid);
      let parentData: UserProfile | undefined = activeUser || undefined;
      
      if (isFirebaseActive) {
        try {
          const pSnap = await getDoc(parentRef);
          if (pSnap.exists()) {
            parentData = { uid: pSnap.id, ...pSnap.data() } as UserProfile;
          }
        } catch (pErr) {
          console.warn("Could not fetch remote parent profile, using local:", pErr);
        }
      }

      const existingChildrenIds: string[] = parentData?.childrenIds || (parentData?.childId ? [parentData.childId] : []);
      const updatedChildrenIds = Array.from(new Set([...existingChildrenIds, studentUid]));

      const parentUpdates: any = {
        childId: studentUid,
        childrenIds: updatedChildrenIds,
        role: 'parent'
      };
      if (studentTenantId) {
        parentUpdates.tenantId = studentTenantId;
      }
      
      if (isFirebaseActive) {
        await updateDoc(parentRef, parentUpdates);
      }

      // 2. Update Student Profile
      const studentUpdates: any = {
        parentId: parentUid,
        parentName: parentData?.name || activeUser?.name || 'Parent',
        parentEmail: parentData?.email || activeUser?.email || ''
      };
      if (isFirebaseActive) {
        await updateDoc(studentRef, studentUpdates);
      }

      // 3. Create/Update formal Link Record
      const linkId = `link-${parentUid}-${studentUid}`;
      if (isFirebaseActive) {
        await setDoc(doc(db, 'userLinks', linkId), {
          id: linkId,
          parentId: parentUid,
          studentId: studentUid,
          parentName: parentData?.name || activeUser?.name || 'Parent',
          studentName: studentName || studentData?.name || 'Student',
          status: 'verified',
          type: 'guardian',
          createdAt: new Date().toISOString(),
          tenantId: studentTenantId || parentData?.tenantId || activeTenant.id
        });
      }

      console.log(`WBL Link Engine: Established link for Parent ${parentUid} to Student ${studentUid} (Total children: ${updatedChildrenIds.length})`);
      
      // Update local state for immediate feedback
      if (activeUser && activeUser.uid === parentUid) {
        setActiveUser(prev => prev ? { 
          ...prev, 
          childId: studentUid,
          childrenIds: updatedChildrenIds,
          tenantId: studentTenantId || prev.tenantId 
        } : null);
      }
    } catch (err) {
      console.error('Manual link error:', err);
      throw err;
    }
  };

  const switchActiveChild = async (studentUid: string) => {
    if (!activeUser || activeUser.role !== 'parent') return;
    try {
      // Find student tenant
      const studentRef = doc(db, 'users', studentUid);
      const studentSnap = await getDoc(studentRef);
      const studentData = studentSnap.data() as UserProfile | undefined;
      const studentTenantId = studentData?.tenantId;

      const updates: any = {
        childId: studentUid
      };
      if (studentTenantId) {
        updates.tenantId = studentTenantId;
      }

      if (isFirebaseActive) {
        await updateDoc(doc(db, 'users', activeUser.uid), updates);
      }

      setActiveUser(prev => prev ? {
        ...prev,
        childId: studentUid,
        tenantId: studentTenantId || prev.tenantId
      } : null);

      if (studentTenantId && studentTenantId !== 'global') {
        const matchingTenant = tenants.find(t => t.id === studentTenantId);
        if (matchingTenant) setActiveTenant(matchingTenant);
      }
    } catch (err) {
      console.error('Error switching active child:', err);
    }
  };

  const migrateStudentData = async (oldStudentId: string, newStudentId: string) => {
    if (isUsingMockKeys || !oldStudentId || !newStudentId || oldStudentId === newStudentId) return;
    console.log(`WBL Migration Engine: Initiating student data migration from ${oldStudentId} to ${newStudentId}...`);
    try {
      const { collection, getDocs, query, where, writeBatch, doc } = await import('firebase/firestore');
      const batch = writeBatch(db);
      let operationCount = 0;

      // 1. Migrate Applications
      const appsSnap = await getDocs(query(collection(db, 'applications'), where('studentId', '==', oldStudentId)));
      appsSnap.forEach(snap => {
        batch.update(doc(db, 'applications', snap.id), { studentId: newStudentId });
        operationCount++;
      });

      // 2. Migrate Placements
      const placeSnap = await getDocs(query(collection(db, 'placements'), where('studentId', '==', oldStudentId)));
      placeSnap.forEach(snap => {
        batch.update(doc(db, 'placements', snap.id), { studentId: newStudentId });
        operationCount++;
      });

      // 3. Migrate Tasks
      const tasksSnap = await getDocs(query(collection(db, 'tasks'), where('studentId', '==', oldStudentId)));
      tasksSnap.forEach(snap => {
        batch.update(doc(db, 'tasks', snap.id), { studentId: newStudentId });
        operationCount++;
      });

      // 4. Migrate Attendance
      const attSnap = await getDocs(query(collection(db, 'attendance'), where('studentId', '==', oldStudentId)));
      attSnap.forEach(snap => {
        batch.update(doc(db, 'attendance', snap.id), { studentId: newStudentId });
        operationCount++;
      });

      // 5. Migrate Interviews
      const intSnap = await getDocs(query(collection(db, 'interviews'), where('studentId', '==', oldStudentId)));
      intSnap.forEach(snap => {
        batch.update(doc(db, 'interviews', snap.id), { studentId: newStudentId });
        operationCount++;
      });

      // 6. Migrate userLinks
      const linksSnap = await getDocs(query(collection(db, 'userLinks'), where('studentId', '==', oldStudentId)));
      linksSnap.forEach(snap => {
        batch.update(doc(db, 'userLinks', snap.id), { studentId: newStudentId });
        operationCount++;
      });

      // 7. Migrate Parents / other users childId reference
      const parentsSnap = await getDocs(query(collection(db, 'users'), where('childId', '==', oldStudentId)));
      parentsSnap.forEach(snap => {
        batch.update(doc(db, 'users', snap.id), { childId: newStudentId });
        operationCount++;
      });

      if (operationCount > 0) {
        await batch.commit();
        console.log(`WBL Migration Engine: Student migration success. Consolidated ${operationCount} records.`);
      } else {
        console.log(`WBL Migration Engine: No matching orphan records found for student ${oldStudentId}.`);
      }
    } catch (err) {
      console.warn("WBL Migration Engine error:", err);
    }
  };

  const migrateParentData = async (oldParentId: string, newParentId: string) => {
    if (isUsingMockKeys || !oldParentId || !newParentId || oldParentId === newParentId) return;
    console.log(`WBL Migration Engine: Initiating parent data migration from ${oldParentId} to ${newParentId}...`);
    try {
      const { collection, getDocs, query, where, writeBatch, doc } = await import('firebase/firestore');
      const batch = writeBatch(db);
      let operationCount = 0;

      // 1. Migrate userLinks
      const linksSnap = await getDocs(query(collection(db, 'userLinks'), where('parentId', '==', oldParentId)));
      linksSnap.forEach(snap => {
        batch.update(doc(db, 'userLinks', snap.id), { parentId: newParentId });
        operationCount++;
      });

      // 2. Migrate students parentId reference
      const studentsSnap = await getDocs(query(collection(db, 'users'), where('parentId', '==', oldParentId)));
      studentsSnap.forEach(snap => {
        batch.update(doc(db, 'users', snap.id), { parentId: newParentId });
        operationCount++;
      });

      if (operationCount > 0) {
        await batch.commit();
        console.log(`WBL Parent Migration: Consolidated ${operationCount} parent records.`);
      }
    } catch (err) {
      console.warn("WBL Parent Migration error:", err);
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    const cleanEmail = email.trim().toLowerCase();
    let existingUser = users.find(u => u.email && u.email.trim().toLowerCase() === cleanEmail) || MOCK_USERS.find(u => u.email && u.email.trim().toLowerCase() === cleanEmail);

    if (!existingUser && !isUsingMockKeys) {
      try {
        const q = query(collection(db, 'users'), where('email', '==', email.trim()));
        const snap = await getDocs(q);
        if (!snap.empty) {
          existingUser = { uid: snap.docs[0].id, ...snap.docs[0].data() } as UserProfile;
        } else if (cleanEmail !== email.trim()) {
          const qLower = query(collection(db, 'users'), where('email', '==', cleanEmail));
          const snapLower = await getDocs(qLower);
          if (!snapLower.empty) {
            existingUser = { uid: snapLower.docs[0].id, ...snapLower.docs[0].data() } as UserProfile;
          }
        }
      } catch (fErr) {
        console.warn('Firestore fallback user query error:', fErr);
      }
    }

    if (existingUser) {
      const storedPassword = existingUser.customPassword || existingUser.tempPassword;

      // If a custom/temp password override is set on user profile, it is strictly authoritative
      if (storedPassword) {
        if (password !== storedPassword) {
          throw new Error('Invalid email or password.');
        }

        console.log(`Authenticating ${cleanEmail} via portal profile credentials...`);
        try {
          await signInWithEmailAndPassword(auth, email, password);
        } catch (signInErr: any) {
          try {
            await createUserWithEmailAndPassword(auth, email, password);
          } catch (createErr: any) {
            // Background auth sync attempt
          }
        }

        setActiveUser(existingUser);
        if (existingUser.tenantId && existingUser.tenantId !== 'global') {
          const userTenant = tenants.find(t => t.id === existingUser.tenantId);
          if (userTenant) setActiveTenant(userTenant);
        }
        setCurrentView('dashboard');
        writeLog(existingUser.tenantId || 'global', email, 'USER_LOGIN', `User ${existingUser.name || email} signed into portal.`);
        return;
      }
    }

    // Default Firebase Auth authentication when no custom password override exists
    try {
      await signInWithEmailAndPassword(auth, email, password);
      if (existingUser) {
        setActiveUser(existingUser);
        if (existingUser.tenantId && existingUser.tenantId !== 'global') {
          const userTenant = tenants.find(t => t.id === existingUser.tenantId);
          if (userTenant) setActiveTenant(userTenant);
        }
      }
      setCurrentView('dashboard');
    } catch (error: any) {
      // If Firebase Auth cannot find the user or returns invalid-credential,
      // and this user profile exists in seeded records (e.g. joshua@losonova.com),
      // allow claiming/registering the account in Firebase Auth with the provided password:
      if (existingUser && (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found')) {
        try {
          const newCred = await createUserWithEmailAndPassword(auth, email, password);
          const uid = newCred.user.uid;
          const userProfileToSave = { ...existingUser, uid };
          if (!isUsingMockKeys) {
            await setDoc(doc(db, 'users', uid), userProfileToSave, { merge: true });
          }
          setActiveUser(userProfileToSave);
          if (userProfileToSave.tenantId && userProfileToSave.tenantId !== 'global') {
            const userTenant = tenants.find(t => t.id === userProfileToSave.tenantId);
            if (userTenant) setActiveTenant(userTenant);
          }
          setCurrentView('dashboard');
          writeLog(userProfileToSave.tenantId || 'global', email, 'USER_SIGNUP', `Portal account claimed for ${email}.`);
          return;
        } catch (claimErr: any) {
          // If claimErr is auth/email-already-in-use, user was registered in Auth but typed wrong password
          console.warn('Firebase Auth sign-in rejected for', cleanEmail);
          throw new Error('Invalid email or password. Please check your credentials or click "Forgot?" to reset.');
        }
      }

      console.warn('Firebase Auth notice:', error.code || error.message);
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        throw new Error('Invalid email or password. Please check your credentials or click "Forgot?" to reset.');
      }
      if (existingUser && isUsingMockKeys) {
        setActiveUser(existingUser);
        if (existingUser.tenantId && existingUser.tenantId !== 'global') {
          const userTenant = tenants.find(t => t.id === existingUser.tenantId);
          if (userTenant) setActiveTenant(userTenant);
        }
        setCurrentView('dashboard');
        writeLog(existingUser.tenantId || 'global', email, 'USER_LOGIN', `User ${existingUser.name || email} signed into portal.`);
        return;
      }
      throw error;
    }
  };

  const signOutUser = async () => {
    let tenantIdStr: string | null = null;
    
    if (!systemConfig.multiTenantEnabled && systemConfig.selectedRegionId !== 'global') {
      tenantIdStr = systemConfig.selectedRegionId;
    } else {
      tenantIdStr = activeUser?.tenantId || (activeTenant && activeTenant.id !== 'apextech' && activeTenant.id !== 'global' ? activeTenant.id : null);
    }

    if (!isUsingMockKeys) {
      await signOut(auth).catch((err) => console.warn("SignOut auth error:", err));
    }
    setProxyOriginalSuperadmin(null);
    setActiveUser(null);
    setCurrentView('dashboard');
    
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('wbl_active_user');
      localStorage.removeItem('wbl_active_user');
      sessionStorage.removeItem('wbl_superadmin_proxy_backup');

      const isRegionSpecific = tenantIdStr && !VALID_VIEWS.includes(tenantIdStr) && tenantIdStr !== 'global' && tenantIdStr !== 'apextech';
      const targetPath = isRegionSpecific ? `/${tenantIdStr}` : '/';

      if (window.location.pathname !== targetPath) {
        window.location.href = targetPath;
      } else {
        window.location.reload();
      }
    }
  };

  const validateBrandingSizes = (updates: any) => {
    const checkValue = (val: any) => {
      if (typeof val === 'string' && val.startsWith('data:image') && val.length > 200000) {
        throw new Error(`Branding image asset is too large. Image size exceeds the maximum database limit of 150KB. Please upload a smaller or compressed image.`);
      } else if (val && typeof val === 'object') {
        Object.values(val).forEach(checkValue);
      }
    };
    checkValue(updates);
  };

  // Corporate Portal Color themes / Subdomain workspace updater (Superadmin console)
  const updateTenantBranding = (tenantId: string, updates: Partial<Tenant>) => {
    if (!tenantId) {
      console.warn("WBL Warning: cannot update tenant branding for undefined tenantId");
      return;
    }
    // Immediate local state update for fast response
    setTenants(prev => {
      const newTenants = prev.map(t => t && t.id === tenantId ? { ...t, ...updates } : t);
      const updatedActive = newTenants.find(t => t && t.id === activeTenant.id);
      if (updatedActive) {
        setActiveTenant(updatedActive);
      }
      return newTenants;
    });

    if (!isUsingMockKeys) {
      try {
        validateBrandingSizes(updates);
        const docRef = doc(db, 'regions', tenantId);
        // setDoc with merge: true handles records that might not be seeded/fully created in Firestore yet
        setDoc(docRef, cleanFirestoreData(updates), { merge: true })
          .then(() => writeLog(tenantId, activeUser?.email || 'Admin', 'UPDATE_BRANDING', `Modified branding configurations.`))
          .catch(e => handleFirestoreError(e, OperationType.UPDATE, `regions/${tenantId}`));
      } catch (validationErr: any) {
        console.error("Validation error for tenant branding update: ", validationErr);
        handleFirestoreError(validationErr, OperationType.UPDATE, `regions/${tenantId}`);
      }
    }
  };

  // Turn all portals on/off atomically using a Firestore writeBatch transaction
  const updateAllTenantsBranding = async (updates: Partial<Tenant>) => {
    // 1. Update local state once
    setTenants(prev => {
      const newTenants = prev.map(t => t ? { ...t, ...updates } : t);
      const updatedActive = newTenants.find(t => t && t.id === activeTenant.id);
      if (updatedActive) {
        setActiveTenant(updatedActive);
      }
      return newTenants;
    });

    // 2. Perform live atomic commit if connected to Firebase
    if (!isUsingMockKeys) {
      try {
        validateBrandingSizes(updates);
        const batch = writeBatch(db);
        // Loop over 46 counties and add setDoc with merge to the batch structure
        const countiesList = tenants.length > 0 ? tenants : INITIAL_TENANTS;
        countiesList.forEach(county => {
          if (!county || !county.id) return;
          const docRef = doc(db, 'regions', county.id);
          batch.set(docRef, updates, { merge: true });
        });
        await batch.commit();
        writeLog('global', activeUser?.email || 'Admin', 'BULK_UPDATE_BRANDING', `Modified branding configurations for all counties.`);
      } catch (e: any) {
        console.error("Bulk Firestore update error, falling back to individual updates:", e);
        // Fallback sequentially in case writeBatch fails
        const countiesList = tenants.length > 0 ? tenants : INITIAL_TENANTS;
        for (const county of countiesList) {
          if (!county || !county.id) continue;
          try {
            validateBrandingSizes(updates);
            const docRef = doc(db, 'regions', county.id);
            await setDoc(docRef, cleanFirestoreData(updates), { merge: true });
          } catch (err) {
            console.error(`Failed individual bulk write for ${county.id}:`, err);
          }
        }
      }
    }
  };

  const updateGlobalBranding = async (updates: Partial<GlobalBranding>) => {
    setGlobalBranding(prev => ({
      ...prev,
      ...updates,
      palette: updates.palette ? { ...prev.palette, ...updates.palette } : prev.palette,
      logos: updates.logos ? { ...prev.logos, ...updates.logos } : prev.logos,
      clusterImages: updates.clusterImages ? { ...prev.clusterImages, ...updates.clusterImages } : prev.clusterImages,
      subclusterImages: updates.subclusterImages ? { ...prev.subclusterImages, ...updates.subclusterImages } : prev.subclusterImages,
    }));

    if (!isUsingMockKeys) {
      try {
        validateBrandingSizes(updates);
        const docRef = doc(db, 'regions', 'global');
        await setDoc(docRef, cleanFirestoreData(updates), { merge: true });
        writeLog('global', activeUser?.email || 'Admin', 'UPDATE_GLOBAL_BRANDING', `Modified global branding identity configurations.`);
      } catch (e: any) {
        console.error("Failed to update global branding: ", e);
        handleFirestoreError(e, OperationType.UPDATE, 'regions/global');
      }
    }
  };

  const createTenant = (tenant: Tenant) => {
    if (!isUsingMockKeys) {
      setDoc(doc(db, 'regions', tenant.id), tenant)
        .then(() => writeLog(tenant.id, activeUser?.email || 'Superadmin', 'CREATE_TENANT', `Registered Tenant workspace [${tenant.name}]`))
        .catch(e => handleFirestoreError(e, OperationType.CREATE, `regions/${tenant.id}`));
    }
  };

  // --- Notification Preferences Evaluation Engine ---
  const shouldUserReceiveNotification = (
    user: UserProfile | null | undefined,
    channel: 'email' | 'in_app',
    category?: keyof NotificationPreferences
  ): boolean => {
    if (!user) return true; // Non-registered recipients (e.g. external contact) default to true
    const prefs = user.notificationPrefs;
    if (!prefs) return true; // Default is all enabled

    if (channel === 'email') {
      if (prefs.emailNotifications === false) return false;
      if (prefs.instantEmailAlerts === false) return false;

      // Quiet Hours Evaluation (Eastern Time)
      try {
        const now = new Date();
        const estDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
        const estHour = estDate.getHours();
        const estDay = estDate.getDay(); // 0 is Sun, 6 is Sat

        if (prefs.nightlyBlackout) {
          // 10:00 PM (22:00) – 6:00 AM (06:00)
          if (estHour >= 22 || estHour < 6) {
            return false;
          }
        }

        if (prefs.schoolHourBlackout) {
          // 8:00 AM (08:00) – 3:00 PM (15:00) on weekdays (Mon-Fri)
          if (estDay >= 1 && estDay <= 5 && estHour >= 8 && estHour < 15) {
            return false;
          }
        }
      } catch (e) {
        // Fall through safely if timezone parsing fails
      }
    } else if (channel === 'in_app') {
      if (prefs.inAppNotifications === false) return false;
    }

    // Specific Alert Category Evaluation
    if (category && prefs[category] === false) {
      return false;
    }

    return true;
  };

  // --- SendGrid Notification Dispatcher ---
  const sendNotificationEmail = async (
    to: string | null | undefined, 
    subject: string, 
    title: string, 
    bodyHtml: string, 
    overrideTenantId?: string,
    category?: keyof NotificationPreferences
  ) => {
    if (!to || typeof to !== 'string') {
      console.warn(`WBL Notify: Recipient email is undefined or invalid. Skipping dispatch for "${subject}"`);
      return;
    }

    const normTo = to.trim().toLowerCase();
    const recipientUser = users.find(u => u.email?.trim().toLowerCase() === normTo);

    // Resolve tenant for styling and storage
    let mailTenant = activeTenant;
    let resolvedTenantId = overrideTenantId || recipientUser?.tenantId;
    if (resolvedTenantId) {
      const found = tenants.find(t => t.id === resolvedTenantId || t.subdomain === resolvedTenantId);
      if (found) {
        mailTenant = found;
      }
    }

    const rawMessageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`;
    const regionId = mailTenant?.id || 'spartanburg';
    const properRegionName = formatRegionProperNoun(mailTenant?.name || mailTenant?.id || 'Spartanburg');
    const regionWithNoun = formatRegionWithNoun(properRegionName);

    // 1. Evaluate Channel Permissions
    const allowInApp = shouldUserReceiveNotification(recipientUser, 'in_app', category);
    const allowEmail = shouldUserReceiveNotification(recipientUser, 'email', category);

    let suppressionReason: string | undefined = undefined;
    if (!allowEmail) {
      if (recipientUser?.notificationPrefs?.emailNotifications === false) {
        suppressionReason = 'Master email notifications disabled in user profile';
      } else if (recipientUser?.notificationPrefs?.instantEmailAlerts === false) {
        suppressionReason = 'Instant email dispatch disabled in user profile';
      } else if (category && recipientUser?.notificationPrefs && recipientUser.notificationPrefs[category] === false) {
        suppressionReason = `User opted out of "${String(category)}" alerts`;
      } else {
        suppressionReason = 'Suppressed due to blackout/quiet hours';
      }
    }

    const channel: 'email_and_portal' | 'email_only' | 'portal_only' = 
      (allowInApp && allowEmail) ? 'email_and_portal' : 
      allowEmail ? 'email_only' : 'portal_only';

    const deliveryStatus: 'delivered' | 'simulated' | 'suppressed' | 'failed' = 
      allowEmail ? 'delivered' : 'suppressed';

    const notificationRecord: PortalMessage = {
      id: rawMessageId,
      tenantId: mailTenant?.id || 'global',
      toEmail: normTo,
      recipientName: recipientUser?.name || `${recipientUser?.contactFirstName || ''} ${recipientUser?.contactLastName || ''}`.trim() || '',
      recipientRole: recipientUser?.role || 'external',
      subject,
      title,
      bodyHtml,
      sentAt: new Date().toISOString(),
      read: false,
      fromName: `${properRegionName} WBL Portal`,
      deleted: !allowInApp, // If user disabled in-portal notifications, soft-deleted for their inbox, but retained for superadmin ledger
      category: category ? String(category) : 'standardAlerts',
      channel,
      deliveryStatus,
      suppressionReason
    };

    if (!isUsingMockKeys) {
      try {
        await setDoc(doc(db, 'messages', rawMessageId), notificationRecord);
        console.log(`WBL Notify: Recorded notification message ${rawMessageId} for ${normTo} (Status: ${deliveryStatus})`);
      } catch (dbErr: any) {
        console.warn('WBL Notify: Failed writing notification record to Firestore:', dbErr.message);
      }
    } else {
      setMessages(prev => [notificationRecord, ...prev]);
    }

    // 2. Outbound Email Notifications Check
    if (!allowEmail) {
      console.log(`WBL Notify: Outbound email to ${normTo} suppressed per user preferences: ${suppressionReason}`);
      return;
    }

    // 3. Dispatch the real outbound SendGrid request
    try {
      console.log(`WBL Notify: Requesting SendGrid email dispatch to: ${to} | Subject: "${subject}" | Category: ${category ? String(category) : 'standard'}`);
      const darkBg = getRegionDarkBg(regionId);
      const accentColor = getRegionThemeAccent(regionId);
      let logoUrl = mailTenant?.logos?.navigation || globalBranding?.logos?.navigation || '';
      if (logoUrl && logoUrl.startsWith('/')) {
        logoUrl = getTenantBaseUrl(mailTenant) + logoUrl;
      }

      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to,
          fromName: `${properRegionName} WBL Portal`,
          subject,
          messageId: rawMessageId,
          category: category ? String(category) : 'standardAlerts',
          recipientName: notificationRecord.recipientName,
          recipientRole: notificationRecord.recipientRole,
          tenantId: mailTenant?.id || 'global',
          html: `
            <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f8fafc;">
              <div style="border: 1px solid #e2e8f0; border-radius: 16px; background-color: #ffffff; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.03);">
                <!-- Header Band: Dark Main template -->
                <div style="background-color: ${darkBg}; padding: 32px 24px; text-align: center; color: #ffffff;">
                  ${logoUrl ? `
                    <img src="${logoUrl}" alt="${properRegionName} Logo" style="max-height: 44px; margin: 0 auto 12px; display: block;" />
                  ` : `
                    <div style="font-size: 28px; font-weight: 900; letter-spacing: -0.05em; margin: 0; color: #ffffff;">
                      ${properRegionName.toUpperCase()} <span style="color: ${accentColor};">WBL</span>
                    </div>
                  `}
                  <h1 style="color: #ffffff; font-size: 18px; font-weight: 800; margin: 8px 0 0; letter-spacing: -0.02em; text-transform: uppercase;">
                    ${regionWithNoun}
                  </h1>
                </div>
                
                <!-- Theme Accent Bar -->
                <div style="height: 4px; background-color: ${accentColor};"></div>
                
                <!-- Email Body -->
                <div style="padding: 32px 24px; background-color: #ffffff;">
                  <h2 style="color: #0f172a; font-size: 20px; font-weight: 800; margin-top: 0; margin-bottom: 20px; letter-spacing: -0.02em;">
                    ${title}
                  </h2>
                  <div style="font-size: 14px; line-height: 1.6; color: #334155;">
                    ${bodyHtml}
                  </div>
                </div>
                
                <!-- Footer -->
                <div style="background-color: #f8fafc; padding: 24px; border-top: 1px solid #f1f5f9; text-align: center; font-size: 11px; color: #64748b; line-height: 1.5;">
                  <p style="margin: 0; font-weight: 600;">This email was sent from the Work-Based Learning Portal notification service for the ${regionWithNoun}.</p>
                  <p style="margin: 4px 0 0; color: #94a3b8;">Please do not reply directly to this automated email.</p>
                </div>
              </div>
            </div>
          `
        })
      });

      const resData = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.warn('WBL Notify client side: Server returned failure processing SendGrid email:', resData);
      } else {
        console.log('WBL Notify client side: SendGrid email dispatched successfully.');
        if (resData.simulated && !isUsingMockKeys) {
          await setDoc(doc(db, 'messages', rawMessageId), { deliveryStatus: 'simulated' }, { merge: true }).catch(() => {});
        }
      }
    } catch (e) {
      console.error('WBL Notify client side: Network failure triggering SendGrid notifier:', e);
    }
  };

  const submitRegionInquiry = async (data: {
    regionId: string;
    regionName: string;
    name: string;
    email: string;
    phone?: string;
    role?: string;
    organization?: string;
    notes?: string;
  }) => {
    const payload = {
      ...data,
      type: 'regional_inquiry',
      status: 'new',
      createdAt: new Date().toISOString()
    };

    try {
      if (!isUsingMockKeys) {
        await addDoc(collection(db, 'support_inquiries'), cleanFirestoreData(payload));
      }
    } catch (err: any) {
      console.error("Failed to save region inquiry to Firestore:", err);
    }

    writeLog(
      data.regionId, 
      data.email, 
      'REGION_INQUIRY', 
      `Submitted regional inquiry for ${data.regionName} County WBL portal (${data.organization || 'General'})`
    );

    // Send email to superadmin
    try {
      const superadminSubject = `[Regional Inquiry] Inquiry for ${data.regionName} County`;
      const superadminTitle = `New Regional Inquiry: ${data.regionName} County`;
      const superadminBody = `
        <p>A new regional inquiry has been submitted regarding the Work-Based Learning portal for <strong>${data.regionName} County</strong>.</p>
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin: 16px 0;">
          <p style="margin: 4px 0;"><strong>Name:</strong> ${data.name}</p>
          <p style="margin: 4px 0;"><strong>Email:</strong> <a href="mailto:${data.email}">${data.email}</a></p>
          ${data.phone ? `<p style="margin: 4px 0;"><strong>Phone:</strong> ${data.phone}</p>` : ''}
          ${data.role ? `<p style="margin: 4px 0;"><strong>Role / Affiliation:</strong> ${data.role}</p>` : ''}
          ${data.organization ? `<p style="margin: 4px 0;"><strong>Organization / School:</strong> ${data.organization}</p>` : ''}
          ${data.notes ? `<p style="margin: 4px 0;"><strong>Inquiry / Notes:</strong> ${data.notes}</p>` : ''}
        </div>
      `;
      await sendNotificationEmail('admin@wblportal.com', superadminSubject, superadminTitle, superadminBody, 'global');
    } catch (mailErr) {
      console.warn("Mail dispatch error on region inquiry:", mailErr);
    }

    // Send confirmation email to requester
    try {
      const userSubject = `We received your inquiry regarding ${data.regionName} County Work-Based Learning`;
      const userTitle = `Thank you for your inquiry regarding ${data.regionName} County`;
      const userBody = `
        <p>Hello ${data.name},</p>
        <p>Thank you for reaching out with your inquiry regarding the Work-Based Learning Portal for <strong>${data.regionName} County</strong>.</p>
        <p>Our team has received your message and will follow up with information on Work-Based Learning pathways, district coordination, and regional resources.</p>
        ${data.organization ? `
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin: 16px 0;">
          <p style="margin: 4px 0;"><strong>Organization / School:</strong> ${data.organization}</p>
        </div>` : ''}
      `;
      await sendNotificationEmail(data.email, userSubject, userTitle, userBody, data.regionId);
    } catch (mailErr2) {
      console.warn("Confirmation mail error on region inquiry:", mailErr2);
    }
  };

  async function triggerWelcomeEmail(email: string, role: UserRole, displayName: string, regionName: string, schoolName?: string, tempPassword?: string, tenantId?: string, districtName?: string) {
    const properRegion = formatRegionProperNoun(regionName || activeTenant.name || 'Spartanburg');
    const regionNoun = formatRegionWithNoun(properRegion);

    let roleExplanation = '';
    if (role === 'student') {
      roleExplanation = `As a Student${schoolName ? ` at ${schoolName}` : ''}, you can explore available Work-Based Learning placements, request matches with leading employers, log and track your work hours, complete learning assignments, and collaborate with educational advisors to support your career pathway.`;
    } else if (role === 'parent') {
      roleExplanation = 'As a Parent or Guardian, you can review your student\'s vocational progress, view their active work-based placements, and securely grant required educational consents (such as FERPA) to support their academic and professional development.';
    } else if (role === 'organization') {
      roleExplanation = 'As an Employer Partner, you can post Work-Based Learning opportunities, review student applications, schedule interviews, monitor logged hours, and provide mentorship and evaluations for future professionals.';
    } else if (role === 'advisor') {
      roleExplanation = 'As an Advisor, you can guide students on their career pathways, review and approve placement listings, verify logged hours, and ensure curriculum alignment to prepare students for college and career success.';
    } else if (role === 'regional_admin') {
      roleExplanation = `As a Regional Administrator, you have comprehensive oversight of school districts, local employer networks, student pathways, and program standards across the ${regionNoun}.`;
    } else if (role === 'superadmin') {
      roleExplanation = 'As a Super Administrator, you have full administrative authority to configure platform modules, oversee system audits, manage tenant credentials, and coordinate global portal parameters.';
    } else {
      roleExplanation = `As a ${(role as string).replace('_', ' ')}, you can access all the resources, tools, and portals configured for your role in the Work-Based Learning system.`;
    }

    // Resolve active base URL for links based on the active region (tenant)
    let recipientTenant = activeTenant;
    if (tenantId) {
      const foundTenant = tenants.find(t => t.id === tenantId);
      if (foundTenant) {
        recipientTenant = foundTenant;
      }
    } else if (regionName && regionName !== 'WBL Portal' && regionName !== 'Global' && regionName !== 'Work-Based Learning Portal') {
      const foundTenant = tenants.find(t => t.name === regionName || t.id === regionName);
      if (foundTenant) {
        recipientTenant = foundTenant;
      }
    }
    const tenantBaseUrl = getTenantBaseUrl(recipientTenant);

    try {
      let credentialsHtml = '';
      if (tempPassword) {
        credentialsHtml = `
          <div style="background-color: #fffbeb; padding: 20px; border-radius: 12px; margin: 24px 0; border: 1px solid #fde68a;">
            <h3 style="margin-top: 0; color: #92400e; font-size: 14px; font-weight: bold; text-transform: uppercase;">Login Credentials</h3>
            <p style="margin: 8px 0; font-size: 14px; color: #451a03;"><strong>Username:</strong> ${email}</p>
            <p style="margin: 8px 0; font-size: 14px; color: #451a03;"><strong>Temporary Password:</strong> <code style="background: #fef3c7; padding: 2px 6px; border-radius: 4px;">${tempPassword}</code></p>
            <p style="margin-top: 12px; margin-bottom: 0; font-size: 12px; color: #92400e; font-style: italic;">* You will be required to create a new password upon your initial sign-in.</p>
          </div>
        `;
      }

      await sendNotificationEmail(
        email,
        `Welcome to the Work-Based Learning Portal, ${displayName}!`,
        'Welcome to the Work-Based Learning Portal',
        `
          <p>Dear <strong>${displayName}</strong>,</p>
          <p>We are thrilled to welcome you to the <strong>${regionNoun}</strong> Work-Based Learning Portal.</p>
          
          <div style="background-color: #f8fafc; padding: 20px; border-radius: 12px; margin: 24px 0; border: 1px solid #e2e8f0;">
            <h3 style="margin-top: 0; color: #0f172a; font-size: 15px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.05em;">Your Role: <span style="color: #0094c8;">${(role as string).replace('_', ' ').toUpperCase()}</span></h3>
            <p style="margin-bottom: 0; font-size: 14px; line-height: 1.6; color: #475569;">${roleExplanation}</p>
          </div>

          ${credentialsHtml}

          <p>To get started, please access your secure dashboard, update your profile settings, and begin collaborating through the Work-Based Learning Portal.</p>
          
          <div style="text-align: center; margin: 32px 0;">
            <a href="${tenantBaseUrl}" style="background-color: #0094c8; color: white; padding: 12px 28px; border-radius: 8px; text-decoration: none; font-weight: bold; display: inline-block; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">Access Portal</a>
          </div>

          <p style="font-size: 13px; color: #64748b;">If you have any questions or require assistance, please contact your local program advisor or school administrator.</p>
          
          <p style="font-size: 14px; margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; color: #94a3b8;">
            Best regards,<br />
            <strong>The ${regionNoun} Team</strong>
          </p>
        `,
        recipientTenant?.id
      );

      // Trigger email notifications to Superadmins and Regional Admins
      await notifyAdminsOnUserSignup({
        email,
        role,
        name: displayName,
        region: regionName,
        tenantId: tenantId || recipientTenant?.id || 'global',
        school: schoolName,
        district: districtName
      });
    } catch (err) {
      console.error('Failed dispatching welcome email:', err);
    }
  }

  async function notifyAdminsOnUserSignup(user: { email: string; role: UserRole; name?: string; region?: string; tenantId?: string; school?: string; district?: string }) {
    try {
      const userRoleFormatted = (user.role || 'user').replace('_', ' ').toUpperCase();
      const userName = user.name || user.email || 'New User';
      const userEmail = user.email;
      const userTenantId = user.tenantId || 'global';
      const signupDate = new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });

      const foundUser = users.find(u => u.email?.toLowerCase() === userEmail?.toLowerCase());
      const schoolName = user.school || foundUser?.school || foundUser?.pendingSchool || '';
      const districtName = user.district || foundUser?.district || '';

      // Determine region display name
      const matchingTenant = tenants.find(t => t.id === userTenantId || t.name === user.region);
      const rawRegionName = matchingTenant?.name || user.region || userTenantId || 'Spartanburg';
      const properRegionName = formatRegionProperNoun(rawRegionName);
      const regionWithNoun = formatRegionWithNoun(properRegionName);

      const isOrgSignup = user.role === 'organization';
      const category: keyof NotificationPreferences = isOrgSignup ? 'partnerOrgAlerts' : 'newUserRegistrationAlerts';

      // Build list of superadmin emails
      const superadminEmails = new Set<string>();
      let anyAdminFound = false;

      // Superadmins from state
      users.filter(u => u.role === 'superadmin' && u.email).forEach(u => {
        anyAdminFound = true;
        if (shouldUserReceiveNotification(u, 'email', category)) {
          superadminEmails.add(u.email!.trim().toLowerCase());
        }
      });

      // Query Firestore for superadmins if using live DB
      if (!isUsingMockKeys && isFirebaseActive) {
        try {
          const superadminSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'superadmin')));
          superadminSnap.forEach(docSnap => {
            const u = docSnap.data() as UserProfile;
            if (u.email) {
              anyAdminFound = true;
              if (shouldUserReceiveNotification(u, 'email', category)) {
                superadminEmails.add(u.email.trim().toLowerCase());
              }
            }
          });
        } catch (err) {
          console.warn('WBL Notify: Error fetching superadmins from Firestore:', err);
        }
      }

      // Build list of regional admin emails for this user's region
      const regionalAdminEmails = new Set<string>();

      // Regional admins from state
      users.filter(u => u.role === 'regional_admin' && u.email && (u.tenantId === userTenantId || u.region === rawRegionName || u.tenantId === matchingTenant?.id)).forEach(u => {
        anyAdminFound = true;
        if (shouldUserReceiveNotification(u, 'email', category)) {
          regionalAdminEmails.add(u.email!.trim().toLowerCase());
        }
      });

      // Query Firestore for regional admins if using live DB
      if (!isUsingMockKeys && isFirebaseActive && userTenantId !== 'global') {
        try {
          const regionalAdminSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'regional_admin'), where('tenantId', '==', userTenantId)));
          regionalAdminSnap.forEach(docSnap => {
            const u = docSnap.data() as UserProfile;
            if (u.email) {
              anyAdminFound = true;
              if (shouldUserReceiveNotification(u, 'email', category)) {
                regionalAdminEmails.add(u.email.trim().toLowerCase());
              }
            }
          });
        } catch (err) {
          console.warn('WBL Notify: Error fetching regional admins from Firestore:', err);
        }
      }

      // Only fall back to default email IF no administrator accounts exist at all in system
      if (superadminEmails.size === 0 && regionalAdminEmails.size === 0 && !anyAdminFound) {
        superadminEmails.add('joshua@losonova.com');
      }

      // 1. Send email to Superadmins
      const superadminSubject = isOrgSignup
        ? `[New Employer Onboarding] ${userName} (${user.name || 'Organization Partner'}) - ${regionWithNoun}`
        : `[New User Registration] ${userName} (${userRoleFormatted}) - ${regionWithNoun}`;
      const superadminTitle = isOrgSignup ? `New Employer & Host Partner Onboarding Alert` : `New Portal User Registration Alert`;
      const superadminBody = `
        <p>Hello Administrator,</p>
        <p>A new ${isOrgSignup ? 'employer/host partner' : 'user account'} has registered on the Work-Based Learning Portal.</p>

        <div style="background-color: #f8fafc; padding: 20px; border-radius: 12px; margin: 24px 0; border: 1px solid #e2e8f0;">
          <h3 style="margin-top: 0; color: #0f172a; font-size: 14px; font-weight: bold; text-transform: uppercase;">User Profile Details</h3>
          <p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>Full Name:</strong> ${userName}</p>
          <p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>Email Address:</strong> <a href="mailto:${userEmail}" style="color: #0094c8; text-decoration: none; font-weight: 600;">${userEmail}</a></p>
          <p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>Role:</strong> <span style="display: inline-block; background-color: #e0f2fe; color: #0369a1; padding: 2px 8px; border-radius: 4px; font-weight: bold; font-size: 12px;">${userRoleFormatted}</span></p>
          <p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>Region:</strong> ${regionWithNoun}</p>
          <p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>School:</strong> ${schoolName || 'Not specified'}</p>
          <p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>District:</strong> ${districtName || 'Not specified'}</p>
          <p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>Registration Date:</strong> ${signupDate}</p>
        </div>

        <p style="font-size: 13px; color: #64748b;">You can review and manage this user account in the Superadmin Console.</p>
      `;

      for (const saEmail of superadminEmails) {
        if (saEmail.toLowerCase() === userEmail?.toLowerCase()) continue;
        await sendNotificationEmail(saEmail, superadminSubject, superadminTitle, superadminBody, 'global', category);
      }

      // 2. Send email to Regional Admins
      const regionalAdminSubject = isOrgSignup
        ? `[Regional Employer Alert] New Host Organization registered in the ${regionWithNoun}`
        : `[Regional Sign-Up Alert] New ${userRoleFormatted} registered in the ${regionWithNoun}`;
      const regionalAdminTitle = isOrgSignup ? `New Regional Employer / Host Organization Alert` : `New Regional User Registration Alert`;
      const regionalAdminBody = `
        <p>Hello Regional Administrator,</p>
        <p>A new ${isOrgSignup ? 'employer/host partner' : 'user'} has registered within the <strong>${regionWithNoun}</strong>.</p>

        <div style="background-color: #f8fafc; padding: 20px; border-radius: 12px; margin: 24px 0; border: 1px solid #e2e8f0;">
          <h3 style="margin-top: 0; color: #0f172a; font-size: 14px; font-weight: bold; text-transform: uppercase;">New User Profile</h3>
          <p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>Full Name:</strong> ${userName}</p>
          <p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>Email Address:</strong> <a href="mailto:${userEmail}" style="color: #0094c8; text-decoration: none; font-weight: 600;">${userEmail}</a></p>
          <p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>Role:</strong> <span style="display: inline-block; background-color: #e0f2fe; color: #0369a1; padding: 2px 8px; border-radius: 4px; font-weight: bold; font-size: 12px;">${userRoleFormatted}</span></p>
          <p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>Region:</strong> ${regionWithNoun}</p>
          ${(user as any).school ? `<p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>School:</strong> ${(user as any).school}</p>` : ''}
          ${(user as any).district ? `<p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>District:</strong> ${(user as any).district}</p>` : ''}
          <p style="margin: 6px 0; font-size: 14px; color: #334155;"><strong>Registration Date:</strong> ${signupDate}</p>
        </div>

        <p style="font-size: 13px; color: #64748b;">You can access your Regional Admin Console to oversee user records and regional placements.</p>
      `;

      for (const raEmail of regionalAdminEmails) {
        if (superadminEmails.has(raEmail) || raEmail.toLowerCase() === userEmail?.toLowerCase()) continue;
        await sendNotificationEmail(raEmail, regionalAdminSubject, regionalAdminTitle, regionalAdminBody, userTenantId, category);
      }

    } catch (err) {
      console.error('WBL Notify: Error sending signup alert emails to admins:', err);
    }
  }

  // --- Real Live WBL Operations ---

  const addOpportunity = async (opportunity: Omit<Opportunity, 'id' | 'createdAt'>) => {
    const isProxy = activeUser?.role === 'superadmin' || activeUser?.role === 'regional_admin';
    const newOpp: Opportunity = {
      ...opportunity,
      id: `opp-${Date.now()}`,
      createdAt: new Date().toISOString(),
      postedByUserId: opportunity.postedByUserId || activeUser?.uid,
      postedByUserEmail: opportunity.postedByUserEmail || activeUser?.email,
      postedByName: opportunity.postedByName || activeUser?.name,
      postedByRole: opportunity.postedByRole || activeUser?.role,
      isProxyPosting: opportunity.isProxyPosting !== undefined ? opportunity.isProxyPosting : isProxy,
      createdBy: opportunity.createdBy || activeUser?.email
    };

    if (!isUsingMockKeys) {
      try {
        await setDoc(doc(db, 'opportunities', newOpp.id), cleanFirestoreData(newOpp));
        writeLog(newOpp.tenantId, activeUser?.email || 'Manager', 'POST_INTERNSHIP', `Posted opportunity "${newOpp.title}"`);
      } catch (e) {
        handleFirestoreError(e, OperationType.CREATE, `opportunities/${newOpp.id}`);
      }
    } else {
      setOpportunities(prev => [...prev, newOpp]);
      const newLog: SystemLog = {
        id: `log-${Date.now()}`,
        tenantId: newOpp.tenantId,
        userEmail: activeUser?.email || 'Manager',
        action: 'POST_INTERNSHIP',
        details: `Posted opportunity "${newOpp.title}" (Mock)`,
        timestamp: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev]);
    }

    // Send notification email to Organization, Regional Admin, and Superadmin for new postings (DO NOT email advisors)
    try {
      const oppTenant = tenants.find(t => t.id === newOpp.tenantId) || activeTenant;
      const oppBaseUrl = getTenantBaseUrl(oppTenant);
      const properOppRegion = formatRegionProperNoun(oppTenant?.name || oppTenant?.id || 'Spartanburg');
      const oppRegionNoun = formatRegionWithNoun(properOppRegion);

      // 1. Send receipt/confirmation to the Organization / Poster
      const orgEmails = new Set<string>();
      if (activeUser?.email) {
        orgEmails.add(activeUser.email.trim().toLowerCase());
      }
      if (newOpp.externalContactEmail) {
        orgEmails.add(newOpp.externalContactEmail.trim().toLowerCase());
      }
      // If posted on behalf of an organization, resolve org user email from user profiles
      if (newOpp.organizationId) {
        const orgUser = users.find(u => u.uid === newOpp.organizationId || (u.role === 'organization' && (u.orgName === newOpp.organizationName || u.name === newOpp.organizationName)));
        if (orgUser?.email) orgEmails.add(orgUser.email.trim().toLowerCase());
      } else if (newOpp.organizationName) {
        const orgUser = users.find(u => u.role === 'organization' && (u.orgName === newOpp.organizationName || u.name === newOpp.organizationName));
        if (orgUser?.email) orgEmails.add(orgUser.email.trim().toLowerCase());
      }

      for (const orgEmail of orgEmails) {
        await sendNotificationEmail(
          orgEmail,
          `[WBL Portal] Opportunity Posted Successfully: ${newOpp.title}`,
          `Opportunity Posted Successfully`,
          `
            <p>Dear ${newOpp.organizationName || 'Partner'},</p>
            <p>Thank you for submitting your Work-Based Learning opportunity. This email confirms that your opportunity has been successfully posted to the portal:</p>
            <div style="background-color: #f8fafc; border-left: 4px solid #10b981; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;">
              <strong style="font-size: 16px; color: #014c8c;">${newOpp.title}</strong><br/>
              <span style="color: #64748b; font-size: 13px;">${newOpp.organizationName} &bull; ${newOpp.location}</span>
            </div>
            <p>The regional administration in the <strong>${oppRegionNoun}</strong> and superadmin team have been notified to oversee and facilitate your listing.</p>
            <p>You can manage this posting and track student applications directly inside your portal dashboard at any time.</p>
            <p style="margin-top: 24px;"><a href="${oppBaseUrl}/opportunities" style="display: inline-block; background-color: #10b981; color: #ffffff; text-decoration: none; padding: 10px 20px; font-weight: 700; border-radius: 8px; font-size: 14px;">Manage Your Opportunity</a></p>
          `,
          newOpp.tenantId
        ).catch(err => console.warn('Silent issue on dispatching opportunity receipt email to organization:', err));
      }

      // 2. Build Superadmin emails
      const superadminEmails = new Set<string>();
      let anyAdminFound = false;

      users.filter(u => u.role === 'superadmin' && u.email).forEach(u => {
        anyAdminFound = true;
        if (shouldUserReceiveNotification(u, 'email', 'opportunityCreatedAlerts')) {
          superadminEmails.add(u.email!.trim().toLowerCase());
        }
      });

      if (!isUsingMockKeys && isFirebaseActive) {
        try {
          const saSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'superadmin')));
          saSnap.forEach(docSnap => {
            const u = docSnap.data() as UserProfile;
            if (u.email) {
              anyAdminFound = true;
              if (shouldUserReceiveNotification(u, 'email', 'opportunityCreatedAlerts')) {
                superadminEmails.add(u.email.trim().toLowerCase());
              }
            }
          });
        } catch (err) {
          console.warn('WBL Notify: Error fetching superadmins for opportunity alert:', err);
        }
      }

      // 3. Build Regional Admin emails for newOpp.tenantId
      const regionalAdminEmails = new Set<string>();
      users.filter(u => u.role === 'regional_admin' && u.email && (u.tenantId === newOpp.tenantId || u.region === properOppRegion || u.tenantId === oppTenant?.id)).forEach(u => {
        anyAdminFound = true;
        if (shouldUserReceiveNotification(u, 'email', 'opportunityCreatedAlerts')) {
          regionalAdminEmails.add(u.email!.trim().toLowerCase());
        }
      });

      if (!isUsingMockKeys && isFirebaseActive && newOpp.tenantId !== 'global') {
        try {
          const raSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'regional_admin'), where('tenantId', '==', newOpp.tenantId)));
          raSnap.forEach(docSnap => {
            const u = docSnap.data() as UserProfile;
            if (u.email) {
              anyAdminFound = true;
              if (shouldUserReceiveNotification(u, 'email', 'opportunityCreatedAlerts')) {
                regionalAdminEmails.add(u.email.trim().toLowerCase());
              }
            }
          });
        } catch (err) {
          console.warn('WBL Notify: Error fetching regional admins for opportunity alert:', err);
        }
      }

      // Fall back only if no admin accounts exist in system
      if (superadminEmails.size === 0 && regionalAdminEmails.size === 0 && !anyAdminFound) {
        superadminEmails.add('joshua@losonova.com');
      }

      // 4. Send alert to Superadmins
      for (const saEmail of superadminEmails) {
        if (orgEmails.has(saEmail)) continue; // avoid duplicate if superadmin was the poster
        await sendNotificationEmail(
          saEmail,
          `[New Opportunity Posted] ${newOpp.title} at ${newOpp.organizationName} - ${oppRegionNoun}`,
          `New Opportunity Posted: ${newOpp.title}`,
          `
            <p>Hello Administrator,</p>
            <p>A new Work-Based Learning opportunity has been posted in the <strong>${oppRegionNoun}</strong>:</p>
            <div style="background-color: #f8fafc; border-left: 4px solid #0094c8; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;">
              <strong style="font-size: 16px; color: #0f172a;">${newOpp.title}</strong><br/>
              <span style="color: #64748b; font-size: 13px;">${newOpp.organizationName} &bull; ${newOpp.location}</span><br/>
              <span style="color: #0369a1; font-size: 12px; font-weight: 600;">Region: ${oppRegionNoun}</span>
            </div>
            <p>You can review and manage this posting in the Superadmin Console.</p>
            <p style="margin-top: 24px;"><a href="${oppBaseUrl}/opportunities" style="display: inline-block; background-color: #0094c8; color: #ffffff; text-decoration: none; padding: 10px 20px; font-weight: 700; border-radius: 8px; font-size: 14px;">Review Opportunity in Portal</a></p>
          `,
          'global',
          'opportunityCreatedAlerts'
        ).catch(err => console.warn('Silent issue on dispatching opportunity alert to superadmin:', err));
      }

      // 5. Send alert to Regional Admins
      for (const raEmail of regionalAdminEmails) {
        if (superadminEmails.has(raEmail) || orgEmails.has(raEmail)) continue;
        await sendNotificationEmail(
          raEmail,
          `[New Opportunity Posted] ${newOpp.title} at ${newOpp.organizationName} - ${oppRegionNoun}`,
          `New Opportunity Posted in ${oppRegionNoun}`,
          `
            <p>Hello Regional Administrator,</p>
            <p>A new Work-Based Learning opportunity has been posted in your region (<strong>${oppRegionNoun}</strong>):</p>
            <div style="background-color: #f8fafc; border-left: 4px solid #0094c8; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;">
              <strong style="font-size: 16px; color: #0f172a;">${newOpp.title}</strong><br/>
              <span style="color: #64748b; font-size: 13px;">${newOpp.organizationName} &bull; ${newOpp.location}</span>
            </div>
            <p>Please sign in to your Regional Admin Console to review the details and manage student placement routing for this posting.</p>
            <p style="margin-top: 24px;"><a href="${oppBaseUrl}/opportunities" style="display: inline-block; background-color: #0094c8; color: #ffffff; text-decoration: none; padding: 10px 20px; font-weight: 700; border-radius: 8px; font-size: 14px;">Review Regional Opportunity</a></p>
          `,
          newOpp.tenantId,
          'opportunityCreatedAlerts'
        ).catch(err => console.warn('Silent issue on dispatching opportunity alert to regional admin:', err));
      }

      // Note: Advisors are not sent an email alert for every posting (restricted to Organization, Regional Admin, and Superadmin only).
    } catch (notifyErr) {
      console.warn('Silent issue on dispatching opportunity email alerts:', notifyErr);
    }
  };

  const recommendStudentToOpportunity = async (studentId: string, opportunityId: string) => {
    if (!activeUser || !['advisor', 'parent'].includes(activeUser.role)) return;
    const student = users.find(u => u.uid === studentId);
    const opp = opportunities.find(o => o.id === opportunityId);
    if (!student || !opp) return;

    // Check if student is allowed to apply based on target schools
    if (opp.targetSchools && opp.targetSchools.length > 0) {
      const studentSchoolNorm = (student.school || '').toLowerCase().trim();
      const studentSchoolIdNorm = (student.schoolId || '').toLowerCase().trim();
      const matchesSchool = opp.targetSchools.some(ts => {
        const tNorm = (ts || '').toLowerCase().trim();
        return tNorm === studentSchoolNorm || tNorm === studentSchoolIdNorm;
      });
      if (!matchesSchool) {
        console.warn(`Student ${student.name} cannot apply to ${opp.title} (school restricted). Recommendation blocked.`);
        return;
      }
    }

    const newRec: Recommendation = {
      id: `rec-${Date.now()}-${studentId}`,
      tenantId: student.tenantId || activeTenant.id,
      opportunityId,
      opportunityTitle: opp.title,
      studentId: student.uid,
      studentName: student.name,
      advisorId: activeUser.uid,
      advisorName: activeUser.name,
      recommendedAt: new Date().toISOString(),
    };

    if (!isUsingMockKeys) {
      try {
        await setDoc(doc(db, 'recommendations', newRec.id), newRec);
        writeLog(student.tenantId || activeTenant.id, activeUser.email, 'RECOMMEND_STUDENT', `Recommended ${student.name} for ${opp.title}`);
      } catch (e) {
        handleFirestoreError(e, OperationType.CREATE, `recommendations/${newRec.id}`);
      }
    } else {
      setRecommendations(prev => [...prev, newRec]);
      const newLog: SystemLog = {
        id: `log-${Date.now()}`,
        tenantId: student.tenantId || activeTenant.id,
        userEmail: activeUser.email,
        action: 'RECOMMEND_STUDENT',
        details: `Recommended ${student.name} for ${opp.title} (Mock)`,
        timestamp: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev]);
    }

    // Trigger automated notification dispatch
    try {
      const recommenderType = activeUser.role === 'parent' ? 'parent' : 'advisor';
      await sendManualPortalMessage(
        student.email,
        `Career Recommendation: ${opp.title}`,
        `Career Opportunity Recommendation`,
        `
          <p>Dear ${student.name},</p>
          <p>Your ${recommenderType}, <strong>${activeUser.name}</strong>, has reviewed your career pathway and recommends that you apply for the <strong>${opp.title}</strong> position at <strong>${opp.organizationName}</strong>.</p>
          <div style="background-color: #f1f5f9; padding: 12px; border-radius: 8px; margin: 16px 0; font-size: 13px;">
            <strong>${activeUser.role === 'parent' ? 'Parent' : 'Advisor'} Note:</strong><br/>
            <p style="margin: 6px 0 0; color: #475569; font-style: italic;">"I believe this opportunity is a great fit for your goals and interests. Please review the posting details and submit your application if you are interested."</p>
          </div>
          <p>You can review this opportunity and apply directly through your Work-Based Learning Portal dashboard.</p>
        `
      );
    } catch (notifyErr) {
      console.warn('Silent issue on dispatching recommendation alerts:', notifyErr);
    }
  };

  const updateOpportunity = async (id: string, opportunity: Partial<Opportunity>) => {
    const cleanedOpp = cleanFirestoreData(opportunity);
    if (!isUsingMockKeys) {
      try {
        const docRef = doc(db, 'opportunities', id);
        await updateDoc(docRef, cleanedOpp);
        setOpportunities(prev => prev.map(o => o.id === id ? { ...o, ...opportunity } : o));
        writeLog(opportunity.tenantId || activeTenant.id, activeUser?.email || 'Manager', 'UPDATE_INTERNSHIP', `Updated opportunity "${opportunity.title}"`);
      } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `opportunities/${id}`);
      }
    } else {
      setOpportunities(prev => prev.map(o => o.id === id ? { ...o, ...opportunity } : o));
      const newLog: SystemLog = {
        id: `log-${Date.now()}`,
        tenantId: opportunity.tenantId || activeTenant.id,
        userEmail: activeUser?.email || 'Manager',
        action: 'UPDATE_INTERNSHIP',
        details: `Updated opportunity "${opportunity.title}" (Mock)`,
        timestamp: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev]);
    }
  };

  const applyToOpportunity = async (opportunityId: string, coverLetter: string, resumeSummary: string) => {
    if (!activeUser) return;
    if (checkProxyActionGuard('Application submission')) return;

    // 1. Enforce FERPA Consent
    const ferpaGranted = typeof activeUser.ferpaConsent === 'object'
      ? activeUser.ferpaConsent?.granted === true
      : activeUser.ferpaConsent === true;
    if (!ferpaGranted) {
      throw new Error("FERPA consent is required before submitting applications. Please complete FERPA authorization in your profile.");
    }

    // 2. Enforce Advisor/Admin Verification for Students
    const isStudentDenied = !!(activeUser.advisorDenied || activeUser.verificationStatus === 'denied');
    if (activeUser.role === 'student' && isStudentDenied) {
      throw new Error("Student verification denied. Your account profile was marked as invalid or denied by your school advisor. Please contact your school administrator.");
    }

    const isStudentVerified = !!(activeUser.advisorVerified || activeUser.verified);
    if (activeUser.role === 'student' && !isStudentVerified) {
      throw new Error("Advisor verification required. Your profile must be verified by a school advisor or administrator before applying for opportunities.");
    }

    const opp = opportunities.find(o => o.id === opportunityId);
    if (!opp) return;

    const isProxy = opp.isProxyPosting || opp.organizationId === 'external-partner' || opp.postedByRole === 'superadmin' || opp.postedByRole === 'regional_admin';

    const newApp: Application = {
      id: `app-${Date.now()}`,
      tenantId: opp.tenantId || activeTenant.id,
      opportunityId,
      opportunityTitle: opp.title,
      organizationName: opp.organizationName,
      studentId: activeUser.uid,
      studentName: activeUser.name,
      studentEmail: activeUser.email,
      status: 'pending',
      appliedAt: new Date().toISOString(),
      coverLetter,
      resumeSummary,
      postedByUserId: opp.postedByUserId,
      postedByUserEmail: opp.postedByUserEmail,
      postedByRole: opp.postedByRole,
      isProxyPosting: isProxy,
      externalContactEmail: opp.externalContactEmail || undefined
    };

    if (!isUsingMockKeys) {
      try {
        await setDoc(doc(db, 'applications', newApp.id), cleanFirestoreData(newApp));
        writeLog(opp.tenantId, activeUser.email, 'SUBMIT_APPLICATION', `Applied to ${opp.title} opportunity.`);
      } catch (e) {
        handleFirestoreError(e, OperationType.CREATE, `applications/${newApp.id}`);
      }
    } else {
      setApplications(prev => [...prev, newApp]);
      const newLog: SystemLog = {
        id: `log-${Date.now()}`,
        tenantId: opp.tenantId,
        userEmail: activeUser.email,
        action: 'SUBMIT_APPLICATION',
        details: `Applied to ${opp.title} opportunity. (Mock)`,
        timestamp: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev]);
    }

    // Trigger automated notification dispatch
    try {
      // 1. Send receipts to the applicant student
      await sendNotificationEmail(
        activeUser.email,
        `[Application Received] Your application for ${opp.title}`,
        `Application Submitted Successfully`,
        `
          <p>Dear ${activeUser.name},</p>
          <p>Your application for the <strong>${opp.title}</strong> position at <strong>${opp.organizationName}</strong> has been received.</p>
          <div style="background-color: #f1f5f9; padding: 12px; border-radius: 8px; margin: 16px 0; font-size: 13px;">
            <strong>Your Cover Letter / Summary:</strong><br/>
            <p style="margin: 6px 0 0; color: #475569; font-style: italic;">"${coverLetter || resumeSummary || 'No cover letter attached.'}"</p>
          </div>
          <p>The host organization and regional coordinators will review your application soon. You will receive an automated email notification when a decision is made or when an interview is scheduled.</p>
        `,
        opp.tenantId
      );

      // 2. Alert the representative / recruiters or the Proxy Admin (Regional Admin or Superadmin) who posted it
      const oppTenant = tenants.find(t => t.id === opp.tenantId) || activeTenant;
      const targetBaseUrl = getTenantBaseUrl(oppTenant);
      const properOppRegion = formatRegionProperNoun(oppTenant?.name || oppTenant?.id || 'Spartanburg');
      const oppRegionNoun = formatRegionWithNoun(properOppRegion);

      // Check registered organization accounts in the system
      const registeredOrgUsers = users.filter(u => 
        u.role === 'organization' && 
        (u.uid === opp.organizationId || 
         (u.orgName && u.orgName.toLowerCase().trim() === opp.organizationName.toLowerCase().trim()) || 
         (u.name && u.name.toLowerCase().trim() === opp.organizationName.toLowerCase().trim()))
      );
      const isOrgRegisteredUser = registeredOrgUsers.length > 0 && opp.organizationId !== 'external-partner';

      const recipientEmails = new Set<string>();

      if (isOrgRegisteredUser) {
        registeredOrgUsers.forEach(u => { 
          if (u.email) recipientEmails.add(u.email.trim().toLowerCase()); 
        });
      }

      // If the organization is NOT a registered portal user, OR if proxy-posted by a regional admin or superadmin:
      // Route application notifications directly to the regional admin or superadmin that posted it!
      const shouldNotifyPoster = isProxy || !isOrgRegisteredUser;

      if (shouldNotifyPoster) {
        if (opp.postedByUserEmail) {
          recipientEmails.add(opp.postedByUserEmail.trim().toLowerCase());
        }
        if (opp.postedByUserId) {
          const posterUser = users.find(u => u.uid === opp.postedByUserId);
          if (posterUser?.email) recipientEmails.add(posterUser.email.trim().toLowerCase());
        }
        if (opp.createdBy && opp.createdBy.includes('@')) {
          recipientEmails.add(opp.createdBy.trim().toLowerCase());
        }

        // Fallback: If no specific poster email was recorded on older proxy/external records, route to Regional Admin(s) of this tenant & Superadmin
        if (recipientEmails.size === 0) {
          const regionalAdmins = users.filter(u => u.role === 'regional_admin' && (u.tenantId === opp.tenantId || u.region === oppTenant?.name));
          regionalAdmins.forEach(ra => { if (ra.email) recipientEmails.add(ra.email.trim().toLowerCase()); });
          recipientEmails.add('joshua@losonova.com');
        }
      }

      // Include external contact email if specified on the posting
      if (opp.externalContactEmail && opp.externalContactEmail.includes('@')) {
        recipientEmails.add(opp.externalContactEmail.trim().toLowerCase());
      }

      for (const email of recipientEmails) {
        const isPosterAdmin = email === opp.postedByUserEmail?.toLowerCase() || 
          email === 'joshua@losonova.com' || 
          users.some(u => u.email?.toLowerCase() === email && (u.role === 'superadmin' || u.role === 'regional_admin'));

        await sendNotificationEmail(
          email,
          `[New WBL Application] ${activeUser.name} applied for ${opp.title} (${opp.organizationName})`,
          `New Student Application Received`,
          `
            <p>Dear ${isPosterAdmin ? 'Administrator / Proxy Manager' : 'Hiring Team'},</p>
            <p>You have received a new student application for your posting, <strong>${opp.title}</strong>:</p>
            <div style="background-color: #f8fafc; border-left: 4px solid #0094c8; padding: 14px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;">
              <strong>Student:</strong> ${activeUser.name}<br/>
              <strong>Email:</strong> <a href="mailto:${activeUser.email}">${activeUser.email}</a><br/>
              <strong>Position:</strong> ${opp.title}<br/>
              <strong>Organization:</strong> ${opp.organizationName}${!isOrgRegisteredUser ? ' <span style="color: #64748b; font-size: 12px;">(External Partner Organization)</span>' : ''}<br/>
              <span style="color: #0369a1; font-size: 12px; font-weight: 600;">Region: ${oppRegionNoun}</span>
            </div>
            ${coverLetter ? `
              <div style="background-color: #f1f5f9; padding: 12px; border-radius: 8px; margin: 16px 0; font-size: 13px;">
                <strong>Cover Letter / Candidate Statement:</strong>
                <p style="margin: 6px 0 0; color: #334155; font-style: italic; white-space: pre-line;">"${coverLetter}"</p>
              </div>
            ` : ''}
            ${resumeSummary ? `
              <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; padding: 12px; border-radius: 8px; margin: 16px 0; font-size: 13px;">
                <strong>Resume / Qualifications Summary:</strong>
                <p style="margin: 6px 0 0; color: #334155; font-style: italic; white-space: pre-line;">"${resumeSummary}"</p>
              </div>
            ` : ''}
            ${isPosterAdmin && !isOrgRegisteredUser ? `
              <div style="font-size: 13px; color: #0369a1; background: #e0f2fe; border: 1px solid #bae6fd; padding: 10px 14px; border-radius: 6px; margin: 16px 0;">
                ℹ️ <strong>Proxy Posting Notification:</strong> Because <strong>${opp.organizationName}</strong> is not an active portal user account, this student application notification has been directly routed to you as the Regional Admin / Superadmin who created this proxy posting.
              </div>
            ` : ''}
            <p>Please sign in to the Work-Based Learning Portal to review credentials, update the application status, or schedule an interview with the student.</p>
            <p style="margin-top: 24px;"><a href="${targetBaseUrl}/applications" style="display: inline-block; background-color: #0094c8; color: #ffffff; text-decoration: none; padding: 10px 20px; font-weight: 700; border-radius: 8px; font-size: 14px;">View Student Application</a></p>
          `,
          opp.tenantId
        );
      }
    } catch (err) {
      console.warn('WBL Notify: Silent error on dispatching application submission emails:', err);
    }
  };

  const reviewApplication = async (applicationId: string, status: 'accepted' | 'rejected', agreement?: TrainingAgreement, declineLetter?: string) => {
    if (checkProxyActionGuard('Application review decision')) return;
    if (!isUsingMockKeys) {
      try {
        const docRef = doc(db, 'applications', applicationId);
        const updates: any = { status };
        if (declineLetter !== undefined) {
          updates.declineLetter = declineLetter;
        }
        if (status === 'accepted' || status === 'rejected') {
          updates.decisionDate = new Date().toISOString();
        }
        await updateDoc(docRef, updates);
        
        const targetApp = applications.find(a => a.id === applicationId);
        if (targetApp) {
          writeLog(targetApp.tenantId, activeUser?.email || 'organization', 'REVIEW_APPLICATION', `Reviewed application of ${targetApp.studentName}: ${status}`);
          if (status === 'accepted') {
            // Find regional defaults or default advisors to assign
            const defaultAdvisor = users.find(u => (u.role === 'regional_admin' || u.role === 'advisor') && u.tenantId === targetApp.tenantId) || users.find(u => u.role === 'regional_admin' || u.role === 'advisor');
            await createPlacement(
              targetApp.opportunityId,
              targetApp.studentId,
              defaultAdvisor?.email || 'mentor.mary@example.com',
              defaultAdvisor?.name || 'Mary Vance',
              agreement
            );
          } else if (status === 'rejected') {
            const q = query(collection(db, 'placements'), where('opportunityId', '==', targetApp.opportunityId), where('studentId', '==', targetApp.studentId));
            const snap = await getDocs(q);
            for (const docSnap of snap.docs) {
              const placementId = docSnap.id;
              await deleteDoc(docSnap.ref);
              const tq = query(collection(db, 'tasks'), where('placementId', '==', placementId));
              const tsnap = await getDocs(tq);
              for (const tdoc of tsnap.docs) {
                await deleteDoc(tdoc.ref);
              }
            }
          }
        }
      } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `applications/${applicationId}`);
      }
    } else {
      const decisionDate = (status === 'accepted' || status === 'rejected') ? new Date().toISOString() : undefined;
      setApplications(prev => prev.map(app => app.id === applicationId ? { ...app, status, declineLetter, decisionDate } : app));
      const targetApp = applications.find(a => a.id === applicationId);
      if (targetApp) {
        const newLog: SystemLog = {
          id: `log-${Date.now()}`,
          tenantId: targetApp.tenantId,
          userEmail: activeUser?.email || 'organization',
          action: 'REVIEW_APPLICATION',
          details: `Reviewed application of ${targetApp.studentName}: ${status} (Mock)`,
          timestamp: new Date().toISOString()
        };
        setLogs(prev => [newLog, ...prev]);
        if (status === 'accepted') {
          const defaultAdvisor = users.find(u => (u.role === 'regional_admin' || u.role === 'advisor') && u.tenantId === targetApp.tenantId) || users.find(u => u.role === 'regional_admin' || u.role === 'advisor');
          await createPlacement(
            targetApp.opportunityId,
            targetApp.studentId,
            defaultAdvisor?.email || 'mentor.mary@example.com',
            defaultAdvisor?.name || 'Mary Vance',
            agreement
          );
        } else if (status === 'rejected') {
          const matchedPlacements = placements.filter(p => p.opportunityId === targetApp.opportunityId && p.studentId === targetApp.studentId);
          const placementIds = matchedPlacements.map(p => p.id);
          setPlacements(prev => prev.filter(p => !(p.opportunityId === targetApp.opportunityId && p.studentId === targetApp.studentId)));
          setTasks(prev => prev.filter(t => !placementIds.includes(t.placementId)));
        }
      }
    }

    // Notify student of Application review outcome
    try {
      const targetApp = applications.find(a => a.id === applicationId);
      if (targetApp) {
        const appTenant = tenants.find(t => t.id === targetApp.tenantId) || activeTenant;
        const targetBaseUrl = getTenantBaseUrl(appTenant);
        await sendNotificationEmail(
          targetApp.studentEmail,
          `[Application Update] Your application for ${targetApp.opportunityTitle}`,
          `Application Status Updated`,
          status === 'accepted'
            ? `
              <p>Dear ${targetApp.studentName},</p>
              <p>Congratulations! Your application for <strong>${targetApp.opportunityTitle}</strong> at <strong>${targetApp.organizationName}</strong> has been <strong>ACCEPTED</strong>.</p>
              <p>An advisor is setting up your Work-Based Learning placement structure, assigning your learning targets, and scheduling your orientation.</p>
              <p>Please sign in to your portal to view your placement details.</p>
              <p style="margin-top: 24px;"><a href="${targetBaseUrl}/placements" style="display: inline-block; background-color: #0094c8; color: #ffffff; text-decoration: none; padding: 10px 20px; font-weight: 700; border-radius: 8px; font-size: 14px;">View My Placements</a></p>
            `
            : `
              <p>Dear ${targetApp.studentName},</p>
              <p>Thank you for submitting your application for <strong>${targetApp.opportunityTitle}</strong> at <strong>${targetApp.organizationName}</strong>.</p>
              <p>The host organization has reviewed all candidate submissions and is not moving forward with your application at this time.</p>
              <p>We encourage you to explore other available opportunities in the portal and apply for positions that match your career interests.</p>
              <p style="margin-top: 24px;"><a href="${targetBaseUrl}/opportunities" style="display: inline-block; background-color: #0094c8; color: #ffffff; text-decoration: none; padding: 10px 20px; font-weight: 700; border-radius: 8px; font-size: 14px;">Browse Available Opportunities</a></p>
            `
        );
      }
    } catch (err) {
      console.warn('WBL Notify: Silent error on dispatching application review decision emails:', err);
    }
  };

  const scheduleInterview = async (interview: Omit<Interview, 'id'>) => {
    const newInterview: Interview = {
      ...interview,
      id: `int-${Date.now()}`
    };

    const cleanedInterview = cleanFirestoreData(newInterview);

    if (!isUsingMockKeys) {
      try {
        await setDoc(doc(db, 'interviews', newInterview.id), cleanedInterview);
        await updateDoc(doc(db, 'applications', interview.applicationId), {
          interviewScheduled: true
        });
        writeLog(interview.tenantId, activeUser?.email || 'organization', 'SCHEDULE_INTERVIEW', `Scheduled interview for ${interview.studentName}.`);
      } catch (e) {
        handleFirestoreError(e, OperationType.CREATE, `interviews/${newInterview.id}`);
      }
    } else {
      setInterviews(prev => [...prev, newInterview]);
      setApplications(prev => prev.map(a => a.id === interview.applicationId ? { ...a, interviewScheduled: true } : a));
      const newLog: SystemLog = {
        id: `log-${Date.now()}`,
        tenantId: interview.tenantId,
        userEmail: activeUser?.email || 'organization',
        action: 'SCHEDULE_INTERVIEW',
        details: `Scheduled interview for ${interview.studentName}. (Mock)`,
        timestamp: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev]);
    }

    // Notify student about upcoming interview
    try {
      const studentUser = users.find(u => u.uid === interview.studentId);
      if (studentUser) {
        await sendNotificationEmail(
          studentUser.email,
          `[Interview Invitation] ${interview.opportunityTitle} with ${interview.organizationName}`,
          `Interview Scheduled`,
          `
            <p>Dear ${interview.studentName},</p>
            <p>An interview has been scheduled for your application with <strong>${interview.organizationName}</strong>:</p>
            <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; padding: 16px; margin: 16px 0; border-radius: 8px;">
              <strong>Opportunity:</strong> ${interview.opportunityTitle}<br/>
              <strong>Date:</strong> ${interview.date}<br/>
              <strong>Time:</strong> ${formatTime12HourEST(interview.time)}<br/>
              <strong>Interviewer:</strong> ${interview.person}<br/>
              <strong>Format / Location:</strong> ${interview.mode.toUpperCase()}${interview.link ? ` (<a href="${interview.link}">Join Video Meeting</a>)` : ''}${interview.address ? ` at ${interview.address}` : ''}
            </div>
            <p>Please sign in to your portal to review your interview details and access any required preparation materials.</p>
          `
        );
      }

      // Also notify external contact email if specified on the opportunity
      const opp = opportunities.find(o => o.id === interview.opportunityId);
      const contactEmail = opp?.externalContactEmail;
      if (contactEmail && contactEmail.includes('@')) {
        await sendNotificationEmail(
          contactEmail.trim().toLowerCase(),
          `[Interview Confirmed] ${interview.studentName} for ${interview.opportunityTitle}`,
          `Candidate Interview Scheduled`,
          `
            <p>Dear ${interview.organizationName || 'Opportunity Contact'},</p>
            <p>An interview has been scheduled with candidate <strong>${interview.studentName}</strong> for your opportunity <strong>${interview.opportunityTitle}</strong>:</p>
            <div style="background-color: #f8fafc; border-left: 4px solid #0094c8; padding: 16px; margin: 16px 0; border-radius: 0 8px 8px 0;">
              <strong>Candidate:</strong> ${interview.studentName} (${studentUser?.email || ''})<br/>
              <strong>Date:</strong> ${interview.date}<br/>
              <strong>Time:</strong> ${formatTime12HourEST(interview.time)}<br/>
              <strong>Interviewer:</strong> ${interview.person}<br/>
              <strong>Format / Location:</strong> ${interview.mode.toUpperCase()}${interview.link ? ` (<a href="${interview.link}">Meeting Link</a>)` : ''}${interview.address ? ` at ${interview.address}` : ''}
            </div>
            <p>Candidate Contact: <a href="mailto:${studentUser?.email || ''}">${studentUser?.email || ''}</a></p>
          `,
          interview.tenantId
        ).catch(err => console.warn('Silent issue on dispatching interview email to opportunity contact:', err));
      }
    } catch (err) {
      console.warn('WBL Notify: Silent error on dispatching interview scheduled emails:', err);
    }
  };

  const cancelInterview = async (interviewId: string, applicationId: string) => {
    let targetInterview = interviews.find(i => i.id === interviewId);
    
    if (!isUsingMockKeys) {
      try {
        if (!targetInterview) {
          const docSnap = await getDoc(doc(db, 'interviews', interviewId));
          if (docSnap.exists()) {
            targetInterview = { id: docSnap.id, ...docSnap.data() } as Interview;
          }
        }
        await deleteDoc(doc(db, 'interviews', interviewId));
        await updateDoc(doc(db, 'applications', applicationId), {
          interviewScheduled: false
        });
        writeLog(activeTenant.id, activeUser?.email || 'organization', 'CANCEL_INTERVIEW', `Cancelled interview ${interviewId}.`);
      } catch (e) {
        handleFirestoreError(e, OperationType.DELETE, `interviews/${interviewId}`);
      }
    } else {
      setInterviews(prev => prev.filter(i => i.id !== interviewId));
      setApplications(prev => prev.map(a => a.id === applicationId ? { ...a, interviewScheduled: false } : a));
      const newLog: SystemLog = {
        id: `log-${Date.now()}`,
        tenantId: activeTenant.id,
        userEmail: activeUser?.email || 'organization',
        action: 'CANCEL_INTERVIEW',
        details: `Cancelled interview ${interviewId}. (Mock)`,
        timestamp: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev]);
    }

    if (targetInterview) {
      try {
        const studentUser = users.find(u => u.uid === targetInterview?.studentId);
        if (studentUser) {
          await sendNotificationEmail(
            studentUser.email,
            `[Interview Cancelled] ${targetInterview.opportunityTitle} with ${targetInterview.organizationName}`,
            `Interview Cancelled`,
            `
              <p>Dear ${targetInterview.studentName},</p>
              <p>Your scheduled interview for <strong>${targetInterview.opportunityTitle}</strong> with <strong>${targetInterview.organizationName}</strong> has been cancelled.</p>
              <p>Please sign in to the portal for further details or to contact the organization.</p>
            `
          );
        }

        const opp = opportunities.find(o => o.id === targetInterview?.opportunityId);
        const contactEmail = opp?.externalContactEmail;
        if (contactEmail && contactEmail.includes('@')) {
          await sendNotificationEmail(
            contactEmail.trim().toLowerCase(),
            `[Interview Cancelled] ${targetInterview?.opportunityTitle} with ${targetInterview?.studentName}`,
            `Interview Cancelled`,
            `
              <p>Hello,</p>
              <p>The interview scheduled with <strong>${targetInterview?.studentName}</strong> for <strong>${targetInterview?.opportunityTitle}</strong> on <strong>${targetInterview?.date}</strong> has been cancelled.</p>
            `,
            targetInterview?.tenantId
          ).catch(err => console.warn('Silent issue on dispatching interview cancellation email to opportunity contact:', err));
        }
      } catch (err) {
        console.warn('WBL Notify: Silent error on dispatching interview cancelled email:', err);
      }
    }
  };

  const rescheduleInterview = async (interviewId: string, updates: Partial<Interview>) => {
    const cleanUpdates = cleanFirestoreData(updates);

    let targetInterview = interviews.find(i => i.id === interviewId);

    if (!isUsingMockKeys) {
      try {
        if (!targetInterview) {
          const docSnap = await getDoc(doc(db, 'interviews', interviewId));
          if (docSnap.exists()) {
            targetInterview = { id: docSnap.id, ...docSnap.data() } as Interview;
          }
        }
        await updateDoc(doc(db, 'interviews', interviewId), cleanUpdates);
        writeLog(activeTenant.id, activeUser?.email || 'organization', 'RESCHEDULE_INTERVIEW', `Rescheduled interview ${interviewId}.`);
      } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `interviews/${interviewId}`);
      }
    } else {
      setInterviews(prev => prev.map(i => i.id === interviewId ? { ...i, ...cleanUpdates } : i));
      const newLog: SystemLog = {
        id: `log-${Date.now()}`,
        tenantId: activeTenant.id,
        userEmail: activeUser?.email || 'organization',
        action: 'RESCHEDULE_INTERVIEW',
        details: `Rescheduled interview ${interviewId}. (Mock)`,
        timestamp: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev]);
    }

    if (targetInterview) {
      const mergedInterview = { ...targetInterview, ...cleanUpdates };
      try {
        const studentUser = users.find(u => u.uid === mergedInterview.studentId);
        if (studentUser) {
          await sendNotificationEmail(
            studentUser.email,
            `[Interview Rescheduled] ${mergedInterview.opportunityTitle} with ${mergedInterview.organizationName}`,
            `Interview Rescheduled`,
            `
              <p>Dear ${mergedInterview.studentName},</p>
              <p>Your scheduled interview for <strong>${mergedInterview.opportunityTitle}</strong> with <strong>${mergedInterview.organizationName}</strong> has been rescheduled:</p>
              <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; padding: 16px; margin: 16px 0; border-radius: 8px;">
                <strong>Date:</strong> ${mergedInterview.date}<br/>
                <strong>Time:</strong> ${formatTime12HourEST(mergedInterview.time)}<br/>
                <strong>Interviewer:</strong> ${mergedInterview.person}<br/>
                <strong>Format / Location:</strong> ${mergedInterview.mode.toUpperCase()}${mergedInterview.link ? ` (<a href="${mergedInterview.link}">Join Video Meeting</a>)` : ''}${mergedInterview.address ? ` at ${mergedInterview.address}` : ''}
              </div>
              <p>Please sign in to the portal to confirm your updated schedule.</p>
            `
          );
        }
      } catch (err) {
        console.warn('WBL Notify: Silent error on dispatching interview rescheduled email:', err);
      }
    }
  };

  const updateInterviewNotesAndScore = async (interviewId: string, notes: string, score?: number) => {
    const updates: Partial<Interview> = {
      orgNotes: notes,
      ...(score !== undefined ? { orgScore: score } : {})
    };
    const cleanUpdates = cleanFirestoreData(updates);

    if (!isUsingMockKeys) {
      try {
        await updateDoc(doc(db, 'interviews', interviewId), cleanUpdates);
        writeLog(activeTenant.id, activeUser?.email || 'organization', 'UPDATE_INTERVIEW_NOTES', `Updated notes and score for interview ${interviewId}.`);
      } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `interviews/${interviewId}`);
      }
    }

    setInterviews(prev => prev.map(i => i.id === interviewId ? { ...i, ...cleanUpdates } : i));
  };

  const getClosestWeekdayMidpoint = (startStr: string, endStr: string): string => {
    try {
      const start = new Date(startStr);
      const end = new Date(endStr);
      const tStart = start.getTime();
      const tEnd = end.getTime();
      if (isNaN(tStart) || isNaN(tEnd) || tEnd <= tStart) {
        const fallback = new Date(Date.now() + 45 * 24 * 60 * 60 * 1000);
        return fallback.toISOString().split('T')[0];
      }
      const midTime = tStart + (tEnd - tStart) / 2;
      const midDate = new Date(midTime);
      const day = midDate.getDay(); // 0 = Sunday, 6 = Saturday

      if (day !== 0 && day !== 6) {
        return midDate.toISOString().split('T')[0];
      }

      const offsets = [1, -1, 2, -2, 3, -3, 4, -4];
      for (const offset of offsets) {
        const candidate = new Date(midTime + offset * 24 * 60 * 60 * 1000);
        const candDay = candidate.getDay();
        if (candDay !== 0 && candDay !== 6) {
          return candidate.toISOString().split('T')[0];
        }
      }
      return midDate.toISOString().split('T')[0];
    } catch (err) {
      const fallback = new Date(Date.now() + 45 * 24 * 60 * 60 * 1000);
      return fallback.toISOString().split('T')[0];
    }
  };

  const createPlacement = async (oppId: string, studentId: string, mentorEmail: string, mentorName: string, agreement?: TrainingAgreement) => {
    let opp = opportunities.find(o => o.id === oppId);
    let student = users.find(u => u.uid === studentId);

    // Fetch student from Firestore if missing from filtered local users state
    if (!student && !isUsingMockKeys) {
      try {
        const studentDoc = await getDoc(doc(db, 'users', studentId));
        if (studentDoc.exists()) {
          student = { uid: studentDoc.id, ...studentDoc.data() } as UserProfile;
        }
      } catch (e) {
        console.warn("Could not fetch student directly in createPlacement:", e);
      }
    }

    // Fetch opportunity from Firestore if missing from memory
    if (!opp && !isUsingMockKeys) {
      try {
        const oppDoc = await getDoc(doc(db, 'opportunities', oppId));
        if (oppDoc.exists()) {
          opp = { id: oppDoc.id, ...oppDoc.data() } as Opportunity;
        }
      } catch (e) {
        console.warn("Could not fetch opportunity directly in createPlacement:", e);
      }
    }

    // Attempt to locate application for fallbacks
    const matchedApp = applications.find(a => a.studentId === studentId && a.opportunityId === oppId);

    // Define placement variables with comprehensive fallback guarantees
    const tenantId = opp?.tenantId || matchedApp?.tenantId || activeTenant.id || 'all';
    const opportunityTitle = opp?.title || matchedApp?.opportunityTitle || 'Work-Based Learning Placement';
    const organizationId = opp?.organizationId || '';
    const organizationName = agreement?.worksiteName || opp?.organizationName || matchedApp?.organizationName || 'Employer Worksite';
    const studentName = student?.name || matchedApp?.studentName || 'Student Candidate';
    const studentEmail = student?.email || matchedApp?.studentEmail || '';
    const supervisorTitle = opp?.supervisorTitle || 'Internship Supervisor';
    const jobDescription = opp?.description || 'We are seeking a highly motivated...';

    const newPlacement: Placement = {
      id: `place-${Date.now()}`,
      tenantId,
      opportunityId: oppId,
      opportunityTitle,
      organizationId,
      organizationName,
      studentId,
      studentName,
      studentEmail,
      mentorId: `mentor-${Date.now()}`,
      mentorName: agreement?.supervisorName || mentorName,
      startDate: agreement?.startDate || new Date().toISOString().split('T')[0],
      endDate: agreement?.endDate || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 90 days rotation
      status: 'active',
      assignedDays: agreement?.assignedDays || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      assignedHoursTotal: agreement?.assignedHoursTotal ?? 120,
      supervisorTitle,
      registryContact: agreement?.supervisorEmail || mentorEmail,
      directLine: agreement?.supervisorPhone || '(555) 000-0000',
      eval1: {
        status: 'pending',
        scores: {},
        dueDate: getClosestWeekdayMidpoint(
          agreement?.startDate || new Date().toISOString().split('T')[0],
          agreement?.endDate || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        )
      },
      eval2: {
        status: 'pending',
        scores: {},
        dueDate: agreement?.endDate || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      },
      isRecommended: !!matchedApp?.recommendedBy,
      agreement: agreement || {
        isAlignedToIGP: undefined,
        isPaid: true,
        earnedCourseCredit: undefined,
        courseCode: '',
        hasInsurance: undefined,
        careerPathway: '',
        jobDescription,
        learningObjectives: ['1. Professionalism', '2. Communication', '3. Technical Competency']
      }
    };

    // Preseed starting curriculum milestones
    const task1: StudentTask = {
      id: `task-acc-${Date.now()}-1`,
      tenantId,
      placementId: newPlacement.id,
      studentId: studentId,
      title: 'Sign Agreement & Complete Workplace Safety Briefing',
      description: 'Review policy parameters, safety guidelines and sign documents with your mentor.',
      status: 'todo',
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      updatedAt: new Date().toISOString()
    };

    const task2: StudentTask = {
      id: `task-acc-${Date.now()}-2`,
      tenantId,
      placementId: newPlacement.id,
      studentId: studentId,
      title: 'Post Onboarding Internship Log Review',
      description: 'Draft initial target outcomes and review with supervisor.',
      status: 'todo',
      dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      updatedAt: new Date().toISOString()
    };

    if (!isUsingMockKeys) {
      try {
        await setDoc(doc(db, 'placements', newPlacement.id), cleanFirestoreData(newPlacement));
        await setDoc(doc(db, 'tasks', task1.id), cleanFirestoreData(task1));
        await setDoc(doc(db, 'tasks', task2.id), cleanFirestoreData(task2));
        writeLog(opp?.tenantId || activeTenant.id, activeUser?.email || 'Admin', 'CREATE_PLACEMENT', `Assigned placement structure for ${studentName}`);
      } catch (e) {
        handleFirestoreError(e, OperationType.CREATE, `placements/${newPlacement.id}`);
      }
    } else {
      setPlacements(prev => [...prev, newPlacement]);
      setTasks(prev => [...prev, task1, task2]);
      const newLog: SystemLog = {
        id: `log-${Date.now()}`,
        tenantId: opp?.tenantId || activeTenant.id,
        userEmail: activeUser?.email || 'Admin',
        action: 'CREATE_PLACEMENT',
        details: `Assigned placement structure for ${studentName} (Mock)`,
        timestamp: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev]);
    }

    try {
      if (studentEmail) {
        sendNotificationEmail(
          studentEmail,
          `[New Placement] You have been assigned to ${organizationName}`,
          `Work-Based Learning Placement Assigned`,
          `
            <p>Dear ${studentName},</p>
            <p>A new Work-Based Learning placement has been established for you:</p>
            <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; padding: 16px; margin: 16px 0; border-radius: 8px;">
              <strong>Organization:</strong> ${organizationName}<br/>
              <strong>Opportunity:</strong> ${opportunityTitle}<br/>
              <strong>Mentor / Supervisor:</strong> ${newPlacement.mentorName}<br/>
              <strong>Start Date:</strong> ${newPlacement.startDate}
            </div>
            <p>Please sign in to your portal to review your onboarding documentation, training agreement, and assigned learning tasks.</p>
          `
        );
      }
    } catch (err) {
      console.warn('WBL Notify: Silent error on dispatching placement assigned email:', err);
    }
  };

  const addTask = async (placementId: string, studentId: string, title: string, description: string, dueDate: string) => {
    const task: StudentTask = {
      id: `task-${Date.now()}`,
      tenantId: activeTenant.id,
      placementId,
      studentId,
      title,
      description,
      status: 'todo',
      dueDate,
      updatedAt: new Date().toISOString()
    };

    if (!isUsingMockKeys) {
      try {
        await setDoc(doc(db, 'tasks', task.id), task);
        writeLog(activeTenant.id, activeUser?.email || 'Mentor', 'CREATE_TASK', `Appointed student assignments task: "${title}"`);
      } catch (e) {
        handleFirestoreError(e, OperationType.CREATE, `tasks/${task.id}`);
      }
    } else {
      setTasks(prev => [...prev, task]);
      const newLog: SystemLog = {
        id: `log-${Date.now()}`,
        tenantId: activeTenant.id,
        userEmail: activeUser?.email || 'Mentor',
        action: 'CREATE_TASK',
        details: `Appointed student assignments task: "${title}" (Mock)`,
        timestamp: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev]);
    }

    // Notify student about new assigned task
    try {
      const studentUser = users.find(u => u.uid === studentId);
      if (studentUser) {
        sendNotificationEmail(
          studentUser.email,
          `[New Task Assigned] ${title}`,
          `New Learning Task Assigned`,
          `
            <p>Dear ${studentUser.name},</p>
            <p>A new Work-Based Learning task has been assigned to you by your supervisor or advisor:</p>
            <div style="background-color: #f8fafc; border-left: 4px solid #0094c8; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;">
              <strong style="color: #0f172a;">${title}</strong><br/>
              <p style="margin: 6px 0 0; font-size: 13px; color: #475569;">${description}</p>
              <span style="font-size: 11px; color: #ef4444; font-weight: bold;">Due Date: ${dueDate}</span>
            </div>
            <p>Please sign in to your portal to complete and submit your assignment before the due date.</p>
          `
        );
      }
    } catch (err) {
      console.warn('WBL Notify: Silent error on dispatching task assigned emails:', err);
    }
  };

  const submitTask = async (taskId: string, notes: string, link: string) => {
    if (!isUsingMockKeys) {
      try {
        const docRef = doc(db, 'tasks', taskId);
        await updateDoc(docRef, {
          status: 'submitted',
          submissionNotes: notes,
          submissionLink: link,
          updatedAt: new Date().toISOString()
        });
        const targetTask = tasks.find(t => t.id === taskId);
        if (targetTask) {
          writeLog(targetTask.tenantId, activeUser?.email || 'Student', 'SUBMIT_TASK', `Submitted task: "${targetTask.title}"`);
        }
      } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `tasks/${taskId}`);
      }
    } else {
      setTasks(prev => prev.map(t => t.id === taskId ? {
        ...t,
        status: 'submitted',
        submissionNotes: notes,
        submissionLink: link,
        updatedAt: new Date().toISOString()
      } : t));
      const targetTask = tasks.find(t => t.id === taskId);
      if (targetTask) {
        const newLog: SystemLog = {
          id: `log-${Date.now()}`,
          tenantId: targetTask.tenantId,
          userEmail: activeUser?.email || 'Student',
          action: 'SUBMIT_TASK',
          details: `Submitted task: "${targetTask.title}" (Mock)`,
          timestamp: new Date().toISOString()
        };
        setLogs(prev => [newLog, ...prev]);
      }
    }

    // Notify mentor or supervisor when a student turns in a task
    try {
      const targetTask = tasks.find(t => t.id === taskId);
      if (targetTask) {
        const placement = placements.find(p => p.id === targetTask.placementId);
        if (placement && placement.registryContact) {
          sendNotificationEmail(
            placement.registryContact,
            `[Task Completed] ${targetTask.studentName} has submitted "${targetTask.title}"`,
            `Student Task Submitted for Review`,
            `
              <p>Dear ${placement.mentorName || 'Advisor'},</p>
              <p>Your student, <strong>${targetTask.studentName}</strong>, has completed and submitted their Work-Based Learning task:</p>
              <div style="background-color: #f8fafc; border-left: 4px solid #0094c8; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;">
                <strong style="color: #0f172a;">${targetTask.title}</strong><br/>
                <p style="margin: 6px 0 0; font-size: 13px; color: #475569;">${targetTask.description}</p>
                <p style="margin: 8px 0 0; font-size: 13px;"><strong>Submission Notes:</strong> ${notes || 'None provided'}</p>
                ${link ? `<p style="margin: 4px 0 0; font-size: 13px;"><strong>Submission Link:</strong> <a href="${link}">${link}</a></p>` : ''}
              </div>
              <p>Please sign in to your dashboard to review the submission, assign a score, and provide constructive feedback.</p>
            `
          );
        }
      }
    } catch (err) {
      console.warn('WBL Notify: Silent error on dispatching student task submittal emails:', err);
    }
  };

  const gradeTask = async (taskId: string, feedback: string, rating: number) => {
    if (!isUsingMockKeys) {
      try {
        const docRef = doc(db, 'tasks', taskId);
        await updateDoc(docRef, {
          status: 'approved',
          feedback,
          rating,
          updatedAt: new Date().toISOString()
        });
        const targetTask = tasks.find(t => t.id === taskId);
        if (targetTask) {
          writeLog(targetTask.tenantId, activeUser?.email || 'Mentor', 'EVALUATE_TASK', `Rated Task "${targetTask.title}" details.`);
        }
      } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `tasks/${taskId}`);
      }
    } else {
      setTasks(prev => prev.map(t => t.id === taskId ? {
        ...t,
        status: 'approved',
        feedback,
        rating,
        updatedAt: new Date().toISOString()
      } : t));
      const targetTask = tasks.find(t => t.id === taskId);
      if (targetTask) {
        const newLog: SystemLog = {
          id: `log-${Date.now()}`,
          tenantId: targetTask.tenantId,
          userEmail: activeUser?.email || 'Mentor',
          action: 'EVALUATE_TASK',
          details: `Rated Task "${targetTask.title}" details. (Mock)`,
          timestamp: new Date().toISOString()
        };
        setLogs(prev => [newLog, ...prev]);
      }
    }

    // Notify student that their work has been evaluated
    try {
      const targetTask = tasks.find(t => t.id === taskId);
      if (targetTask) {
        const studentUser = users.find(u => u.uid === targetTask.studentId);
        if (studentUser) {
          sendNotificationEmail(
            studentUser.email,
            `[Task Reviewed] Your task "${targetTask.title}" has been reviewed`,
            `Task Submission Reviewed`,
            `
              <p>Dear ${studentUser.name},</p>
              <p>Your Work-Based Learning task assignment has been reviewed and graded:</p>
              <div style="background-color: #f8fafc; border-left: 4px solid #10b981; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;">
                <strong style="color: #0f172a;">${targetTask.title}</strong><br/>
                <p style="margin: 6px 0 0; font-size: 13px; color: #475569;"><strong>Status:</strong> Completed</p>
                <p style="margin: 4px 0 0; font-size: 13px; color: #475569;"><strong>Score / Rating:</strong> ${rating} / 5</p>
                <p style="margin: 4px 0 0; font-size: 13px; color: #475569;"><strong>Mentor Feedback:</strong> "${feedback || 'No written feedback provided.'}"</p>
              </div>
              <p>Keep up the excellent progress in your Work-Based Learning program!</p>
            `
          );
        }
      }
    } catch (err) {
      console.warn('WBL Notify: Silent error on dispatching task evaluation reviews:', err);
    }
  };

  const submitAttendance = async (placementId: string, weekEnding: string, hoursWorked: number, summary: string) => {
    if (!activeUser) return;
    if (checkProxyActionGuard('Hours submission')) return;
    const placement = placements.find(p => p.id === placementId);
    if (!placement) return;

    const report: AttendanceReport = {
      id: `att-${Date.now()}`,
      tenantId: activeTenant.id,
      placementId,
      studentId: activeUser.uid,
      studentName: activeUser.name,
      weekEnding,
      hoursWorked,
      summary,
      status: 'pending',
      createdAt: new Date().toISOString()
    };

    if (!isUsingMockKeys) {
      try {
        await setDoc(doc(db, 'attendance', report.id), report);
        writeLog(placement.tenantId, activeUser.email, 'SUBMIT_ATTENDANCE', `Logged ${hoursWorked} hours for week ending ${weekEnding}`);
      } catch (e) {
        handleFirestoreError(e, OperationType.CREATE, `attendance/${report.id}`);
      }
    } else {
      setAttendance(prev => [...prev, report]);
      const newLog: SystemLog = {
        id: `log-${Date.now()}`,
        tenantId: placement.tenantId,
        userEmail: activeUser.email,
        action: 'SUBMIT_ATTENDANCE',
        details: `Logged ${hoursWorked} hours for week ending ${weekEnding} (Mock)`,
        timestamp: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev]);
    }

    // Notify contact registries/mentors on hours logged
    try {
      if (placement) {
        if (placement.registryContact) {
          sendNotificationEmail(
            placement.registryContact,
            `[Hours Logged] ${activeUser.name} submitted attendance for week ending ${weekEnding}`,
            `Weekly Attendance Log Submitted`,
            `
              <p>Dear ${placement.mentorName || 'Advisor'},</p>
              <p>Your student, <strong>${activeUser.name}</strong>, has submitted their weekly Work-Based Learning attendance log:</p>
              <div style="background-color: #f8fafc; border-left: 4px solid #0094c8; padding: 12px 16px; margin: 16px 0; border-radius: 0 8px 8px 0;">
                <strong>Week Ending:</strong> ${weekEnding}<br/>
                <strong>Hours Logged:</strong> ${hoursWorked} hours<br/>
                <strong>Weekly Summary:</strong> "${summary || 'No summary provided.'}"
              </div>
              <p>Please sign in to your dashboard to review and approve these logged hours.</p>
            `,
            placement.tenantId,
            'hoursApprovalAlerts'
          );
        }

        // Notify regional admins of this region who have hoursApprovalAlerts enabled
        const regAdmins = users.filter(u => u.role === 'regional_admin' && u.email && (u.tenantId === placement.tenantId || u.tenantId === activeTenant.id));
        for (const ra of regAdmins) {
          if (ra.email && ra.email.toLowerCase() !== placement.registryContact?.toLowerCase() && shouldUserReceiveNotification(ra, 'email', 'hoursApprovalAlerts')) {
            sendNotificationEmail(
              ra.email,
              `[Regional Hours Logged] ${activeUser.name} (${placement.organizationName})`,
              `Student Hours Logged in Region`,
              `
                <p>Hello Regional Administrator,</p>
                <p>Student <strong>${activeUser.name}</strong> logged <strong>${hoursWorked} hours</strong> for the week ending <strong>${weekEnding}</strong> at <strong>${placement.organizationName}</strong>.</p>
                <p>Supervisor: ${placement.mentorName || 'Assigned Mentor'} (${placement.registryContact || 'Unassigned'})</p>
              `,
              placement.tenantId,
              'hoursApprovalAlerts'
            ).catch(() => {});
          }
        }
      }
    } catch (err) {
      console.warn('WBL Notify: Silent error on dispatching attendance submission notification:', err);
    }
  };

  const approveAttendance = async (reportId: string, status: 'approved' | 'rejected', feedback?: string) => {
    if (checkProxyActionGuard('Attendance approval')) return;
    if (!isUsingMockKeys) {
      try {
        const docRef = doc(db, 'attendance', reportId);
        await updateDoc(docRef, {
           status,
           feedback: feedback || '',
           approvedBy: activeUser?.uid || 'system'
        });
        const record = attendance.find(a => a.id === reportId);
        if (record) {
          writeLog(record.tenantId, activeUser?.email || 'Mentor', 'EVALUATE_ATTENDANCE', `${status.toUpperCase()} attendance for week ending ${record.weekEnding}`);
        }
      } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `attendance/${reportId}`);
      }
    } else {
      setAttendance(prev => prev.map(a => a.id === reportId ? {
        ...a,
        status,
        feedback: feedback || '',
        approvedBy: activeUser?.uid || 'system'
      } : a));
      const record = attendance.find(a => a.id === reportId);
      if (record) {
        const newLog: SystemLog = {
          id: `log-${Date.now()}`,
          tenantId: record.tenantId,
          userEmail: activeUser?.email || 'Mentor',
          action: 'EVALUATE_ATTENDANCE',
          details: `${status.toUpperCase()} attendance for week ending ${record.weekEnding} (Mock)`,
          timestamp: new Date().toISOString()
        };
        setLogs(prev => [newLog, ...prev]);
      }
    }

    // Notify student regarding approval decision
    try {
      const record = attendance.find(a => a.id === reportId);
      if (record) {
        const studentUser = users.find(u => u.uid === record.studentId);
        if (studentUser) {
          sendNotificationEmail(
            studentUser.email,
            `[Attendance Log Update] Your hours for week ending ${record.weekEnding} have been ${status === 'approved' ? 'approved' : 'rejected'}`,
            `Attendance Log ${status === 'approved' ? 'Approved' : 'Rejected'}`,
            `
              <p>Dear ${studentUser.name},</p>
              <p>Your logged attendance of <strong>${record.hoursWorked} hours</strong> for the week ending <strong>${record.weekEnding}</strong> has been <strong>${status.toUpperCase()}</strong>.</p>
              ${feedback ? `<div style="background-color: #f1f5f9; padding: 12px; border-radius: 8px; margin: 16px 0; font-size: 13px;"><strong>Advisor Feedback:</strong> ${feedback}</div>` : ''}
              <p>Please sign in to your portal to review your cumulative approved hours and program progress.</p>
            `,
            record.tenantId,
            'hoursApprovalAlerts'
          );
        }
      }
    } catch (err) {
      console.warn('WBL Notify: Silent error on dispatching attendance evaluate notifications:', err);
    }
  };

  const updatePlacementEvaluation = async (placementId: string, evalNum: 1 | 2, evaluation: PlacementEvaluation) => {
    if (checkProxyActionGuard('Evaluation modification')) return;
    const field = evalNum === 1 ? 'eval1' : 'eval2';
    let targetPlacement = placements.find(p => p.id === placementId);

    if (!isUsingMockKeys) {
      try {
        if (!targetPlacement) {
          const docSnap = await getDoc(doc(db, 'placements', placementId));
          if (docSnap.exists()) {
            targetPlacement = { id: docSnap.id, ...docSnap.data() } as Placement;
          }
        }
        const docRef = doc(db, 'placements', placementId);
        await updateDoc(docRef, { [field]: evaluation });
        writeLog(activeTenant.id, activeUser?.email || '', 'UPDATE_EVALUATION', `Submitted Eval ${evalNum} for placement ${placementId}`);
      } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `placements/${placementId}`);
      }
    } else {
      setPlacements(prev => prev.map(p => p.id === placementId ? { ...p, [field]: evaluation } : p));
    }

    if (targetPlacement) {
      try {
        const studentUser = users.find(u => u.uid === targetPlacement?.studentId);
        if (studentUser) {
          sendNotificationEmail(
            studentUser.email,
            `[Evaluation Complete] Evaluation ${evalNum} for ${targetPlacement.organizationName}`,
            `Performance Evaluation Completed`,
            `
              <p>Dear ${targetPlacement.studentName},</p>
              <p>Your supervisor at <strong>${targetPlacement.organizationName}</strong> has completed your <strong>Evaluation ${evalNum}</strong>.</p>
              <p>Please sign in to the portal to review your evaluation scores and supervisor feedback.</p>
            `,
            targetPlacement.tenantId,
            'evaluationAlerts'
          );
        }

        // Also notify Regional Admins for this placement who have evaluationAlerts enabled
        const regAdmins = users.filter(u => u.role === 'regional_admin' && u.email && (u.tenantId === targetPlacement.tenantId || u.region === targetPlacement.tenantName));
        for (const ra of regAdmins) {
          if (ra.email && shouldUserReceiveNotification(ra, 'email', 'evaluationAlerts')) {
            sendNotificationEmail(
              ra.email,
              `[Regional Evaluation Completed] ${targetPlacement.studentName} - Evaluation ${evalNum}`,
              `Workplace Evaluation Completed`,
              `
                <p>Hello Regional Administrator,</p>
                <p>A workplace performance evaluation (Evaluation ${evalNum}) has been finalized for student <strong>${targetPlacement.studentName}</strong> at <strong>${targetPlacement.organizationName}</strong>.</p>
                <p>You can review detailed rubric scores and competency ratings in your Regional Admin Console.</p>
              `,
              targetPlacement.tenantId,
              'evaluationAlerts'
            ).catch(() => {});
          }
        }
      } catch (err) {
        console.warn('WBL Notify: Silent error on dispatching evaluation email:', err);
      }
    }
  };

  const updatePlacementAgreement = async (placementId: string, agreement: TrainingAgreement) => {
    if (checkProxyActionGuard('Training agreement modification')) return;
    const updates: any = { agreement };
    if (agreement.startDate) updates.startDate = agreement.startDate;
    if (agreement.endDate) updates.endDate = agreement.endDate;
    if (agreement.assignedDays) updates.assignedDays = agreement.assignedDays;
    if (agreement.worksiteName) updates.organizationName = agreement.worksiteName;
    if (agreement.supervisorName) updates.mentorName = agreement.supervisorName;
    if (agreement.supervisorPhone) updates.directLine = agreement.supervisorPhone;
    if (agreement.supervisorEmail) updates.registryContact = agreement.supervisorEmail;
    if (agreement.assignedHoursTotal !== undefined) updates.assignedHoursTotal = agreement.assignedHoursTotal;

    const targetPl = placements.find(p => p.id === placementId);
    if (targetPl) {
      const sDate = agreement.startDate || targetPl.startDate;
      const eDate = agreement.endDate || targetPl.endDate;
      const eval1DueDate = getClosestWeekdayMidpoint(sDate, eDate);
      const eval2DueDate = eDate;

      updates.eval1 = {
        ...(targetPl.eval1 || { status: 'pending', scores: {} }),
        dueDate: eval1DueDate,
        reminderBeforeSent: targetPl.eval1?.dueDate === eval1DueDate ? targetPl.eval1?.reminderBeforeSent : false,
        reminderAfterSent: targetPl.eval1?.dueDate === eval1DueDate ? targetPl.eval1?.reminderAfterSent : false,
      };
      updates.eval2 = {
        ...(targetPl.eval2 || { status: 'pending', scores: {} }),
        dueDate: eval2DueDate,
        reminderBeforeSent: targetPl.eval2?.dueDate === eval2DueDate ? targetPl.eval2?.reminderBeforeSent : false,
        reminderAfterSent: targetPl.eval2?.dueDate === eval2DueDate ? targetPl.eval2?.reminderAfterSent : false,
      };
    }

    if (!isUsingMockKeys) {
      try {
        const docRef = doc(db, 'placements', placementId);
        await updateDoc(docRef, cleanFirestoreData(updates));
        writeLog(activeTenant.id, activeUser?.email || '', 'UPDATE_AGREEMENT', `Updated training agreement for placement ${placementId}`);
      } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `placements/${placementId}`);
      }
    } else {
      setPlacements(prev => prev.map(p => p.id === placementId ? { ...p, ...updates } : p));
    }
  };

  const markEvaluationReminderSent = async (placementId: string, evalNum: 1 | 2, type: 'before' | 'after') => {
    const field = evalNum === 1 ? 'eval1' : 'eval2';
    const subfield = type === 'before' ? 'reminderBeforeSent' : 'reminderAfterSent';

    const target = placements.find(p => p.id === placementId);
    if (!target) return;

    const currentEval = evalNum === 1 ? target.eval1 : target.eval2;
    const updatedEval = currentEval ? { ...currentEval, [subfield]: true } : { status: 'pending', scores: {}, [subfield]: true };

    const updates = { [field]: updatedEval };

    if (!isUsingMockKeys) {
      try {
        const docRef = doc(db, 'placements', placementId);
        await updateDoc(docRef, updates);
      } catch (err) {
        console.error('Failed to update reminder status in Firestore:', err);
      }
    }
    setPlacements(prev => prev.map(p => p.id === placementId ? { ...p, [field]: updatedEval } : p));
  };

  const sendEvaluationReminderEmail = async (placement: Placement, evalNum: 1 | 2, timing: 'before' | 'after', dueDate: string) => {
    const isBefore = timing === 'before';
    const evalTitle = evalNum === 1 ? 'Midpoint Evaluation' : 'Final Evaluation';
    const recipientName = placement.mentorName || 'Mentor';
    const studentName = placement.studentName;
    const properPlRegion = formatRegionProperNoun(placement.tenantId || 'Spartanburg');
    const plRegionNoun = formatRegionWithNoun(properPlRegion);

    const subject = isBefore
      ? `[Reminder] Upcoming ${evalTitle} for ${studentName} is due tomorrow`
      : `[Urgent Reminder] Past Due: ${evalTitle} for ${studentName}`;

    const title = isBefore
      ? `${evalTitle} Due Tomorrow`
      : `${evalTitle} Is Past Due`;

    const bodyHtml = isBefore
      ? `
        <p>Dear ${recipientName},</p>
        <p>This is a friendly reminder that the <strong>${evalTitle}</strong> for <strong>${studentName}</strong> in the <strong>${plRegionNoun}</strong> is scheduled for completion tomorrow on <strong>${dueDate}</strong>.</p>
        <p>Regular performance evaluations document student progress and ensure alignment with program requirements.</p>
        <p>Please sign in to the portal and complete the evaluation at your earliest convenience.</p>
        <p style="margin-top: 24px;">Best regards,<br/>The ${plRegionNoun} Team</p>
      `
      : `
        <p>Dear ${recipientName},</p>
        <p>Our records indicate that the <strong>${evalTitle}</strong> for <strong>${studentName}</strong> in the <strong>${plRegionNoun}</strong> was due on <strong>${dueDate}</strong> and is currently <strong>one week past due</strong>.</p>
        <p>Please sign in to the portal and complete the evaluation as soon as possible to ensure that the student receives full credit for their placement.</p>
        <p style="margin-top: 24px;">Best regards,<br/>The ${plRegionNoun} Team</p>
      `;

    if (placement.registryContact) {
      await sendNotificationEmail(placement.registryContact, subject, title, bodyHtml, placement.tenantId);
    }
    if (placement.studentEmail) {
      const studentSubject = isBefore
        ? `[Reminder] Your ${evalTitle} is due tomorrow`
        : `[Reminder] Your ${evalTitle} is past due`;
      const studentTitle = isBefore
        ? `Work-Based Learning ${evalTitle} Due Tomorrow`
        : `Work-Based Learning ${evalTitle} Past Due`;
      const studentBodyHtml = isBefore
        ? `
          <p>Dear ${studentName},</p>
          <p>Your <strong>${evalTitle}</strong> for your placement at <strong>${placement.organizationName}</strong> in the <strong>${plRegionNoun}</strong> is due tomorrow on <strong>${dueDate}</strong>.</p>
          <p>Please check in with your supervisor, <strong>${placement.mentorName}</strong>, to ensure they have the opportunity to review your progress and submit your evaluation in the portal.</p>
          <p style="margin-top: 24px;">Best regards,<br/>The ${plRegionNoun} Team</p>
        `
        : `
          <p>Dear ${studentName},</p>
          <p>Your <strong>${evalTitle}</strong> for your placement at <strong>${placement.organizationName}</strong> in the <strong>${plRegionNoun}</strong> was due on <strong>${dueDate}</strong> and is currently past due.</p>
          <p>Please follow up with your supervisor, <strong>${placement.mentorName}</strong>, to kindly remind them to sign in to the portal and finalize your evaluation.</p>
          <p style="margin-top: 24px;">Best regards,<br/>The ${plRegionNoun} Team</p>
        `;
      await sendNotificationEmail(placement.studentEmail, studentSubject, studentTitle, studentBodyHtml, placement.tenantId);
    }
  };

  const checkAndSendEvaluationReminders = async (plList: Placement[]) => {
    const now = new Date();
    const oneDayMs = 24 * 60 * 60 * 1000;

    for (const p of plList) {
      if (p.status !== 'active') continue;

      // 1. Eval 1 Reminders
      if (p.eval1 && p.eval1.dueDate) {
        const dueDateObj = new Date(p.eval1.dueDate + 'T00:00:00');
        const targetReminderTime = new Date(dueDateObj.getTime() - oneDayMs);
        targetReminderTime.setHours(12, 0, 0, 0); // 12:00 PM local time day before

        const dueDateTimeEnd = new Date(p.eval1.dueDate + 'T23:59:59');

        // Check for 1-day-before reminder (dispatched at or after 12:00 PM on the day before, before due date end)
        if (now.getTime() >= targetReminderTime.getTime() && now.getTime() <= dueDateTimeEnd.getTime() && p.eval1.status !== 'released' && !p.eval1.reminderBeforeSent) {
          console.log(`Sending eval1 1-day-before reminder for placement ${p.id}`);
          await sendEvaluationReminderEmail(p, 1, 'before', p.eval1.dueDate);
          await markEvaluationReminderSent(p.id, 1, 'before');
        }

        // Check for overdue reminder (7 days after due date)
        const overdueTime = new Date(dueDateTimeEnd.getTime() + (7 * oneDayMs));
        if (now.getTime() >= overdueTime.getTime() && p.eval1.status !== 'released' && !p.eval1.reminderAfterSent) {
          console.log(`Sending eval1 1-week-after reminder for placement ${p.id}`);
          await sendEvaluationReminderEmail(p, 1, 'after', p.eval1.dueDate);
          await markEvaluationReminderSent(p.id, 1, 'after');
        }
      }

      // 2. Eval 2 Reminders
      if (p.eval2 && p.eval2.dueDate) {
        const dueDateObj = new Date(p.eval2.dueDate + 'T00:00:00');
        const targetReminderTime = new Date(dueDateObj.getTime() - oneDayMs);
        targetReminderTime.setHours(12, 0, 0, 0); // 12:00 PM local time day before

        const dueDateTimeEnd = new Date(p.eval2.dueDate + 'T23:59:59');

        // Check for 1-day-before reminder (dispatched at or after 12:00 PM on the day before, before due date end)
        if (now.getTime() >= targetReminderTime.getTime() && now.getTime() <= dueDateTimeEnd.getTime() && p.eval2.status !== 'released' && !p.eval2.reminderBeforeSent) {
          console.log(`Sending eval2 1-day-before reminder for placement ${p.id}`);
          await sendEvaluationReminderEmail(p, 2, 'before', p.eval2.dueDate);
          await markEvaluationReminderSent(p.id, 2, 'before');
        }

        // Check for overdue reminder (7 days after due date)
        const overdueTime = new Date(dueDateTimeEnd.getTime() + (7 * oneDayMs));
        if (now.getTime() >= overdueTime.getTime() && p.eval2.status !== 'released' && !p.eval2.reminderAfterSent) {
          console.log(`Sending eval2 1-week-after reminder for placement ${p.id}`);
          await sendEvaluationReminderEmail(p, 2, 'after', p.eval2.dueDate);
          await markEvaluationReminderSent(p.id, 2, 'after');
        }
      }
    }
  };

  const hireStudent = async (placementId: string) => {
    const updates = {
      status: 'hired' as const,
      hiredDate: new Date().toISOString().split('T')[0]
    };
    let targetPlacement = placements.find(p => p.id === placementId);

    if (!isUsingMockKeys) {
      try {
        if (!targetPlacement) {
          const docSnap = await getDoc(doc(db, 'placements', placementId));
          if (docSnap.exists()) {
            targetPlacement = { id: docSnap.id, ...docSnap.data() } as Placement;
          }
        }
        const docRef = doc(db, 'placements', placementId);
        await updateDoc(docRef, updates);
        writeLog(activeTenant.id, activeUser?.email || '', 'HIRE_STUDENT', `Student hired from placement ${placementId}`);
      } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `placements/${placementId}`);
      }
    } else {
      setPlacements(prev => prev.map(p => p.id === placementId ? { ...p, ...updates } : p));
    }

    if (targetPlacement) {
      try {
        const studentUser = users.find(u => u.uid === targetPlacement?.studentId);
        if (studentUser) {
          sendNotificationEmail(
            studentUser.email,
            `[Congratulations] You Have Been Hired by ${targetPlacement.organizationName}`,
            `Congratulations on Being Hired!`,
            `
              <p>Dear ${targetPlacement.studentName},</p>
              <p>Congratulations! You have been <strong>HIRED</strong> directly from your placement with <strong>${targetPlacement.organizationName}</strong>.</p>
              <p>Your portal status has been officially updated to reflect this outstanding milestone in your career pathway.</p>
            `
          );
        }
      } catch (err) {
        console.warn('WBL Notify: Silent error on dispatching hire student email:', err);
      }
    }
  };


  const addPlacementNote = async (
    placementId: string,
    text: string,
    visibleToStudents: boolean,
    visibleToAdvisors: boolean
  ) => {
    const activePl = placements.find(p => p.id === placementId);
    if (!activePl) return;

    const newNote: PlacementNote = {
      id: Math.random().toString(36).substring(2, 11),
      text,
      createdAt: new Date().toISOString(),
      createdBy: {
        uid: activeUser?.uid || activeUser?.email || 'system',
        name: activeUser?.name || activeUser?.email || 'System User',
        role: activeUser?.role || 'organization'
      },
      visibleToStudents,
      visibleToAdvisors
    };

    const updatedNotes = [...(activePl.notes || []), newNote];

    // Optimistically update local state immediately
    setPlacements(prev => prev.map(p => p.id === placementId ? { ...p, notes: updatedNotes } : p));

    if (!isUsingMockKeys) {
      try {
        const docRef = doc(db, 'placements', placementId);
        await updateDoc(docRef, { notes: updatedNotes });
        writeLog(activeTenant.id, activeUser?.email || '', 'ADD_PLACEMENT_NOTE', `Added note to placement ${placementId}`);
      } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `placements/${placementId}`);
      }
    }
  };

  const deletePlacementNote = async (placementId: string, noteId: string) => {
    const activePl = placements.find(p => p.id === placementId);
    if (!activePl) return;

    const updatedNotes = (activePl.notes || []).filter(n => n.id !== noteId);

    // Optimistically update local state immediately
    setPlacements(prev => prev.map(p => p.id === placementId ? { ...p, notes: updatedNotes } : p));

    if (!isUsingMockKeys) {
      try {
        const docRef = doc(db, 'placements', placementId);
        await updateDoc(docRef, { notes: updatedNotes });
        writeLog(activeTenant.id, activeUser?.email || '', 'DELETE_PLACEMENT_NOTE', `Deleted note from placement ${placementId}`);
      } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `placements/${placementId}`);
      }
    }
  };

  const getOrgTeamInviteUrl = (
    orgUser: Partial<UserProfile>,
    member?: { name?: string; title?: string; email?: string; phone?: string; orgRole?: string }
  ): string => {
    const regionMatch = orgUser.region
      ? tenants.find(t => t.id !== 'global' && (t.name.toLowerCase().trim() === orgUser.region?.toLowerCase().trim() || t.id.toLowerCase().trim() === orgUser.region?.toLowerCase().trim()))
      : undefined;
    const resolvedTenantId =
      (orgUser.tenantId && orgUser.tenantId !== 'global' ? orgUser.tenantId : '') ||
      regionMatch?.id ||
      (activeTenant.id && activeTenant.id !== 'global' ? activeTenant.id : 'spartanburg');
    const foundTenant = tenants.find(t => t.id === resolvedTenantId || t.subdomain === resolvedTenantId) || activeTenant;
    const baseUrl = typeof window !== 'undefined' && window.location.origin
      ? window.location.origin
      : getTenantBaseUrl(foundTenant);
    const umbrellaId = orgUser.parentOrgId || orgUser.orgTeamId || orgUser.uid || '';
    const orgLabel = orgUser.orgName || orgUser.name || 'Partner Organization';
    const address = orgUser.address || '';
    const extendedRegions = (orgUser.extendedRegions || []).filter(Boolean).join(',');

    const params = new URLSearchParams();
    params.set('joinOrg', '1');
    params.set('role', 'organization');
    params.set('orgId', umbrellaId);
    params.set('orgName', orgLabel);
    params.set('primaryRegion', resolvedTenantId);
    params.set('region', resolvedTenantId);
    if (address) params.set('address', address);
    if (extendedRegions) params.set('extendedRegions', extendedRegions);
    if (member?.email) params.set('email', (member.email || '').trim().toLowerCase());
    if (member?.name) params.set('name', (member.name || '').trim());
    if (member?.title) params.set('title', (member.title || '').trim());
    if (member?.phone) params.set('phone', (member.phone || '').trim());
    if (member?.orgRole) params.set('orgRole', member.orgRole);

    return `${baseUrl}/${resolvedTenantId}?${params.toString()}`;
  };

  const delegateOrgLead = async (newLeadUid: string, targetOrgId?: string) => {
    const effectiveUmbrellaId = targetOrgId || activeUser?.parentOrgId || activeUser?.orgTeamId || activeUser?.uid;
    if (!effectiveUmbrellaId) throw new Error("No organization ID specified.");

    const umbrellaDocUser = users.find(u => u.uid === effectiveUmbrellaId) || (activeUser?.uid === effectiveUmbrellaId ? activeUser : null);
    const targetNewLead = users.find(u => u.uid === newLeadUid);
    if (!targetNewLead) throw new Error("Target team member not found.");

    // Find previous lead
    const orgUsers = users.filter(u =>
      u.uid === effectiveUmbrellaId ||
      u.parentOrgId === effectiveUmbrellaId ||
      u.orgTeamId === effectiveUmbrellaId ||
      (u.role === 'organization' && u.orgName && umbrellaDocUser?.orgName && u.orgName.toLowerCase().trim() === umbrellaDocUser.orgName.toLowerCase().trim())
    );
    const previousLead = orgUsers.find(u => u.isOrgLead === true && u.uid !== newLeadUid) || (umbrellaDocUser?.uid !== newLeadUid ? umbrellaDocUser : null);

    // Update new lead in Firestore
    await updateDoc(doc(db, 'users', newLeadUid), {
      isOrgLead: true,
      orgRole: 'owner',
      orgTeamId: effectiveUmbrellaId,
      parentOrgId: effectiveUmbrellaId,
      extendedRegions: umbrellaDocUser?.extendedRegions || targetNewLead.extendedRegions || []
    });

    // Update old lead in Firestore if different
    if (previousLead && previousLead.uid !== newLeadUid) {
      await updateDoc(doc(db, 'users', previousLead.uid), {
        isOrgLead: false,
        orgRole: 'admin'
      });
    }

    // Sync umbrella doc's orgTeamMembers array
    let updatedMembersList: OrganizationTeamMember[] = [];
    if (umbrellaDocUser) {
      let currentMembers = [...(umbrellaDocUser.orgTeamMembers || [])];

      // Update or add new lead
      const newIdx = currentMembers.findIndex(m => m.uid === newLeadUid || (m.email && targetNewLead.email && m.email.toLowerCase().trim() === targetNewLead.email.toLowerCase().trim()));
      if (newIdx >= 0) {
        currentMembers[newIdx] = {
          ...currentMembers[newIdx],
          uid: newLeadUid,
          isLead: true,
          orgRole: 'owner'
        };
      } else {
        currentMembers.push({
          id: newLeadUid,
          uid: newLeadUid,
          name: targetNewLead.name || 'Organization Lead',
          email: targetNewLead.email || '',
          phone: targetNewLead.contactPhone || '',
          title: targetNewLead.professionalTitle || 'Organization Lead',
          orgRole: 'owner',
          isLead: true,
          addedAt: new Date().toISOString()
        });
      }

      // Demote previous lead in members list
      if (previousLead && previousLead.uid !== newLeadUid) {
        const oldIdx = currentMembers.findIndex(m => m.uid === previousLead.uid || (m.email && previousLead.email && m.email.toLowerCase().trim() === previousLead.email.toLowerCase().trim()));
        if (oldIdx >= 0) {
          currentMembers[oldIdx] = {
            ...currentMembers[oldIdx],
            isLead: false,
            orgRole: 'admin'
          };
        }
      }

      // Ensure only the new lead has isLead: true
      currentMembers = currentMembers.map(m => m.uid === newLeadUid ? { ...m, isLead: true, orgRole: 'owner' } : { ...m, isLead: false });
      updatedMembersList = currentMembers;

      await updateDoc(doc(db, 'users', umbrellaDocUser.uid), {
        orgTeamMembers: cleanFirestoreData(currentMembers)
      });
    }

    // Optimistically update users array in memory
    setUsers(prev => prev.map(u => {
      if (u.uid === newLeadUid) {
        return {
          ...u,
          isOrgLead: true,
          orgRole: 'owner',
          orgTeamId: effectiveUmbrellaId,
          parentOrgId: effectiveUmbrellaId,
          extendedRegions: umbrellaDocUser?.extendedRegions || u.extendedRegions || []
        };
      }
      if (previousLead && u.uid === previousLead.uid) {
        return { ...u, isOrgLead: false, orgRole: 'admin' };
      }
      if (umbrellaDocUser && u.uid === umbrellaDocUser.uid && updatedMembersList.length > 0) {
        return { ...u, orgTeamMembers: updatedMembersList };
      }
      return u;
    }));

    // Optimistically update activeUser in memory if affected
    if (activeUser?.uid === newLeadUid) {
      setActiveUser(prev => prev ? ({ ...prev, isOrgLead: true, orgRole: 'owner', orgTeamId: effectiveUmbrellaId, parentOrgId: effectiveUmbrellaId, extendedRegions: umbrellaDocUser?.extendedRegions || prev.extendedRegions || [] }) : null);
    } else if (activeUser?.uid === previousLead?.uid) {
      setActiveUser(prev => prev ? ({ ...prev, isOrgLead: false, orgRole: 'admin' }) : null);
    }

    writeLog(activeTenant.id, activeUser?.email || '', 'DELEGATE_ORG_LEAD', `Delegated Organization Lead title to ${targetNewLead.name || targetNewLead.email} (${newLeadUid})`);
  };

  const removeOrgTeamMember = async (
    targetMember: { id: string; uid?: string; email?: string; name?: string },
    options?: { deleteAuthAndProfile?: boolean; targetOrgId?: string }
  ) => {
    const effectiveUmbrellaId = options?.targetOrgId || activeUser?.parentOrgId || activeUser?.orgTeamId || activeUser?.uid;
    if (!effectiveUmbrellaId) throw new Error("No organization ID specified.");

    const umbrellaDocUser = users.find(u => u.uid === effectiveUmbrellaId) || (activeUser?.uid === effectiveUmbrellaId ? activeUser : null);
    const targetUid = targetMember.uid;
    const targetEmail = (targetMember.email || '').toLowerCase().trim();
    const isPlatformAdmin = activeUser?.role === 'superadmin' || activeUser?.role === 'regional_admin' || activeUser?.role === 'admin';
    const allowPermanentPurge = Boolean(options?.deleteAuthAndProfile && isPlatformAdmin);

    // 1. Only platform admins (Regional / Super Admin) may permanently purge Auth & Firestore profiles:
    if (allowPermanentPurge && targetUid) {
      await deleteUserProfile(targetUid);
    } else if (targetUid) {
      // 2. Organization Leads strictly un-link the user from the organization in Firestore (revoke access)
      if (!isUsingMockKeys) {
        try {
          await updateDoc(doc(db, 'users', targetUid), {
            parentOrgId: '',
            orgTeamId: '',
            isOrgLead: false,
            orgRole: 'unassigned',
            orgName: ''
          });
        } catch (e) {
          handleFirestoreError(e, OperationType.UPDATE, `users/${targetUid}`);
        }
      }
      setUsers(prev => prev.map(u => {
        if (u.uid === targetUid) {
          return {
            ...u,
            parentOrgId: undefined,
            orgTeamId: undefined,
            isOrgLead: false,
            orgRole: 'unassigned',
            orgName: ''
          };
        }
        return u;
      }));
    }

    // 3. Remove from umbrella organization's orgTeamMembers and orgAuthorizedEmails array
    if (umbrellaDocUser) {
      const updatedMembers = (umbrellaDocUser.orgTeamMembers || []).filter(m =>
        m.id !== targetMember.id &&
        (!targetUid || m.uid !== targetUid) &&
        (!targetEmail || (m.email || '').toLowerCase().trim() !== targetEmail)
      );

      const updatedAuthorizedEmails = (umbrellaDocUser.orgAuthorizedEmails || []).filter(e =>
        !targetEmail || e.toLowerCase().trim() !== targetEmail
      );

      if (!isUsingMockKeys) {
        try {
          await updateDoc(doc(db, 'users', umbrellaDocUser.uid), {
            orgTeamMembers: updatedMembers,
            orgAuthorizedEmails: updatedAuthorizedEmails
          });
        } catch (e) {
          handleFirestoreError(e, OperationType.UPDATE, `users/${umbrellaDocUser.uid}`);
        }
      }

      setUsers(prev => prev.map(u => u.uid === umbrellaDocUser.uid ? {
        ...u,
        orgTeamMembers: updatedMembers,
        orgAuthorizedEmails: updatedAuthorizedEmails
      } : u));

      if (activeUser?.uid === umbrellaDocUser.uid) {
        setActiveUser(prev => prev ? ({
          ...prev,
          orgTeamMembers: updatedMembers,
          orgAuthorizedEmails: updatedAuthorizedEmails
        }) : null);
      }
    }

    const actionType = allowPermanentPurge ? 'REMOVE_ORG_TEAM_MEMBER_PURGE' : 'REMOVE_ORG_TEAM_MEMBER_UNLINK';
    writeLog(
      activeTenant.id,
      activeUser?.email || '',
      actionType,
      `Lead removed team member ${targetMember.name || targetMember.email} (Auth delete: ${allowPermanentPurge})`
    );
  };

  const sendOrgTeamInviteEmail = async (
    orgUser: Partial<UserProfile>,
    member: { name: string; title?: string; email: string; phone?: string; orgRole: string }
  ) => {
    if (!member.email) return;
    const orgLabel = orgUser.orgName || orgUser.name || 'your organization';
    const inviterName =
      (`${orgUser.contactFirstName || ''} ${orgUser.contactLastName || ''}`.trim()) ||
      orgUser.name ||
      orgUser.email ||
      orgLabel;
    const roleLabel =
      member.orgRole === 'admin'
        ? 'Organization Admin (Full Access)'
        : 'Worksite Supervisor / Mentor';
    const regionMatch = orgUser.region
      ? tenants.find(t => t.id !== 'global' && (t.name.toLowerCase().trim() === orgUser.region?.toLowerCase().trim() || t.id.toLowerCase().trim() === orgUser.region?.toLowerCase().trim()))
      : undefined;
    const resolvedTenantId =
      (orgUser.tenantId && orgUser.tenantId !== 'global' ? orgUser.tenantId : '') ||
      regionMatch?.id ||
      (activeTenant.id && activeTenant.id !== 'global' ? activeTenant.id : 'spartanburg');
    const inviteUrl = getOrgTeamInviteUrl({ ...orgUser, tenantId: resolvedTenantId }, member);

    await sendNotificationEmail(
      member.email,
      `Action Required: Connect your account to ${orgLabel} on the WBL Portal`,
      `${orgLabel} — Team Access Invitation`,
      `
        <p>Hello <strong>${member.name}</strong>,</p>
        <p><strong>${inviterName}</strong> has invited you to join <strong>${orgLabel}</strong> on the Work-Based Learning Portal as a <strong>${roleLabel}</strong>${member.title ? ` (${member.title})` : ''}.</p>

        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; margin: 20px 0;">
          <p style="margin: 0 0 8px 0; font-size: 13px; color: #0f172a;"><strong>Organization:</strong> ${orgLabel}</p>
          <p style="margin: 0 0 8px 0; font-size: 13px; color: #0f172a;"><strong>Your Assigned Role:</strong> ${roleLabel}</p>
          ${member.title ? `<p style="margin: 0 0 8px 0; font-size: 13px; color: #0f172a;"><strong>Position / Title:</strong> ${member.title}</p>` : ''}
          <p style="margin: 0; font-size: 13px; color: #0f172a;"><strong>Authorized Email:</strong> ${member.email}</p>
        </div>

        <div style="text-align: center; margin: 28px 0;">
          <a href="${inviteUrl}" style="background-color: #0094c8; color: #ffffff; padding: 14px 28px; border-radius: 8px; text-decoration: none; font-weight: bold; font-size: 14px; display: inline-block;">
            Accept Invitation &amp; Connect to ${orgLabel}
          </a>
        </div>

        <p style="font-size: 13px; color: #334155; margin-bottom: 8px;"><strong>How to connect your account:</strong></p>
        <ol style="font-size: 13px; color: #475569; padding-left: 20px; margin-top: 0;">
          <li style="margin-bottom: 6px;">Click the <strong>Accept Invitation &amp; Connect to ${orgLabel}</strong> button above to open your pre-filled onboarding page.</li>
          <li style="margin-bottom: 6px;">Create your portal password (or sign in with your existing password if <strong>${member.email}</strong> already has an account).</li>
          <li style="margin-bottom: 6px;">Your account will immediately link under <strong>${orgLabel}</strong> with shared access to opportunities, student applications, placements, evaluations, and reports.</li>
        </ol>

        <p style="font-size: 11px; color: #64748b; margin-top: 20px; word-break: break-all;">
          Direct Invitation Link: <a href="${inviteUrl}" style="color: #0094c8;">${inviteUrl}</a>
        </p>
      `,
      resolvedTenantId
    );
  };

  const updateUserProfile = async (uid: string, updates: Partial<UserProfile>) => {
    let effectiveUpdates = { ...updates };
    const targetUser = users.find(u => u.uid === uid) || (activeUser?.uid === uid ? activeUser : null);
    const effectiveRole = effectiveUpdates.role || targetUser?.role;
    
    // Automatic School to District and Region reverse resolution
    const schoolToResolve = effectiveUpdates.school || effectiveUpdates.schoolId || (targetUser?.school && !effectiveUpdates.school ? targetUser.school : undefined);
    if (schoolToResolve) {
      const activeTenantHint = (effectiveUpdates.tenantId && effectiveUpdates.tenantId !== 'global') 
        ? effectiveUpdates.tenantId 
        : (targetUser?.tenantId && targetUser.tenantId !== 'global' ? targetUser.tenantId : activeTenant.id);
        
      const resolved = resolveDistrictForSchool(
        schoolToResolve,
        activeTenantHint !== 'global' ? activeTenantHint : undefined,
        allSchools,
        allDistricts
      );
      if (resolved.districtName && (!effectiveUpdates.district || effectiveUpdates.district === 'Unassigned')) {
        effectiveUpdates.district = resolved.districtName;
      }
      if (resolved.districtId && (!effectiveUpdates.districtId || effectiveUpdates.districtId === 'Unassigned')) {
        effectiveUpdates.districtId = resolved.districtId;
      }
      // If student is assigned to 'global', missing tenant, or updating school, auto-bind tenantId to the school's region
      if (effectiveRole === 'student' && (!effectiveUpdates.tenantId || effectiveUpdates.tenantId === 'global' || targetUser?.tenantId === 'global')) {
        const schoolRegion = resolved.regionId || deriveRegionIdForSchool({ name: schoolToResolve });
        if (schoolRegion && schoolRegion !== 'global') {
          effectiveUpdates.tenantId = schoolRegion;
          effectiveUpdates.region = formatRegionWithNoun(schoolRegion);
        }
      }
    }

    // Automatically compute orgAuthorizedEmails whenever orgTeamMembers is updated
    if (effectiveUpdates.orgTeamMembers) {
      effectiveUpdates.orgAuthorizedEmails = Array.from(
        new Set(
          effectiveUpdates.orgTeamMembers
            .map(m => (m.email || '').toLowerCase().trim())
            .filter(Boolean)
        )
      );
    }

    const cleanedUpdates = cleanFirestoreData(effectiveUpdates);

    // Identify shared umbrella organization fields if this is an organization profile update
    const isOrgUpdate = targetUser?.role === 'organization' || effectiveUpdates.role === 'organization' || Boolean(effectiveUpdates.orgTeamMembers);
    const umbrellaId = targetUser?.parentOrgId || (targetUser?.role === 'organization' ? uid : undefined);

    const sharedOrgFields: Partial<UserProfile> = {};
    if (isOrgUpdate) {
      const sharedKeys: (keyof UserProfile)[] = [
        'orgName', 'industry', 'address', 'website', 'avatar',
        'companyProfileText', 'onboardingText', 'targetingMap',
        'extendedRegions', 'orgTeamMembers', 'orgAuthorizedEmails'
      ];
      sharedKeys.forEach(k => {
        if (cleanedUpdates[k] !== undefined) {
          (sharedOrgFields as any)[k] = cleanedUpdates[k];
        }
      });
    }

    // Update local state immediately so changes are reflected in UI and auth validation
    setUsers(prev => prev.map(u => {
      if (u.uid === uid) return { ...u, ...cleanedUpdates };
      if (isOrgUpdate && umbrellaId && Object.keys(sharedOrgFields).length > 0) {
        const belongsToSameUmbrella =
          u.uid === umbrellaId ||
          u.parentOrgId === umbrellaId ||
          (u.role === 'organization' && sharedOrgFields.orgAuthorizedEmails?.includes((u.email || '').toLowerCase().trim()));
        if (belongsToSameUmbrella) {
          return { ...u, ...sharedOrgFields };
        }
      }
      return u;
    }));
    if (activeUser && activeUser.uid === uid) {
      setActiveUser(prev => prev ? ({ ...prev, ...cleanedUpdates } as UserProfile) : null);
    }

    if (!isUsingMockKeys) {
      try {
        const docRef = doc(db, 'users', uid);
        await setDoc(docRef, cleanedUpdates, { merge: true });

        // Propagate shared umbrella organization fields to primary org doc and linked team member accounts
        if (isOrgUpdate && umbrellaId && Object.keys(sharedOrgFields).length > 0) {
          if (umbrellaId !== uid) {
            await setDoc(doc(db, 'users', umbrellaId), sharedOrgFields, { merge: true }).catch(() => {});
          }
          const linkedMembers = users.filter(u =>
            u.uid !== uid &&
            u.uid !== umbrellaId &&
            (u.parentOrgId === umbrellaId ||
              (u.role === 'organization' && sharedOrgFields.orgAuthorizedEmails?.includes((u.email || '').toLowerCase().trim())))
          );
          for (const member of linkedMembers) {
            await setDoc(doc(db, 'users', member.uid), sharedOrgFields, { merge: true }).catch(() => {});
          }
        }
        
        writeLog(activeTenant.id, activeUser?.email || 'Superadmin', 'UPDATE_PROFILE', `Modified profile details for: ${uid}`);
      } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `users/${uid}`);
      }
    } else {
      const newLog: SystemLog = {
        id: `log-${Date.now()}`,
        tenantId: activeTenant.id,
        userEmail: activeUser?.email || 'Superadmin',
        action: 'UPDATE_PROFILE',
        details: `Modified profile details for: ${uid} (Mock)`,
        timestamp: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev]);
    }
  };

  const process18thBirthdayTransition = async (studentId?: string, overrideParentOversightChoice?: boolean) => {
    const targetUid = studentId || activeUser?.uid;
    if (!targetUid) return;

    // SYNCHRONOUS IN-MEMORY DEDUPLICATION: Prevent multiple calls from re-renders or snapshots
    if (processedBirthdayTransitionSet.current.has(targetUid) && overrideParentOversightChoice === undefined) {
      return;
    }

    const studentUser = users.find(u => u.uid === targetUid) || (activeUser?.uid === targetUid ? activeUser : null);
    if (!studentUser) return;

    // If already processed and email already sent, and no explicit oversight override was requested
    if (studentUser.ferpa18TransitionProcessed && studentUser.ferpa18EmailSent && overrideParentOversightChoice === undefined) {
      processedBirthdayTransitionSet.current.add(targetUid);
      return;
    }

    // Lock in-memory synchronously BEFORE any async operations
    processedBirthdayTransitionSet.current.add(targetUid);

    const isAlreadyProcessed = studentUser.ferpa18TransitionProcessed === true;
    const isEmailAlreadySent = studentUser.ferpa18EmailSent === true;

    const updates: Partial<UserProfile> = {
      isAdult: true,
      ferpa18TransitionProcessed: true,
      ferpa18TransitionDate: studentUser.ferpa18TransitionDate || new Date().toISOString(),
    };

    if (overrideParentOversightChoice !== undefined) {
      updates.allowParentOversightAt18 = overrideParentOversightChoice;
      updates.parentOversightPreferenceSet = true;
      if (overrideParentOversightChoice === false) {
        updates.parentAccessRevokedAt18 = true;
      }
    }

    // Reset FERPA consent to self-consent mode if not already reset
    if (!isAlreadyProcessed) {
      updates.ferpaConsent = {
        granted: false, // Requires 18yo self-consent signature
        date: new Date().toISOString(),
        version: '2.0-18yo-self-consent',
        signatureType: 'pending_18yo_self'
      };
    }

    // Check if TODAY is the student's exact 18th birthday
    const now = new Date();
    const birthDate = studentUser.dateOfBirth ? parseLocalDate(studentUser.dateOfBirth) : null;
    const isExact18thBirthdayToday = birthDate ? (
      birthDate.getMonth() === now.getMonth() && 
      birthDate.getDate() === now.getDate() && 
      calculateAge(studentUser.dateOfBirth, now) === 18
    ) : false;

    // Mark email as sent in Firestore so it never fires for past adults
    updates.ferpa18EmailSent = true;

    await updateUserProfile(targetUid, updates);

    // Send Happy 18th Birthday email ONLY IF today is their exact 18th birthday AND email was never sent before
    if (studentUser.email && isExact18thBirthdayToday && !isEmailAlreadySent && !isAlreadyProcessed) {
      try {
        const studentFirstName = studentUser.contactFirstName || studentUser.name?.split(' ')[0] || 'Student';
        await sendNotificationEmail(
          studentUser.email,
          `Happy 18th Birthday, ${studentFirstName}! 🎉 Important FERPA Privacy Rights & Parent Access Update`,
          'Happy 18th Birthday from Work-Based Learning!',
          `
            <p>Dear <strong>${studentFirstName}</strong>,</p>
            <p>Happy 18th Birthday! 🎉 As an official adult student under federal law (FERPA 34 CFR § 99.5), your educational privacy rights and FERPA disclosure authority now shift <strong>directly to you</strong>.</p>
            
            <div style="background-color: #f0f9ff; padding: 20px; border-radius: 12px; margin: 24px 0; border-left: 4px solid #0094c8;">
              <h3 style="margin-top: 0; color: #0f172a; font-size: 15px; font-weight: bold; text-transform: uppercase;">Your 18th Birthday FERPA Updates</h3>
              <ul style="font-size: 13px; color: #334155; margin-top: 10px; padding-left: 20px; line-height: 1.6;">
                <li>Your previous minor parent consent has been reset to self-consenting mode.</li>
                <li>You can now grant your own FERPA educational disclosure release for internships and placements.</li>
                <li>You can decide whether or not to allow continued parent/guardian oversight on the Parent Dashboard.</li>
              </ul>
            </div>

            <p>Please log into your student portal to sign your 18yo FERPA self-consent release and choose your parent dashboard oversight preference.</p>
            
            <div style="text-align: center; margin: 32px 0;">
              <a href="${getTenantBaseUrl(activeTenant)}" style="background-color: #0094c8; color: white; padding: 12px 28px; border-radius: 8px; text-decoration: none; font-weight: bold; display: inline-block;">Log In To Student Portal</a>
            </div>

            <p style="font-size: 13px; color: #64748b;">Best regards,<br /><strong>Work-Based Learning Program Administration</strong></p>
          `,
          studentUser.tenantId || activeTenant.id
        );
      } catch (e) {
        console.warn('18th birthday email trigger note:', e);
      }
    }

    inAppToast({
      title: '18th Birthday FERPA Shift Processed',
      message: `Updated ${studentUser.name || 'student'} profile to 18yo adult self-consent status.`,
      type: 'info'
    });
  };

  const createAuthenticatedUser = async (email: string, password: string, profile: UserProfile) => {
    if (!isUsingMockKeys) {
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const uid = userCredential.user.uid;
        const profileWithUid = { ...profile, uid };
        await addUserProfile(profileWithUid);
        await triggerWelcomeEmail(email, profile.role, profile.name, profile.region || 'WBL Portal', profile.school, password, profile.tenantId);
        return uid;
      } catch (e: any) {
        if (e && e.code === 'auth/email-already-in-use') {
          try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const uid = userCredential.user.uid;
            const profileWithUid = { ...profile, uid };
            await addUserProfile(profileWithUid);
            return uid;
          } catch (signInErr) {
            handleFirestoreError(e, OperationType.CREATE, `users/${profile.email}`);
            throw e;
          }
        }
        handleFirestoreError(e, OperationType.CREATE, `users/${profile.email}`);
        throw e;
      }
    } else {
      await addUserProfile(profile);
      return profile.uid;
    }
  };

  const addUserProfile = async (profile: UserProfile) => {
    let effectiveProfile = { ...profile };
    if ((effectiveProfile.school || effectiveProfile.schoolId) && (!effectiveProfile.district || !effectiveProfile.districtId)) {
      const resolved = resolveDistrictForSchool(
        effectiveProfile.schoolId || effectiveProfile.school,
        effectiveProfile.tenantId || activeTenant.id,
        allSchools,
        allDistricts
      );
      if (resolved.districtName && !effectiveProfile.district) effectiveProfile.district = resolved.districtName;
      if (resolved.districtId && !effectiveProfile.districtId) effectiveProfile.districtId = resolved.districtId;
    }
    // Remove undefined values to prevent Firestore errors
    const sanitizedProfile = cleanFirestoreData(effectiveProfile);

    if (!isUsingMockKeys) {
      try {
        await setDoc(doc(db, 'users', sanitizedProfile.uid), sanitizedProfile);
        writeLog(sanitizedProfile.tenantId, activeUser?.email || 'Superadmin', 'CREATE_MOCK_USER', `Administrated User Portal Identity: ${sanitizedProfile.name}`);
      } catch (e) {
        handleFirestoreError(e, OperationType.CREATE, `users/${sanitizedProfile.uid}`);
      }
    } else {
      setUsers(prev => [...prev, sanitizedProfile]);
      const newLog: SystemLog = {
        id: `log-${Date.now()}`,
        tenantId: sanitizedProfile.tenantId,
        userEmail: activeUser?.email || 'Superadmin',
        action: 'CREATE_MOCK_USER',
        details: `Administrated User Portal Identity: ${sanitizedProfile.name} (Mock)`,
        timestamp: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev]);
    }
  };

  const deleteUserProfile = async (uid: string) => {
    const isPlatformAdmin = activeUser?.role === 'superadmin' || activeUser?.role === 'regional_admin' || activeUser?.role === 'admin';
    if (!isPlatformAdmin) {
      throw new Error('Only Regional or Super Admins can permanently delete user accounts from Authentication and Firestore.');
    }

    if (!isUsingMockKeys) {
      try {
        await deleteDoc(doc(db, 'users', uid));
        
        // Try to delete from Firebase Auth via backend API
        try {
          const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : undefined;
          const res = await fetch('/api/delete-user', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
            },
            body: JSON.stringify({
              uid,
              callerUid: activeUser?.uid,
              callerRole: activeUser?.role
            })
          });
          if (!res.ok) {
            console.warn("Could not delete user from Firebase Auth:", await res.text());
          }
        } catch (authErr) {
          console.warn("Failed to reach backend to delete user from auth:", authErr);
        }

        setUsers(prev => prev.filter(u => u.uid !== uid));
        writeLog(activeTenant.id, activeUser?.email || 'Superadmin', 'DELETE_USER_PROFILE', `Deleted user: ${uid}`);
      } catch (e) {
        handleFirestoreError(e, OperationType.DELETE, `users/${uid}`);
      }
    } else {
      setUsers(prev => prev.filter(u => u.uid !== uid));
      const newLog: SystemLog = {
        id: `log-${Date.now()}`,
        tenantId: activeTenant.id,
        userEmail: activeUser?.email || 'Superadmin',
        action: 'DELETE_USER_PROFILE',
        details: `Deleted user: ${uid} (Mock)`,
        timestamp: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev]);
    }
  };

  const markMessageAsRead = async (id: string) => {
    setMessages(prev => prev.map(m => m.id === id ? { ...m, read: true } : m));
    if (!isUsingMockKeys) {
      try {
        await setDoc(doc(db, 'messages', id), { read: true }, { merge: true });
      } catch (e: any) {
        console.error('Failed to mark message as read in Firestore:', e.message);
      }
    }
  };

  const markMessageAsUnread = async (id: string) => {
    setMessages(prev => prev.map(m => m.id === id ? { ...m, read: false } : m));
    if (!isUsingMockKeys) {
      try {
        await setDoc(doc(db, 'messages', id), { read: false }, { merge: true });
      } catch (e: any) {
        console.error('Failed to mark message as unread in Firestore:', e.message);
      }
    }
  };

  const deletePortalMessage = async (id: string) => {
    // 1. Immediately remove from local state for instant 0ms UI feedback
    setMessages(prev => prev.filter(m => m.id !== id));

    // 2. Persist deletion in Firestore if connected
    if (!isUsingMockKeys) {
      try {
        await setDoc(doc(db, 'messages', id), { deleted: true }, { merge: true });
      } catch (e: any) {
        console.error('Failed to soft-delete message in Firestore:', e.message);
        try {
          await deleteDoc(doc(db, 'messages', id));
        } catch (delErr: any) {
          console.error('Failed to hard-delete message in Firestore:', delErr.message);
        }
      }
    }
  };

  const sendManualPortalMessage = async (toEmail: string, subject: string, title: string, bodyHtml: string) => {
    const recipientUser = users.find(u => u.email?.toLowerCase() === toEmail.toLowerCase());
    await sendNotificationEmail(toEmail, subject, title, bodyHtml, recipientUser?.tenantId);
  };

  const sendVerificationReminderToAdvisor = async () => {
    if (!activeUser || activeUser.role !== 'student') return;

    const ferpaGranted = typeof activeUser.ferpaConsent === 'object'
      ? activeUser.ferpaConsent?.granted === true
      : activeUser.ferpaConsent === true;
    if (!ferpaGranted) {
      throw new Error("FERPA consent must be granted before you can request advisor verification or connect with an advisor.");
    }

    const studentSchoolId = activeUser.schoolId || activeUser.school;
    const studentDistrictId = activeUser.districtId || activeUser.district;

    // Locate advisors / regional admins / superadmins matching student's school or tenant
    const matchedAdvisors = users.filter(u => {
      if (u.role !== 'advisor' && u.role !== 'admin' && u.role !== 'regional_admin' && u.role !== 'superadmin') return false;
      if (u.role === 'superadmin') return true;
      if (u.tenantId && activeUser.tenantId && u.tenantId !== activeUser.tenantId && u.tenantId !== 'global' && activeUser.tenantId !== 'global') return false;

      if (u.role === 'advisor') {
        const servesSchool = (u.schoolsServedIds && studentSchoolId && u.schoolsServedIds.includes(studentSchoolId)) ||
                             (u.schoolsServed && activeUser.school && u.schoolsServed.includes(activeUser.school)) ||
                             (u.schoolId && studentSchoolId && u.schoolId === studentSchoolId) ||
                             (u.school && activeUser.school && u.school === activeUser.school) ||
                             (u.districtId && studentDistrictId && u.districtId === studentDistrictId);
        return servesSchool;
      }
      return true;
    });

    const studentName = activeUser.name || `${activeUser.contactFirstName || ''} ${activeUser.contactLastName || ''}`.trim() || 'A student';
    const schoolName = activeUser.school || activeUser.district || 'your school';

    const subject = `Action Required: Verification Reminder for ${studentName}`;
    const title = `Student Profile Verification Request`;
    const bodyHtml = `
      <div style="font-size: 14px; color: #334155; line-height: 1.6;">
        <p>Student <strong>${studentName}</strong> (${activeUser.email}) from <strong>${schoolName}</strong> has requested verification of their profile in the WBL Portal.</p>
        <p>Your verification sign-off is required before ${studentName} can apply for placement opportunities.</p>
        <div style="margin: 20px 0; padding: 16px; background-color: #f1f5f9; border-radius: 12px; border-left: 4px solid #0094c8;">
          <strong style="color: #0f172a; font-size: 13px;">Advisor Action Needed:</strong>
          <p style="margin: 4px 0 0 0; font-size: 13px; color: #475569;">
            Please log in to your Advisor Console, navigate to <strong>Advisees / Students</strong>, locate <strong>${studentName}</strong>, and click <strong>Verify Profile</strong>.
          </p>
        </div>
      </div>
    `;

    const recipients = matchedAdvisors.length > 0
      ? matchedAdvisors
      : users.filter(u => u.role === 'superadmin' || u.role === 'regional_admin');

    const dispatchedEmails = new Set<string>();
    for (const advisor of recipients) {
      if (advisor.email && !dispatchedEmails.has(advisor.email.toLowerCase())) {
        dispatchedEmails.add(advisor.email.toLowerCase());
        await sendNotificationEmail(advisor.email, subject, title, bodyHtml, advisor.tenantId || activeUser.tenantId);
      }
    }

    // Persist timestamp on student profile
    await updateUserProfile(activeUser.uid, {
      lastVerificationReminderSent: new Date().toISOString()
    });
  };

  const getTenantBaseUrl = (tenant?: Tenant | null) => {
    // 1. Check if the specific tenant has a configured customDomain
    if (tenant) {
      if (tenant.customDomain && tenant.customDomain.trim() && !tenant.customDomain.includes('wblportal.edu')) {
        const cd = tenant.customDomain.trim();
        if (cd.startsWith('http://') || cd.startsWith('https://')) {
          return cd;
        }
        return `https://${cd}`;
      }

      // 2. Spartanburg region automatically defaults to thespartanburgedge.com
      const tid = (tenant.id || '').toLowerCase();
      const tsub = (tenant.subdomain || '').toLowerCase();
      const tname = (tenant.name || '').toLowerCase();
      if (tid === 'spartanburg' || tsub === 'spartanburg' || tname.includes('spartanburg')) {
        return 'https://thespartanburgedge.com';
      }
    }

    // 3. Global custom domain configured in Superadmin console (e.g. wblportal.com)
    if (globalBranding?.customDomain && globalBranding.customDomain.trim() && !globalBranding.customDomain.includes('wblportal.edu')) {
      const gcd = globalBranding.customDomain.trim();
      if (gcd.startsWith('http://') || gcd.startsWith('https://')) {
        return gcd;
      }
      return `https://${gcd}`;
    }

    // 4. Default for all other regions
    return 'https://wblportal.com';
  };

  // Computed values filtered by activeTenant.id if not 'global'
  const filteredUsers = useMemo(() => {
    if (activeUser?.role === 'superadmin') return users;
    if (!activeTenant?.id || activeTenant.id === 'global') return users;
    return users.filter(u => u.tenantId === activeTenant.id || u.role === 'superadmin');
  }, [users, activeTenant.id, activeUser?.role]);

  const filteredOpportunities = useMemo(() => {
    // If activeTenant is global or not set:
    if (!activeTenant?.id || activeTenant.id === 'global') {
      // If user has multiple regions (primary tenantId + extendedRegions), show opportunities in all their appropriate regions
      if (activeUser && activeUser.role !== 'superadmin') {
        const userRegions = [
          activeUser.tenantId,
          ...(activeUser.extendedRegions || [])
        ].filter(Boolean).map(r => r.toLowerCase());

        if (userRegions.length > 0) {
          return opportunities.filter(o => {
            const oTenant = (o.tenantId || '').toLowerCase();
            const oRegion = (o.regionId || '').toLowerCase();
            const oTargets = (o.targetRegionIds || []).map(t => t.toLowerCase());
            return userRegions.includes(oTenant) || 
                   userRegions.includes(oRegion) || 
                   oTargets.some(t => userRegions.includes(t));
          });
        }
      }
      return opportunities;
    }

    // Otherwise filter strictly by the selected activeTenant
    return opportunities.filter(o => {
      const activeId = activeTenant.id.toLowerCase();
      return (o.tenantId || '').toLowerCase() === activeId || 
             (o.regionId || '').toLowerCase() === activeId || 
             (o.targetRegionIds || []).map(t => t.toLowerCase()).includes(activeId);
    });
  }, [opportunities, activeTenant.id, activeUser]);

  const filteredApplications = useMemo(() => {
    if (!activeTenant?.id || activeTenant.id === 'global') return applications;
    return applications.filter(a => a.tenantId === activeTenant.id);
  }, [applications, activeTenant.id]);

  const filteredPlacements = useMemo(() => {
    if (!activeTenant?.id || activeTenant.id === 'global') return placements;
    return placements.filter(p => p.tenantId === activeTenant.id);
  }, [placements, activeTenant.id]);

  const filteredTasks = useMemo(() => {
    if (!activeTenant?.id || activeTenant.id === 'global') return tasks;
    return tasks.filter(t => t.tenantId === activeTenant.id);
  }, [tasks, activeTenant.id]);

  const filteredAttendance = useMemo(() => {
    if (!activeTenant?.id || activeTenant.id === 'global') return attendance;
    return attendance.filter(a => a.tenantId === activeTenant.id);
  }, [attendance, activeTenant.id]);

  const filteredInterviews = useMemo(() => {
    if (!activeTenant?.id || activeTenant.id === 'global') return interviews;
    return interviews.filter(i => i.tenantId === activeTenant.id);
  }, [interviews, activeTenant.id]);

  const filteredUserLinks = useMemo(() => {
    if (!activeTenant?.id || activeTenant.id === 'global') return userLinks;
    return userLinks.filter(ul => ul.tenantId === activeTenant.id);
  }, [userLinks, activeTenant.id]);

  const filteredSchools = useMemo(() => {
    if (!activeTenant?.id || activeTenant.id === 'global') return allSchools;
    const filtered = allSchools.filter(s => s.regionId === activeTenant.id || s.tenantId === activeTenant.id);
    console.log("DEBUG: Filtering schools. Tenant ID:", activeTenant.id, "All schools count:", allSchools.length, "Filtered count:", filtered.length);
    return filtered;
  }, [allSchools, activeTenant.id]);

  const filteredDistricts = useMemo(() => {
    if (!activeTenant?.id || activeTenant.id === 'global') return allDistricts;
    return allDistricts.filter(d => d.regionId === activeTenant.id || d.tenantId === activeTenant.id);
  }, [allDistricts, activeTenant.id]);

  return (
    <WBLContext.Provider
      value={{
        activeTenant,
        activeUser,
        tenants,
        users: filteredUsers,
        opportunities: filteredOpportunities,
        allOpportunities: opportunities,
        applications: filteredApplications,
        placements: filteredPlacements,
        tasks: filteredTasks,
        attendance: filteredAttendance,
        logs,
        interviews: filteredInterviews,
        messages,
        userLinks: filteredUserLinks,
        schools: filteredSchools,
        allSchools: allSchools,
        districts: filteredDistricts,
        allDistricts: allDistricts,
        isLoading: isLoading || !isSystemConfigLoaded,
        isFirebaseActive,
        isDbSeeded,
        currentView,
        setCurrentView,
        viewingStudentId,
        
        signInWithGoogle,
        signUpWithEmail,
        signUpWithRoleAndData,
        verifyStudentTicket,
        signInWithEmail,
        signOutUser,
        
        changeTenant,
        updateTenantBranding,
        updateAllTenantsBranding,
        updateGlobalBranding,
        createTenant,
        globalBranding,
        
        addOpportunity,
        updateOpportunity,
        applyToOpportunity,
        reviewApplication,
        createPlacement,
        scheduleInterview,
        cancelInterview,
        rescheduleInterview,
        updateInterviewNotesAndScore,
        recommendStudentToOpportunity,
        recommendations,
        
        addTask,
        submitTask,
        gradeTask,
        
        submitAttendance,
        approveAttendance,
        updatePlacementEvaluation,
        updatePlacementAgreement,
        hireStudent,
        addPlacementNote,
        deletePlacementNote,
        
        markMessageAsRead,
        markMessageAsUnread,
        deletePortalMessage,
        sendManualPortalMessage,
        sendVerificationReminderToAdvisor,

        updateUserProfile,
        addUserProfile,
        createAuthenticatedUser,
        deleteUserProfile,
        linkStudent,
        switchActiveChild,
        selectedTerm,
        setSelectedTerm,
        systemConfig,
        updateSystemConfig,
        grantFerpaConsent,
        adminOverrideFerpaConsent,
        notifySuperadminOnFerpaConsent,
        resendStudentFerpaConsent,
        process18thBirthdayTransition,
        updateMyPassword,
        adminResetUserPassword,
        runSchoolIdMigration,
        getTenantBaseUrl,
        getOrgTeamInviteUrl,
        sendOrgTeamInviteEmail,
        delegateOrgLead,
        removeOrgTeamMember,
        submitRegionInquiry,
        shouldUserReceiveNotification,

        proxyOriginalSuperadmin,
        isProxyActive,
        startProxySession,
        exitProxySession
      }}
    >
      {children}
    </WBLContext.Provider>
  );
};

export const useWBL = () => {
  const context = useContext(WBLContext);
  if (context === undefined) {
    throw new Error('useWBL must be used inside a WBLProvider');
  }
  return context;
};
